import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiCpu, FiGrid, FiLayers, FiSearch, FiTool, FiUpload } from "react-icons/fi";
import { toast } from "react-toastify";
import { setCartItem } from "../../redux/features/cartSlice";
import Title from "./Title";
import ContactDetails from "./ContactDetails";
import {
  useAddQuoteToCartMutation,
  useCalculateInstantQuoteMutation,
  useCreateQuoteMutation,
  useGetInstantQuoteConfigQuery,
  useSubmitQuoteMutation,
  useUpdateQuoteMutation,
} from "../../redux/api/quoteApi";

const DEFAULT_FORM_VALUES = {
  quoteType: "standard_pcb",
  customerInfo: {
    name: "",
    email: "",
    phone: "",
    company: "",
  },
  shipping: {
    countryCode: "US",
  },
  pcbSpec: {
    boardType: "single_pieces",
    panelRequirements: "",
    breakAwayRail: "no",
    routeProcess: "pcbway_prefer",
    xOutAllowance: "accept",
    differentDesignCount: 1,
    size: {
      length: 100,
      width: 80,
      unit: "mm",
    },
    quantity: 10,
    layers: 2,
    copperLayerSide: "",
    solderMaskSide: "",
    silkscreenSide: "",
    material: "FR4",
    materialModel: "",
    fr4Tg: "TG150",
    thickness: 1.6,
    thicknessUnit: "mm",
    minTrackSpacing: "6/6mil",
    minHoleSize: "0.3mm",
    solderMask: "green",
    silkscreen: "white",
    surfaceFinish: "hasl_lead_free",
    surfaceFinishDetail: {
      goldThickness: "",
      nickelThickness: "",
      palladiumThickness: "",
      selectiveArea: "",
      specialRequest: "",
    },
    viaProcess: "tenting_vias",
    finishedCopper: "0.5oz",
    innerCopper: "1oz",
    uvPrinting: "none",
    removeProductNo: "no",
    edgeConnector: "no",
    edgeConnectorBevelling: "no",
    edgeConnectorSurfaceFinish: "hard_gold",
    edgeConnectorGoldThickness: "standard",
    edgeConnectorCustomGoldThickness: "",
    edgeConnectorImmersionGoldThickness: '1U"',
    edgeConnectorGoldNickelThickness: 'Au:10U"/Ni:120U"',
    edgeConnectorEnepigThickness: 'Ni:200U" Pd:2U" Au:2U"',
    acceptHaslToEnigSubstitution: false,
    poNumber: "",
    advancedPcbType: "through_hole_board",
    advancedBoardSpec: "ipc_6012_class_2",
    orderWithStencil: false,
    orderWithAssembly: false,
    stencilSpec: {
      stencilType: "non_framework",
      multiLevel: false,
      size: "custom",
      customWidth: 190,
      customHeight: 290,
      poNumber: "",
      side: "top",
      quantity: 1,
      thickness: "0.12",
      fiducials: "none",
      electropolishing: false,
      specialRequest: "",
    },
    assemblySpec: {
      sourcingType: "turnkey",
      boardType: "single_pieces",
      assemblySide: "top",
      quantity: 1,
      sensitiveComponents: false,
      acceptAlternatives: false,
      uniquePartsCount: "",
      smdPartsCount: "",
      bgaQfpPartsCount: "",
      throughHolePartsCount: "",
      depanelBoards: false,
      functionTest: false,
      conformalCoating: false,
      firmwareLoading: false,
      pressFitAssembly: false,
      boxBuildAssembly: false,
      cableHarnessAssembly: false,
      packageBox: "neutral",
      flyingProbeTesting: false,
      xrayTestCount: "",
      detailedInformation: "",
      poNumber: "",
    },
    complianceConfirmed: false,
    specialRequest: "",
    advancedOptions: {
      peelableSoldermask: "none",
      holeCopperThickness: "standard_20um",
      ulMarking: "none",
      dateCode: "none",
      packageBox: "with_logo",
      serialNumbers: "none",
      serialNumberPrintingLocation: "factory_default",
      halogenFree: false,
      edgeConnector: false,
      impedanceControl: false,
      impedanceDesignCount: 1,
      impedanceLayers: "",
      castellatedHoles: false,
      edgePlating: false,
      customStackup: false,
      viaInPad: false,
      resinFilledVias: false,
      inspectionReports: ["default"],
    },
  },
  spec: {},
};

const QUOTE_CATEGORIES = [
  { value: "standard_pcb", label: "Standard PCB" },
  { value: "advanced_pcb", label: "Advanced PCB NEW" },
  { value: "fpc_rigid_flex", label: "FPC/Rigid-Flex" },
  { value: "assembly", label: "Assembly" },
  { value: "smd_stencil", label: "SMD-Stencil" },
  { value: "cnc_3d", label: "CNC | 3D Printing" },
];

const STANDARD_QUANTITY_OPTIONS = [
  5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 125, 150, 200, 250, 300,
  350, 400, 450, 500, 600, 700, 800, 900, 1000, 1500, 2000, 2500, 3000,
  3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000, 9000,
  10000,
];

const PANEL_ROUTE_PROCESS_OPTIONS = [
  { value: "pcbway_prefer", label: "Panel as Creatyea prefer" },
  { value: "v_scoring", label: "Panel as V-Scoring" },
  { value: "tab_route", label: "Panel as Tab Route" },
  { value: "v_scoring_and_tab_route", label: "Both V-Scoring & Tab-routing" },
];

const INNER_COPPER_OPTIONS = ["1oz", "1.5oz", "2oz", "3oz", "4oz", "5oz", "6oz"];

const ATTACHMENT_CATEGORIES = {
  auto: "Auto detect",
  gerber: "Gerber/Drill",
  bom: "BOM",
  pick_place: "Pick & Place",
  drawing: "Drawing",
  cad: "CAD/3D",
  other: "Other",
};

const QUOTE_ATTACHMENT_REQUIREMENTS = {
  standard_pcb: {
    label: "Gerber / Zip Files",
    accept: ".zip,.rar,.gbr,.ger,.gtl,.gbl,.gto,.gbo,.gts,.gbs,.gml,.gm1,.gko,.drl,.xln,.txt,.pdf",
    helper: "Upload Gerber ZIP, drill files, drawings or PDF notes. Max 24 files, 200MB each.",
    defaultCategory: "gerber",
  },
  advanced_pcb: {
    label: "Gerber / Stack-up Files",
    accept: ".zip,.rar,.gbr,.ger,.drl,.xln,.pdf,.xlsx,.xls,.csv",
    helper: "Upload Gerber, stack-up, impedance, drawing or special process files.",
    defaultCategory: "gerber",
  },
  fpc_rigid_flex: {
    label: "FPC / Rigid-Flex Files",
    accept: ".zip,.rar,.gbr,.ger,.drl,.xln,.pdf,.dwg,.dxf,.xlsx,.xls,.csv",
    helper: "Upload Gerber, stack-up, stiffener drawing or coverlay notes.",
    defaultCategory: "gerber",
  },
  assembly: {
    label: "Assembly Files",
    accept: ".zip,.rar,.gbr,.ger,.drl,.xln,.xlsx,.xls,.csv,.txt,.pdf",
    helper: "Upload Gerber, BOM, Pick & Place/CPL and assembly drawings.",
    defaultCategory: "gerber",
  },
  smd_stencil: {
    label: "Stencil Files",
    accept: ".zip,.rar,.gbr,.ger,.gtl,.gbl,.gts,.gbs,.drl,.pdf",
    helper: "Upload paste layer or Gerber files for stencil review.",
    defaultCategory: "gerber",
  },
  cnc_3d: {
    label: "CAD / Drawing Files",
    accept: ".step,.stp,.x_t,.iges,.igs,.sldprt,.stl,.obj,.pdf,.dwg,.dxf,.zip",
    helper: "Upload CAD model and optional technical drawings. Max 24 files, 200MB each.",
    defaultCategory: "cad",
  },
};

const MAX_QUOTE_ATTACHMENT_COUNT = 24;
const MAX_QUOTE_ATTACHMENT_SIZE = 200 * 1024 * 1024;

const ADVANCED_PCB_TYPE_OPTIONS = [
  { value: "through_hole_board", label: "Through hole board" },
  { value: "hdi_buried_blind_vias", label: "HDI(Buried/blind vias)" },
];

const ADVANCED_BOARD_SPEC_OPTIONS = [
  { value: "ipc_6012_class_2", label: "IPC 6012 Class 2" },
  { value: "ipc_6012_class_3", label: "IPC 6012 Class 3" },
  { value: "iatf16949", label: "IATF16949" },
  { value: "iso13485", label: "ISO13485" },
  { value: "customer_standard", label: "Customer Standard" },
];

const ADVANCED_LAYER_OPTIONS = [
  1,
  2,
  4,
  6,
  8,
  10,
  12,
  14,
  16,
  18,
  20,
  22,
  24,
  26,
  28,
  30,
  32,
  34,
  36,
  38,
  40,
  42,
  44,
  46,
  48,
  50,
  52,
  54,
  56,
  58,
  60,
];

const EDGE_CONNECTOR_GOLD_THICKNESS_OPTIONS = [
  { value: "3,120", label: 'Au:3U"/Ni:120U"' },
  { value: "5,120", label: 'Au:5U"/Ni:120U"' },
  { value: "10,120", label: 'Au:10U"/Ni:120U"' },
  { value: "15,120", label: 'Au:15U"/Ni:120U"' },
  { value: "20,120", label: 'Au:20U"/Ni:120U"' },
  { value: "25,120", label: 'Au:25U"/Ni:120U"' },
  { value: "30,120", label: 'Au:30U"/Ni:120U"' },
];

const PCB_MATERIAL_IMAGES = {
  FR4: "/img/fr4_metarial.jpg",
  Aluminum: "/img/Aluminum-Board.jpg",
  Rogers: "/img/rogers.jpg",
  HDI: "/img/hdi_board.jpg",
  CopperBase: "/img/copper_board.jpg",
};

const FR4_TG_LABELS = {
  TG130: "TG 130-140",
  TG150: "TG 150-160",
  TG170: "TG 170-180",
  S1000H_TG150: "S1000H TG150",
  S1000_2M_TG170: "S1000-2M TG170",
};

const TARGET_THICKNESS_OPTIONS = [
  0.2,
  0.3,
  0.4,
  0.6,
  0.8,
  1,
  1.2,
  1.6,
  2,
  2.4,
  2.6,
  2.8,
  3,
  3.2,
];

const ADVANCED_MATERIAL_OPTIONS = [
  { value: "tg140_fr4", label: "Tg140 FR-4" },
  { value: "tg150_fr4", label: "Tg150 FR-4" },
  { value: "tg170_fr4", label: "Tg170 FR-4" },
  { value: "tg150_halogen_free", label: "Tg150 FR-4(Halogen-free)" },
  { value: "tg170_halogen_free", label: "Tg170 FR-4(Halogen-free)" },
  { value: "high_cti_600v", label: "High-CTI(>=600V)" },
  { value: "high_cti_halogen_free_600v", label: "High-CTI(Halogen-free,>=600V)" },
  { value: "high_speed_ghz", label: "≈High Speed(GHz)", optionClassName: "quote-pcbway-hot-option" },
  { value: "high_frequency_dk", label: "≡High Frequency PCBs(DK)", optionClassName: "quote-pcbway-hot-option" },
  { value: "special_high_low_temperature", label: "Special Material(High low temperature)" },
];

const ADVANCED_MATERIAL_HINTS = {
  tg140_fr4: {
    prefix: "*Tg140 FR-4 Material:",
    highlight: "Shengyi S1141",
  },
  tg150_fr4: {
    prefix: "*Tg150 FR-4 Material:",
    highlight: "Shengyi S1000H",
    suffix: "for better heat resistance and dimensional stability.",
  },
  tg170_fr4: {
    prefix: "*Tg170 FR-4 Material:",
    highlight: "Shengyi S1000-2M",
    suffix: "recommended for lead-free soldering and high-temperature applications.",
  },
  tg150_halogen_free: {
    prefix: "*Tg150 halogen-free FR-4:",
    highlight: "environment-friendly material",
    suffix: "for products with halogen-free compliance requirements.",
  },
  tg170_halogen_free: {
    prefix: "*Tg170 halogen-free FR-4:",
    highlight: "high TG halogen-free material",
    suffix: "for high reliability and thermal stress requirements.",
  },
  high_cti_600v: {
    prefix: "*High-CTI(>=600V):",
    highlight: "high insulation resistance",
    suffix: "for power, charger and high-voltage creepage designs.",
  },
  high_cti_halogen_free_600v: {
    prefix: "*High-CTI halogen-free:",
    highlight: "CTI>=600V",
    suffix: "with halogen-free compliance for high-voltage products.",
  },
  high_speed_ghz: {
    prefix: "*High Speed(GHz):",
    highlight: "low-loss high-speed material",
    suffix: "for GHz signals, SERDES and high-speed digital designs.",
  },
  high_frequency_dk: {
    prefix: "*High Frequency PCBs(DK):",
    highlight: "stable DK/DF material",
    suffix: "for RF, microwave and antenna applications.",
  },
  special_high_low_temperature: {
    prefix: "*Special Material:",
    highlight: "high/low temperature resistant material",
    suffix: "requires engineering review before final quotation.",
  },
};

const ADVANCED_THICKNESS_OPTIONS = [
  {
    value: 0.15,
    label: "≥0.1-0.2 (reviewed)",
    optionClassName: "quote-advanced-thickness-wide-option",
  },
  0.2,
  0.4,
  0.6,
  0.8,
  1,
  1.2,
  1.6,
  2,
  2.4,
  2.8,
  3.2,
  {
    value: 3.6,
    label: "3.6",
    rowBreakAfter: true,
  },
  4,
  4.4,
  4.8,
  5.2,
  5.6,
  6,
  7,
  8,
];

const ADVANCED_THICKNESS_PRESET_VALUES = ADVANCED_THICKNESS_OPTIONS.map((option) =>
  typeof option === "object" ? Number(option.value) : Number(option)
);

const ADVANCED_TRACK_SPACING_OPTIONS = [
  "3/3mil",
  "4/4mil",
  "5/5mil",
  "6/6mil",
  { value: "8/8mil", label: "8/8mil ↑" },
];

const ADVANCED_HOLE_SIZE_OPTIONS = [
  "0.15mm",
  "0.2mm",
  "0.25mm",
  { value: "0.3mm", label: "0.3mm ↑" },
  { value: "no_drill", label: "No Drill" },
];

const ADVANCED_SOLDER_MASK_OPTIONS = [
  { value: "green", label: "Green" },
  { value: "red", label: "Red" },
  { value: "yellow", label: "Yellow" },
  { value: "blue", label: "Blue" },
  { value: "white", label: "White" },
  { value: "black", label: "Black" },
  { value: "pink", label: "Pink" },
  { value: "grey", label: "Grey" },
  { value: "orange", label: "Orange" },
  { value: "transparent", label: "Transparent" },
  { value: "purple", label: "Purple" },
  { value: "matte_black", label: "Matte black" },
  { value: "matte_green", label: "Matte green" },
  { value: "matte_blue", label: "Matte blue" },
  { value: "matte_red", label: "Matte red" },
  { value: "none", label: "None" },
];

const ADVANCED_SILKSCREEN_OPTIONS = [
  { value: "white", label: "White" },
  { value: "black", label: "Black" },
  { value: "yellow", label: "Yellow" },
  { value: "blue", label: "Blue" },
  { value: "grey", label: "Grey" },
  { value: "none", label: "None" },
];

const FPC_PCB_TYPE_OPTIONS = [
  { value: "flexible_pcb", label: "Flexible PCB" },
  { value: "rigid_flex_board", label: "Rigid-Flex Board" },
];

const FPC_BOARD_TYPE_OPTIONS = [
  { value: "single_pieces", label: "Single pieces" },
  { value: "panel_by_customer", label: "Panel by Customer" },
  { value: "panel_by_supplier", label: "Panel by Supplier" },
];

const FPC_LAYER_OPTIONS = [1, 2, 4, 6, 8, 10, 12, 14, 16];

const FPC_BASE_MATERIAL_OPTIONS = [
  { value: "polyimide_flex", label: "Polyimide Flex" },
  { value: "pet", label: "PET" },
  { value: "high_frequency_dk_3_6", label: "High Frequency(DK≤3.6)" },
];

const FPC_THICKNESS_OPTIONS = [
  0.025,
  0.05,
  0.06,
  0.07,
  0.08,
  0.09,
  0.1,
  0.11,
  0.12,
  0.13,
  0.14,
  0.15,
  0.16,
  0.17,
  0.18,
  0.2,
  0.22,
  0.23,
  0.24,
  0.25,
  0.26,
  0.27,
  0.29,
  0.3,
  0.33,
  0.34,
  { value: 0.35, label: "≥0.35" },
  { value: 0.4, label: "≥0.4" },
];

const FPC_TRACK_SPACING_OPTIONS = [{ value: "0.06mm", label: "≥ 0.06mm" }];

const MM_PER_INCH = 25.4;

/**
 * 获取目标站风格的 FR4-TG 选中提示。
 * @param {string} fr4Tg 当前 FR4-TG 选项
 * @param {number|string} layers PCB 层数
 * @returns {string} 提示文字
 */
const getFr4TgHint = (fr4Tg, layers) => {
  if (fr4Tg === "S1000H_TG150" && Number(layers) === 2) {
    return "The base material of 2-layer PCBs is automatically upgraded to S1000H TG150 for FREE with more stable and higher quality.";
  }

  if (fr4Tg === "TG130" && Number(layers) === 2) {
    return "The base material of 2-layer PCBs is automatically upgraded to TG150 for FREE with more stable and higher quality. Except for some parameters (eg.: Weighty finished copper; Greater board thickness; Custom stackup; Via in pad/ Via filled with resin, etc.).";
  }

  if (["TG170", "S1000_2M_TG170"].includes(fr4Tg)) {
    return "Base material with high TG has good CAF resistance, high heat resistance, dimensional stability and is suitable for lead-free soldering process.";
  }

  if (Number(layers) > 2 && fr4Tg === "TG150") {
    return "Normally, the base material of multi-layer PCBs is automatically upgraded to TG150 for FREE with more stable and higher quality.";
  }

  return "";
};

/**
 * 获取目标站中的特殊成品板厚提示。
 * @param {number|string} thickness 当前选择的板厚
 * @param {string} material 当前材料
 * @param {number|string} layers PCB 层数
 * @returns {string} 成品板厚提示
 */
const getThicknessHint = (thickness, material, layers) => {
  if (material !== "FR4" || Number(layers) > 2) {
    return "";
  }

  if (Number(thickness) === 0.2) {
    return "Finished PCB Thickness: 0.15mm~0.2mm";
  }

  if (Number(thickness) === 0.3) {
    return "Finished PCB Thickness: 0.25mm~0.3mm";
  }

  return "";
};

/**
 * 获取高级 PCB 自定义板厚输入框显示值，预设值保持输入框为空。
 * @param {number|string} thickness 当前板厚
 * @returns {string|number} 自定义输入显示值
 */
const getAdvancedCustomThicknessValue = (thickness) => {
  const numericThickness = Number(thickness);

  if (
    Number.isFinite(numericThickness) &&
    !ADVANCED_THICKNESS_PRESET_VALUES.includes(numericThickness)
  ) {
    return thickness;
  }

  return "";
};

/**
 * PCB 厚度示意图。
 */
const PcbThicknessIllustration = () => (
  <img
    src="/img/thickness.png"
    alt="PCB thickness measurement illustration"
    className="h-auto w-[70px] object-contain"
  />
);

/**
 * 将英寸尺寸转换为毫米，并限制浮点数精度。
 * @param {number|string} value 英寸尺寸
 * @returns {number} 转换后的毫米尺寸
 */
const convertInchesToMillimeters = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Number((numericValue * MM_PER_INCH).toFixed(2));
};

const STANDARD_PCB_HELP_TEXT = {
  boardType: "Choose Single Pieces for separate boards, or a panel option when multiple boards are arranged in one production panel.",
  differentDesignCount: "Enter how many different PCB designs are included in the same panel. Identical boards count as one design.",
  size: "Enter the finished length and width of one PCB. For panel orders, enter the size of a single board rather than the full panel.",
  quantity: "Enter the number of individual PCBs required. For panel orders, use the total number of single boards.",
  layers: "Select the total number of copper layers in the PCB, including the top and bottom copper layers.",
  copperLayerSide: "For a 1-layer PCB, select which side contains the copper layer.",
  solderMaskSide: "For a 1-layer PCB, select which side requires solder mask.",
  silkscreenSide: "For a 1-layer PCB, select which side requires silkscreen printing.",
  finishedCopper: "Select the finished copper weight. Higher copper weight supports more current but may increase cost and manufacturing limits.",
  solderMask: "Select the solder mask color applied over the copper surface. Choose None only when no solder mask is required.",
  silkscreen: "Select the legend color used for component references, markings, and other printed information.",
  material: "Select the base material. HDI is intended for boards with buried or blind vias and is available for 4-layer boards or above.",
  fr4Tg: "TG is the glass transition temperature of FR-4. Higher TG materials provide better heat resistance and dimensional stability.",
  thickness: "Select the finished PCB thickness in millimeters. The actual thickness is subject to normal manufacturing tolerance.",
  minTrackSpacing: "Select the smallest copper trace width and spacing used in the design. Smaller values require finer manufacturing capability.",
  minHoleSize: "Select the smallest finished hole diameter in the design. Choose No Drill when the PCB has no drilled holes.",
  uvPrinting: "Use UV printing for multi-color graphics. The uploaded PCB file must include the required color artwork.",
  edgeConnector: "Choose Yes when the PCB has plated edge contacts, commonly known as gold fingers. Bevelling can be selected when required.",
  surfaceFinish: "Select the exposed copper surface treatment. It protects copper from oxidation and affects solderability and contact performance.",
  removeProductNo: "Choose whether the factory product number may appear on the PCB. Specifying a location may require a marked drawing.",
  viaProcess: "Select how vias are covered. Gerber-defined openings take priority; resin-filled vias should be requested in advanced options.",
  impedanceControl: "Impedance control ensures that signals travel at a specific speed and with minimal reflection. Choose the number of impedance designs and the layers they apply to.",
};

const STANDARD_PCB_HELP_IMAGES = {
  boardType: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+board+type+single+pieces+vs+panel+by+customer+vs+panel+by+supplier+comparison&image_size=landscape_4_3",
  size: "/img/xy-sizepanel.png",
  quantity: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=stack+of+manufactured+printed+circuit+boards&image_size=landscape_4_3",
  layers: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+cross+section+showing+multiple+copper+layers+structure&image_size=landscape_4_3",
  material: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=fr4+aluminum+rogers+pcb+base+material+comparison&image_size=landscape_4_3",
  fr4Tg: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+material+thermal+glass+transition+temperature+tg+illustration&image_size=landscape_4_3",
  thickness: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+thickness+measurement+comparison+0.8mm+1.6mm+2.0mm&image_size=landscape_4_3",
  minTrackSpacing: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=microscopic+view+of+pcb+copper+traces+width+and+spacing&image_size=landscape_4_3",
  minHoleSize: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+drill+holes+different+sizes+comparison&image_size=landscape_4_3",
  solderMask: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+solder+mask+colors+green+red+blue+black+white+matte+comparison&image_size=landscape_4_3",
  silkscreen: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+silkscreen+printing+legend+markings+white+black+yellow&image_size=landscape_4_3",
  uvPrinting: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+with+multi-color+uv+printing+logos+and+graphics&image_size=landscape_4_3",
  edgeConnector: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+gold+fingers+edge+connector+with+bevelling+illustration&image_size=landscape_4_3",
  surfaceFinish: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+surface+finish+comparison+hasl+enig+osp+immersion+silver+gold&image_size=landscape_4_3",
  viaProcess: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+via+covering+process+tenting+vs+plugging+vs+filling+illustration&image_size=landscape_4_3",
  finishedCopper: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+copper+weight+thickness+comparison+1oz+2oz+3oz&image_size=landscape_4_3",
  removeProductNo: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+manufacturing+product+number+marking+removal+illustration&image_size=landscape_4_3",
  impedanceControl: "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=pcb+impedance+control+differential+pair+traces+illustration&image_size=landscape_4_3",
};

const ADVANCED_SELECT_FIELDS = [
  {
    name: "peelableSoldermask",
    label: "Peelable soldermask",
    options: [{ value: "none", label: "None" }, { value: "top", label: "Top side" }, { value: "bottom", label: "Bottom side" }, { value: "both", label: "Both sides" }],
  },
  {
    name: "holeCopperThickness",
    label: "Hole copper thickness",
    options: ["standard_20um", "25um", "30um", "35um", "50um", "55um", "70um"].map((value) => ({
      value,
      label: value === "standard_20um" ? "Standard(20um)" : value,
    })),
  },
  {
    name: "ulMarking",
    label: "UL marking",
    options: [
      { value: "none", label: "None" },
      { value: "top_silkscreen", label: "Yes - add to top silkscreen" },
      { value: "bottom_silkscreen", label: "Yes - add to bottom silkscreen" },
      { value: "top_soldermask", label: "Yes - add to top soldermask" },
      { value: "bottom_soldermask", label: "Yes - add to bottom soldermask" },
      { value: "top_copper", label: "Yes - add to top copper" },
      { value: "bottom_copper", label: "Yes - add to bottom copper" },
      { value: "factory_default", label: "Yes - as Creatyea default" },
      { value: "customer_specified", label: "Yes - as customer specified" },
    ],
  },
  {
    name: "dateCode",
    label: "Date code",
    options: [
      { value: "none", label: "None" },
      { value: "factory_prefer", label: "Add as Creatyea prefer" },
      { value: "customer_required", label: "Add as customer's required" },
      { value: "update_designed", label: "Update the designed date code" },
    ],
  },
  {
    name: "packageBox",
    label: "Package box",
    options: [{ value: "with_logo", label: "With Creatyea logo" }, { value: "neutral", label: "Neutral box" }],
  },
  {
    name: "serialNumbers",
    label: "Add serial numbers",
    options: [
      { value: "none", label: "None" },
      { value: "accept_non_consecutive", label: "Yes - Accept non-consecutive serial numbers - solid font" },
      { value: "consecutive_required", label: "Yes - Don't accept non-consecutive serial numbers - solid font" },
    ],
  },
];

const INSPECTION_REPORT_OPTIONS = [
  { value: "default", label: "Default Inspection Report", disabled: false },
  { value: "microsection", label: "Microsection Inspection Report", disabled: true },
  { value: "solderability", label: "Solderability Test Report", disabled: true },
  { value: "thermal_stress", label: "Thermal Stress Test Report", disabled: true },
  { value: "impedance_test", label: "Impedance Test Report", disabled: true },
  { value: "humidity_indicator", label: "Humidity indicator cards", disabled: true },
];

const STENCIL_SIZE_OPTIONS = [
  { value: "custom", label: "Custom Size（Valid area 190×290mm）" },
  { value: "370x470", label: "370×470mm（Valid area 190×290mm）" },
  { value: "420x520", label: "420×520mm（Valid area 240×340mm）" },
  { value: "450x550", label: "450×550mm（Valid area 270×370mm）" },
  { value: "584x584", label: "584×584mm（Valid area 380×380mm）" },
  { value: "550x650", label: "550×650mm（Valid area 350×450mm）" },
  { value: "736x736", label: "736×736mm（Valid area 500×500mm）" },
  { value: "400x600", label: "400×600mm（Valid area 220×400mm）" },
  { value: "400x800", label: "400×800mm（Valid area 220×600mm）" },
  { value: "400x1000", label: "400×1000mm（Valid area 220×760mm）" },
  { value: "500x800", label: "500×800mm（Valid area 320×600mm）" },
  { value: "400x1200", label: "400×1200mm（Valid area 220×1000mm）" },
  { value: "400x1400", label: "400×1400mm（Valid area 220×1200mm）" },
  { value: "500x1200", label: "500×1200mm（Valid area 320×1000mm）" },
  { value: "500x1400", label: "500×1400mm（Valid area 320×1200mm）" },
  { value: "custom_large", label: "Custom Size（Valid area 550×550mm）" },
];

const STENCIL_SIDE_OPTIONS = [
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "top_bottom_single", label: "Top+Bottom(On Single Stencil)" },
  { value: "top_bottom_separate", label: "Top & Bottom(On Separate Stencil)" },
];

const STENCIL_THICKNESS_OPTIONS = ["0.08", "0.10", "0.12", "0.15", "0.2", "0.25", "0.3"];

const SOLDER_MASK_SWATCHES = {
  green: "#0aa33c",
  red: "#d51b0f",
  yellow: "#ffc400",
  blue: "#1d4ed8",
  white: "#ffffff",
  black: "#000000",
  pink: "#f8b4c4",
  grey: "#9ca3af",
  orange: "#f59e0b",
  transparent: "linear-gradient(135deg, #f8fafc, #e5e7eb)",
  purple: "#c026d3",
  matte_black: "linear-gradient(135deg, #666666, #050505)",
  matte_green: "linear-gradient(135deg, #39d353, #08752b)",
  matte_blue: "linear-gradient(135deg, #93c5fd, #1d4ed8)",
  matte_red: "linear-gradient(135deg, #fca5a5, #dc2626)",
  none: "linear-gradient(135deg, #f9fafb 42%, #111827 43%, #111827 55%, #f9fafb 56%)",
};

// Edge connector 专用表面处理选项：顺序和显示文字与 PCBWay 目标页面保持一致。
const EDGE_CONNECTOR_SURFACE_FINISH_OPTIONS = [
  { value: "hasl_with_lead", label: "HASL with lead" },
  { value: "hasl_lead_free", label: "HASL lead free" },
  { value: "enig", label: "Immersion gold(ENIG)" },
  { value: "osp", label: "OSP" },
  { value: "hard_gold", label: "Hard gold" },
  { value: "immersion_silver", label: "Immersion silver(Ag)" },
  { value: "immersion_tin", label: "Immersion tin" },
  { value: "enepig", label: "ENEPIG" },
  { value: "plain_copper", label: "None/Plain copper" },
];

// 普通 Surface finish 选项：显示文字与 PCBWay 目标页面保持一致。
const SURFACE_FINISH_OPTIONS = [
  { value: "hasl_with_lead", label: "HASL with lead" },
  { value: "hasl_lead_free", label: "HASL lead free" },
  { value: "enig", label: "Immersion gold(ENIG)" },
  { value: "osp", label: "OSP" },
  { value: "hard_gold", label: "Hard gold" },
  { value: "immersion_silver", label: "Immersion silver(Ag)" },
  { value: "immersion_tin", label: "Immersion tin" },
  { value: "hasl_lead_free_selective_enig", label: "HASL lead free + Selective Immersion gold" },
  { value: "hasl_lead_free_selective_hard_gold", label: "HASL lead free + Selective Hard gold" },
  { value: "enig_selective_hard_gold", label: "Immersion gold + Selective Hard gold" },
  { value: "enepig", label: "ENEPIG" },
  { value: "plain_copper", label: "None/Plain copper" },
];

const SURFACE_FINISH_DETAIL_DEFAULTS = {
  hasl_with_lead: {
    specialRequest: "Standard hot air solder leveling with lead.",
  },
  hasl_lead_free: {
    specialRequest: "Lead-free HASL, suitable for RoHS requirements.",
  },
  enig: {
    goldThickness: '1U"',
  },
  osp: {
    specialRequest: "OSP is suitable for short storage cycles and requires careful handling.",
  },
  hard_gold: {
    goldNickelThickness: 'Au:5U"/Ni:120U"',
  },
  immersion_silver: {
    specialRequest: "Immersion silver is suitable for flat soldering surfaces and moderate storage cycles.",
  },
  immersion_tin: {
    specialRequest: "Immersion tin provides flat finish and requires proper storage before assembly.",
  },
  hasl_lead_free_selective_enig: {
    goldThickness: '1U"',
  },
  hasl_lead_free_selective_hard_gold: {
    goldNickelThickness: 'Au:10U"/Ni:120U"',
  },
  enig_selective_hard_gold: {
    immersionHardGoldThickness: 'Immersion gold: 1U", Hard gold: Au:10U"/Ni:120U"',
  },
  enepig: {
    enepigThickness: 'Ni:200U" Pd:2U" Au:2U"',
  },
  plain_copper: {
    specialRequest: "No surface finish. Copper may oxidize and requires manual review.",
  },
};

const SURFACE_FINISH_DETAIL_CONFIG = {
  hasl_with_lead: {
    notice: "HASL with lead is not RoHS lead-free. Please confirm product compliance requirements.",
  },
  hasl_lead_free: {
    notice: "HASL lead free is the default economical lead-free finish.",
  },
  enig: {
    fields: [
      {
        name: "goldThickness",
        label: "Thickness of\nImmersion Gold",
        options: ['1U"', '2U"', '3U"'],
      },
    ],
  },
  osp: {
    notice: "OSP is a thin organic coating. It is recommended for short storage time before assembly.",
  },
  hard_gold: {
    fields: [
      {
        name: "goldNickelThickness",
        label: "Au/Ni thickness",
        options: ['Au:5U"/Ni:120U"'],
        actionLabel: "Please choose other options of Gold /Nickel thickness here",
      },
    ],
  },
  immersion_silver: {
    notice: "Immersion silver has a flat surface and is suitable for high-frequency or lead-free assembly needs.",
  },
  immersion_tin: {
    notice: "Immersion tin has good flatness. Storage and handling conditions should be controlled.",
  },
  hasl_lead_free_selective_enig: {
    fields: [
      {
        name: "goldThickness",
        label: "Thickness of\nImmersion gold",
        options: ['1U"'],
        actionLabel: "Please choose other options of Immersion gold thickness here",
      },
    ],
  },
  hasl_lead_free_selective_hard_gold: {
    fields: [
      {
        name: "goldNickelThickness",
        label: "Thickness of\nHard gold",
        options: ['Au:10U"/Ni:120U"'],
        actionLabel: "Please choose other options of Gold/Nickel thickness here",
      },
    ],
  },
  enig_selective_hard_gold: {
    fields: [
      {
        name: "immersionHardGoldThickness",
        label: "Immersion gold /\nHard gold thickness",
        options: ['Immersion gold: 1U", Hard gold: Au:10U"/Ni:120U"'],
        actionLabel: "Please choose other options thickness here",
      },
    ],
  },
  enepig: {
    fields: [
      {
        name: "enepigThickness",
        label: "Thickness of\nENEPIG",
        options: ['Ni:200U" Pd:2U" Au:2U"'],
        actionLabel: "Please choose other options of Au/Pd thickness here",
      },
    ],
  },
  plain_copper: {
    notice: "None/Plain copper has no protective finish and may oxidize. Please confirm this is intentional.",
  },
};

const SILKSCREEN_SWATCHES = {
  white: "#ffffff",
  black: "#000000",
  yellow: "#ffc400",
  blue: "#1d4ed8",
  grey: "#9ca3af",
  none: "linear-gradient(135deg, #f3f4f6 42%, #222222 43%, #222222 57%, #f3f4f6 58%)",
};

const PCB_COLOR_LABELS = {
  green: "Green",
  red: "Red",
  yellow: "Yellow",
  blue: "Blue",
  white: "White",
  black: "Black",
  purple: "Purple",
  matte_black: "Matte Black",
  matte_green: "Matte Green",
  none: "None",
};

const STANDARD_PCB_FALLBACK_CONFIG = {
  boardTypes: [
    { value: "single_pieces", label: "Single Pieces" },
    { value: "panel_by_customer", label: "Panel by Customer" },
    { value: "panel_by_supplier", label: "Panel by Supplier" },
  ],
  layers: [1, 2, 4, 6, 8, 10, 12, 14],
  materials: [
    { value: "FR4", label: "FR-4" },
    { value: "Aluminum", label: "Aluminum" },
    { value: "Rogers", label: "Rogers" },
    { value: "HDI", label: "HDI (Buried/Blind Vias)" },
    { value: "CopperBase", label: "Copper Base" },
  ],
  fr4TgOptions: [
    { value: "TG130", label: "TG 130-140" },
    { value: "TG150", label: "TG 150-160" },
    { value: "TG170", label: "TG 170-180" },
    { value: "S1000H_TG150", label: "S1000H TG150" },
    { value: "S1000_2M_TG170", label: "S1000-2M TG170" },
  ],
  thicknessOptions: [0.2, 0.3, 0.4, 0.6, 0.8, 1, 1.2, 1.6, 2, 2.4, 2.6, 2.8, 3, 3.2],
  trackSpacingOptions: ["3/3mil", "4/4mil", "5/5mil", "6/6mil", "8/8mil"],
  holeSizeOptions: [
    "0.15mm",
    "0.2mm",
    "0.25mm",
    "0.3mm",
    "0.5mm",
    "0.8mm",
    "1.0mm",
    { value: "no_drill", label: "No Drill" },
  ],
  solderMaskOptions: [
    { value: "green", label: "Green" },
    { value: "red", label: "Red" },
    { value: "yellow", label: "Yellow" },
    { value: "blue", label: "Blue" },
    { value: "white", label: "White" },
    { value: "black", label: "Black" },
    { value: "purple", label: "Purple" },
    { value: "matte_black", label: "Matte Black" },
    { value: "matte_green", label: "Matte Green" },
    { value: "none", label: "None" },
  ],
  silkscreenOptions: [
    { value: "white", label: "White" },
    { value: "black", label: "Black" },
    { value: "yellow", label: "Yellow" },
    { value: "none", label: "None" },
  ],
  surfaceFinishOptions: SURFACE_FINISH_OPTIONS,
  viaProcessOptions: [
    { value: "tenting_vias", label: "Tenting Vias" },
    { value: "plugged_vias", label: "Plugged Vias with Solder Mask" },
    { value: "vias_not_covered", label: "Vias Not Covered" },
  ],
  copperOptions: [
    { value: "0oz", label: "Bare Board (0 oz Cu)" },
    "0.5oz",
    "1oz",
    "2oz",
    "3oz",
    "4oz",
    "5oz",
    "6oz",
    "7oz",
    "8oz",
    "9oz",
    "10oz",
    "11oz",
    "12oz",
    "13oz",
    "14oz",
    "15oz",
    "16oz",
    "17oz",
    "18oz",
    "19oz",
    "20oz",
  ],
  uvPrintingOptions: [
    { value: "none", label: "None" },
    { value: "top", label: "Single-sided: Top" },
    { value: "bottom", label: "Single-sided: Bottom" },
    { value: "double_sided", label: "Double-sided" },
  ],
  removeProductNoOptions: [
    { value: "no", label: "No" },
    { value: "yes", label: "Yes (extra +$1.5)" },
    { value: "specified_location", label: "Specify a location" },
  ],
  edgeConnectorBevellingOptions: [
    { value: "no", label: "No" },
    { value: "20", label: "Yes (20deg)" },
    { value: "30", label: "Yes (30deg)" },
    { value: "45", label: "Yes (45deg)" },
  ],
  advancedOptions: [
    { key: "castellatedHoles", label: "Half-cut/Castellated holes" },
    { key: "edgePlating", label: "Edge plating" },
    { key: "impedanceControl", label: "Impedance control" },
    { key: "halogenFree", label: "Halogen-free" },
    { key: "customStackup", label: "Custom stackup" },
    { key: "carbonInk", label: "Carbon ink" },
    { key: "resinFilledVias", label: "All vias filled with resin and capped" },
    { key: "viaInPad", label: "Via in pad" },
    { key: "countersink", label: "Countersink/Counterbore" },
    { key: "zAxisMilling", label: "Z-axis milling" },
    { key: "pressFitHoles", label: "Press-fit holes" },
    // TODO：确认工厂是否支持铜填孔及其计价规则；确认前保留字段并进入人工复核。
    { key: "copperFilledVias", label: "All vias filled with copper and capped" },
    { key: "blackFr4", label: "Black FR4 (black core)" },
    { key: "embeddedCopper", label: "Embedded copper PCB" },
    { key: "cavityPcb", label: "Cavity PCB" },
    { key: "semiFlexPcb", label: "Semi-Flex PCB" },
    { key: "hybridPcb", label: "Whole/Partially hybrid PCB" },
    { key: "backDrillPcb", label: "Back drill PCB" },
    { key: "leadlessHardGold", label: "Leadless partially plated hard gold" },
    { key: "paperBetweenPcbs", label: "Paper between PCBs" },
  ],
};

const DEFAULT_SPEC_BY_CATEGORY = {
  advanced_pcb: {
    layers: 8,
    material: "Rogers",
    size: { length: 100, width: 80, unit: "mm" },
    quantity: 5,
    impedanceControl: true,
    blindBuriedVias: false,
    customStackup: false,
    viaInPad: false,
    resinFilledVias: false,
    edgePlating: false,
    goldFingers: false,
    eTest: true,
    specialRequest: "",
  },
  fpc_rigid_flex: {
    flexPcbType: "flexible_pcb",
    boardType: "single_pieces",
    layers: 1,
    material: "polyimide_flex",
    size: { length: 80, width: 50, unit: "mm" },
    quantity: 10,
    thickness: 0.1,
    coverlay: true,
    stiffener: false,
    surfaceFinish: "enig",
    finishedCopper: "1oz",
    minTrackSpacing: "0.06mm",
    minHoleSize: "0.2mm",
    specialRequest: "",
  },
  assembly: {
    sourcingType: "turnkey",
    boardType: "single_pieces",
    assemblySide: "top",
    quantity: "",
    sensitiveComponents: false,
    acceptAlternatives: false,
    uniquePartsCount: "",
    smdPartsCount: "",
    bgaQfpPartsCount: "",
    throughHolePartsCount: "",
    depanelBoards: false,
    functionTest: false,
    conformalCoating: false,
    firmwareLoading: false,
    pressFitAssembly: false,
    boxBuildAssembly: false,
    cableHarnessAssembly: false,
    packageBox: "neutral",
    flyingProbeTesting: false,
    xrayTestCount: "",
    detailedInformation: "",
    specialRequest: "",
  },
  smd_stencil: {
    stencilType: "framework",
    multiLevel: false,
    size: "370x470",
    customWidth: 190,
    customHeight: 290,
    poNumber: "",
    quantity: 1,
    thickness: "0.12",
    side: "top",
    electropolishing: false,
    fiducials: "none",
    specialRequest: "",
  },
  cnc_3d: {
    serviceType: "cnc",
    material: "Aluminum",
    materialGrade: "Aluminum 6061",
    size: { length: 100, width: 80, height: 20, unit: "mm" },
    quantity: 1,
    tolerance: "standard",
    surfaceTreatment: "none",
    color: "Silver white",
    process: "SLA",
    surfaceFinish: "",
    thicknessMode: "subject_to_3d_file",
    standardThickness: "",
    customThickness: "",
    technicalDrawingFileName: "",
    hasThreads: false,
    hasInserts: false,
    hasWelding: false,
    toleranceRequirement: "iso_2768_1",
    surfaceRoughness: "250uin_6_3um",
    partMarking: [],
    partsAssembly: "no",
    wallThicknessRiskTaken: true,
    nonStandardPrintedThreadsRiskTaken: true,
    finishedAppearance: "standard",
    inspection: "standard_no_report",
    productDescription: "",
    injectionMoldingType: "steel_mold",
    firstBatchQuantity: "",
    annualVolume: "≤10,000pcs",
    materialAdditive: "None",
    spiFinish: "SPI B-3",
    toolRequirement: "order_new_mold",
    moldDisposition: "keep_in_factory",
    moldRequirementDetails: "",
    specialRequest: "",
  },
};

const GENERIC_SPEC_FIELDS = {
  advanced_pcb: [
    { name: "layers", label: "Layers", type: "number" },
    { name: "material", label: "Material (e.g. FR4, Rogers, PTFE)", type: "text" },
    { name: "hdiStructure", label: "HDI Structure", type: "select", options: ["None", "1+N+1", "2+N+2", "3+N+3", "Any Layer"] },
    { name: "blindBuriedViasStackup", label: "Blind/Buried Vias Stackup", type: "text" },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "impedanceControl", label: "Impedance Control", type: "checkbox" },
    { name: "customStackup", label: "Custom Stackup", type: "checkbox" },
    { name: "viaInPad", label: "Via In Pad", type: "checkbox" },
    { name: "resinFilledVias", label: "Resin Filled Vias", type: "checkbox" },
    { name: "edgePlating", label: "Edge Plating", type: "checkbox" },
    { name: "goldFingers", label: "Gold Fingers", type: "checkbox" },
  ],
  fpc_rigid_flex: [
    { name: "flexPcbType", label: "PCB Type", type: "select", options: ["flexible_pcb", "rigid_flex_board"] },
    { name: "boardType", label: "Board Type", type: "select", options: ["single_pieces", "panel_by_customer", "panel_by_supplier"] },
    { name: "layers", label: "Layers", type: "number" },
    { name: "material", label: "Material", type: "text" },
    { name: "stiffenerMaterial", label: "Stiffener Material", type: "select", options: ["None", "PI", "FR4", "Stainless Steel", "Aluminum"] },
    { name: "stiffenerThickness", label: "Stiffener Thickness (mm)", type: "number" },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "thickness", label: "Finished Thickness (mm)", type: "number" },
    { name: "coverlay", label: "Coverlay", type: "checkbox" },
    { name: "surfaceFinish", label: "Surface Finish", type: "select", options: ["enig", "hasl", "osp"] },
    { name: "finishedCopper", label: "Finished Copper", type: "select", options: ["0.5oz", "1oz", "2oz"] },
  ],
  assembly: [
    { name: "assemblySide", label: "Assembly Side", type: "select", options: ["single_side", "double_side"] },
    { name: "sourcingType", label: "Sourcing Type", type: "select", options: ["turnkey", "consigned", "kitted"] },
    { name: "quantity", label: "Board Quantity", type: "number" },
    { name: "uniquePartsCount", label: "Unique Parts Count", type: "number" },
    { name: "smtJoints", label: "SMT Solder Joints", type: "number" },
    { name: "dipJoints", label: "DIP/Thru-hole Joints", type: "number" },
    { name: "bgaQfnCount", label: "BGA/QFN Count", type: "number" },
  ],
  smd_stencil: [
    { name: "stencilType", label: "Stencil Type", type: "select", options: ["framework", "non_framework"] },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "thickness", label: "Thickness (mm)", type: "number" },
    { name: "side", label: "Side", type: "select", options: ["top", "bottom", "top_bottom"] },
    { name: "electropolishing", label: "Electropolishing", type: "checkbox" },
    { name: "fiducials", label: "Fiducials", type: "checkbox" },
  ],
  cnc_3d: [
    { name: "serviceType", label: "Service Type", type: "select", options: ["cnc", "3d_printing"] },
    { name: "material", label: "Material", type: "text" },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "tolerance", label: "Tolerance", type: "text" },
    { name: "surfaceTreatment", label: "Surface Treatment", type: "text" },
    { name: "color", label: "Color", type: "text" },
  ],
};

const CNC_3D_SERVICE_OPTIONS = [
  {
    value: "cnc",
    title: "CNC machining",
    description: "Milling (3-axis, 5-axis), Turning",
    image: "/img/chamfering.png",
  },
  {
    value: "sheet_metal",
    title: "Sheet metal",
    description: "Laser cutting, Bending",
    image: "/img/slanted_edge_white_bg.png",
  },
  {
    value: "3d_printing",
    title: "3D printing",
    description: "FDM, SLA, SLS, MJF, DMLS, Polyjet",
    image: "/img/product.jpg",
  },
  {
    value: "injection_molding",
    title: "Injection molding",
    description: "Vacuum casting",
    image: "/img/imgnotavailable.png",
  },
];

const INJECTION_MOLDING_TYPE_OPTIONS = [
  {
    value: "steel_mold",
    title: "Injection molding",
    description: "Steel mold",
  },
  {
    value: "silicone_mold",
    title: "Vacuum casting",
    description: "Silicone mold",
  },
];

const INJECTION_ANNUAL_VOLUME_OPTIONS = [
  "≤10,000pcs",
  "10,000-50,000pcs",
  "50,000-100,000pcs",
  "≥100,000pcs",
];

const CNC_3D_UNIT_OPTIONS = ["mm", "inch", "cm"];

const CNC_3D_MATERIAL_OPTIONS = [
  "Aluminum",
  "Stainless steel",
  "Brass",
  "Copper",
  "Titanium",
  "Mild steel",
  "Alloy steel",
  "Tool steel",
  "Spring steel",
  "ABS",
  "Polycarbonate (PC)",
  "Nylon",
  "Polypropylene (PP)",
  "POM",
  "PTFE (Teflon)",
  "PMMA (Acrylic)",
  "Polyethylene (PE)",
  "PEEK",
  "Bakelite",
  "FR4",
  "Rubber",
  "Carbon Fiber",
];

const THREE_D_PRINTING_MATERIAL_OPTIONS = [
  "Resin",
  "Nylon",
  "PLA",
  "ABS",
  "PETG",
  "TPU",
  "PC",
  "ASA",
  "PEEK",
  "PPS",
  "Aluminum",
  "Stainless steel",
  "Titanium",
  "Tool steel",
];

const SHEET_METAL_MATERIAL_OPTIONS = [
  "Aluminum",
  "Stainless steel",
  "Mild steel",
  "Copper",
  "PMMA (Acrylic)",
  "Carbon Fiber",
];

const INJECTION_MOLDING_MATERIAL_OPTIONS = [
  "ABS",
  "POM",
  "Nylon",
  "Polycarbonate (PC)",
  "PC/ABS",
  "PVC",
  "Polyethylene (PE)",
  "Polypropylene (PP)",
  "Polystyrene (PS)",
  "TPU",
];

const VACUUM_CASTING_MATERIAL_OPTIONS = [
  "ABS",
  "POM",
  "Nylon",
  "Polycarbonate (PC)",
  "PVC",
  "Polyethylene (PE)",
  "Polypropylene (PP)",
  "Polystyrene (PS)",
  "TPU",
  "Rubber",
];

const INJECTION_MOLDING_ABS_TYPE_OPTIONS = ["ABS"];

const CNC_ALUMINUM_TYPE_OPTIONS = [
  "Aluminum 6061",
  "Aluminum 7075",
  "Aluminum 5052",
  "Aluminum 2A12",
];

const SHEET_METAL_ALUMINUM_TYPE_OPTIONS = [
  "Aluminum 5052",
  "Aluminum 6061",
];

const THREE_D_RESIN_TYPE_OPTIONS = [
  "Standard white material (UTR 8360)",
  "UTR Imagine Black",
  "UTR-8100 (transparent)",
  "PWR Dark Black",
  "UTR-8100 (translucent)",
  "Somos ® Ledo",
  "UTR 8220",
  "Somos ® Taurus",
  "UTR 3000",
  "UTR Therm",
  "Somos ® EvoLVe 128",
  "UTR Flex",
  "Somos ® PerFORM",
  "TDS EvoDent",
  "Formlabs ESD Resin",
];

const CNC_COLOR_OPTIONS = [
  { value: "Silver white", label: "Silver white", swatch: "#e6e8e8" },
];

const THREE_D_COLOR_OPTIONS = {
  Resin: [{ value: "Matte white", label: "Matte white", swatch: "#ffffff" }],
};

const INJECTION_MOLDING_COLOR_OPTIONS = [
  { value: "Black", label: "Black", swatch: "#000000" },
  { value: "White", label: "White", swatch: "#ffffff" },
  { value: "Transparent", label: "Transparent", swatch: "#f5f5f5" },
  { value: "Gray", label: "Gray", swatch: "#9b9b96" },
  { value: "Red", label: "Red", swatch: "#ff0000" },
  { value: "Blue", label: "Blue", swatch: "#1010ff" },
  { value: "Green", label: "Green", swatch: "#00f018" },
  { value: "Yellow", label: "Yellow", swatch: "#fff200" },
];

const VACUUM_CASTING_COLOR_OPTIONS = [
  { value: "Black", label: "Black", swatch: "#000000" },
  { value: "White", label: "White", swatch: "#ffffff" },
  { value: "Gray", label: "Gray", swatch: "#9b9b96" },
  { value: "Red", label: "Red", swatch: "#ff0000" },
  { value: "Blue", label: "Blue", swatch: "#1010ff" },
  { value: "Green", label: "Green", swatch: "#00f018" },
  { value: "Yellow", label: "Yellow", swatch: "#fff200" },
];

const CNC_MATERIAL_COLOR_OPTIONS = {
  Aluminum: CNC_COLOR_OPTIONS,
  Brass: [{ value: "Brass", label: "Brass", swatch: "#c9ad64" }],
};

const CNC_MATERIAL_DETAILS = {
  Aluminum: {
    title: "Aluminum 6061",
    image: "/img/Aluminum-Board.jpg",
    rating: "4.9",
    reviews: "126 Reviews",
  },
  Brass: {
    title: "Brass C360",
    image: "/img/copper.png",
    rating: "4.9",
    reviews: "126 Reviews",
  },
};

const SHEET_METAL_MATERIAL_DETAILS = {
  Aluminum: {
    title: "Aluminum 5052",
    image: "/img/Aluminum-Board.jpg",
    rating: "4.9",
    reviews: "322 Reviews",
  },
};

const THREE_D_MATERIAL_DETAILS = {
  Resin: {
    title: "Standard white material (UTR 8360)",
    image: "/img/product.jpg",
    rating: "5.0",
    reviews: "1163 Reviews",
  },
};

const INJECTION_MOLDING_MATERIAL_DETAILS = {
  ABS: {
    title: "ABS",
    image: "/img/product.jpg",
    rating: "5.0",
    reviews: "5 Reviews",
  },
};

const CNC_SURFACE_FINISH_OPTIONS = [
  "As machined",
  "Bead blasted",
  "Anodized Type II",
  "Hardcoat anodized",
  "Powder coating",
  "Brushing",
  "Polishing",
  "Black oxide",
  "Chromate conversion",
  "Nickel plating",
  "Zinc plating",
  "Gold plating",
  "Silver plating",
  "Passivation",
  "Electropolishing",
  "Heat treatment",
  "Sand blasting",
  "Painting",
  "Custom",
];

const THREE_D_SURFACE_FINISH_OPTIONS = [
  "None",
  "Sanding",
  "Polishing",
  "Painting",
  "Dyeing",
];

const THREE_D_PROCESS_OPTIONS = ["SLA"];

const SHEET_METAL_SURFACE_FINISH_OPTIONS = [
  "As fabricated",
  "Bead blasted",
  "Brushing",
  "Polishing",
  "Powder coating",
  "Painting",
  "Anodized clear",
  "Anodized black",
  "Hardcoat anodized",
  "Chromate conversion",
  "Passivation",
  "Electropolishing",
  "Zinc plating",
  "Nickel plating",
  "Chrome plating",
  "Tin plating",
  "Copper plating",
  "Black oxide",
  "Sand blasting",
  "Tumbling",
  "Deburring",
  "Silkscreen",
  "Laser marking",
  "Custom",
];

const SHEET_METAL_THICKNESS_OPTIONS = [
  "0.8mm",
  "1.0mm",
  "1.2mm",
  "1.5mm",
  "2.0mm",
  "2.5mm",
  "3.0mm",
  "4.0mm",
];

const INJECTION_MATERIAL_ADDITIVE_OPTIONS = [
  "None",
  "Flame Retardant",
  "Static Dissipative",
  "UV Stabilizer",
  "Custom Material",
];

const INJECTION_SPI_FINISH_OPTIONS = [
  "SPI B-3",
  "SPI A-1",
  "SPI A-2",
  "SPI A-3",
  "SPI B-1",
  "SPI B-2",
  "SPI C-1",
  "SPI C-2",
  "SPI C-3",
  "SPI D-1",
  "SPI D-2",
  "SPI D-3",
];

const VACUUM_CASTING_SURFACE_FINISH_OPTIONS = [
  "TODO Surface finish 1",
  "TODO Surface finish 2",
];

/**
 * 判断当前 Injection molding 子选项是否为 Vacuum casting。
 * @param {Object} spec CNC/3D 报价参数
 * @returns {boolean} 是否选中 Vacuum casting
 */
const isVacuumCastingSpec = (spec) => spec?.injectionMoldingType === "silicone_mold";

/**
 * 获取 Injection molding 当前子选项对应的材料选项。
 * @param {Object} spec CNC/3D 报价参数
 * @returns {Array<string>} 材料选项
 */
const getInjectionMaterialOptions = (spec) =>
  isVacuumCastingSpec(spec) ? VACUUM_CASTING_MATERIAL_OPTIONS : INJECTION_MOLDING_MATERIAL_OPTIONS;

/**
 * 获取 Injection molding 当前子选项对应的颜色选项。
 * @param {Object} spec CNC/3D 报价参数
 * @returns {Array<Object>} 颜色选项
 */
const getInjectionColorOptions = (spec) =>
  isVacuumCastingSpec(spec) ? VACUUM_CASTING_COLOR_OPTIONS : INJECTION_MOLDING_COLOR_OPTIONS;

const CNC_SURFACE_ROUGHNESS_OPTIONS = [
  { value: "250uin_6_3um", label: "250uin/6.3um Ra" },
  { value: "125uin_3_2um", label: "125uin/3.2um Ra" },
  { value: "63uin_1_6um", label: "63uin/1.6um Ra" },
  { value: "32uin_0_8um", label: "32uin/0.8um Ra" },
];

const CNC_PART_MARKING_OPTIONS = [
  { value: "silkscreen", label: "Silkscreen" },
  { value: "laser_engraving", label: "Laser engraving" },
];

const CNC_PARTS_ASSEMBLY_OPTIONS = [
  { value: "no", label: "No" },
  { value: "assembly_test", label: "Assembly test" },
  { value: "ship_in_assembly", label: "Ship in assembly" },
];

const CNC_INSPECTION_OPTIONS = [
  {
    value: "standard_no_report",
    label: "Standard Inspection (No report)",
    details: [
      "No extra fee",
      "Creatyea will do the dimension and surface feature inspections based on the drawings provided by the customer.",
    ],
  },
  {
    value: "standard_formal_report",
    label: "Standard Inspection with Formal Report",
    details: ["Pay extra", "2D technical drawings are required."],
  },
  {
    value: "cmm_formal_report",
    label: "CMM Inspection with Formal Report",
    details: ["Pay extra", "2D technical drawings are required."],
  },
  {
    value: "source_material_certification",
    label: "Source Material Certification",
    details: ["Pay extra", "2D technical drawings are required."],
  },
];

const INJECTION_INSPECTION_OPTIONS = CNC_INSPECTION_OPTIONS.filter((option) =>
  ["standard_no_report", "source_material_certification"].includes(option.value)
);

const CNC_PRODUCT_DESC_OPTIONS = [
  "Aerospace",
  "Automotive",
  "Consumer electronics",
  "Industrial equipment",
  "Medical device",
  "Robotics",
  "Other",
];

/**
 * 获取 CNC 材料对应的颜色选项。
 * @param {string} material 当前材料
 * @returns {Array<Object>} 颜色选项
 */
const getCncColorOptions = (material) => CNC_MATERIAL_COLOR_OPTIONS[material] || [];

/**
 * 获取 3D printing 材料对应的颜色选项。
 * @param {string} material 当前材料
 * @returns {Array<Object>} 颜色选项
 */
const get3dPrintingColorOptions = (material) => THREE_D_COLOR_OPTIONS[material] || [];

const QUOTE_STEPS = [
  "Online Quote",
  "Upload Gerber File",
  "Payment",
  "Fabrication",
  "Shipment",
  "Confirm and Review",
];

/**
 * 获取或创建当前浏览器会话的报价 Session ID。
 * @returns {string} 稳定的会话 ID
 */
const getQuoteSessionId = () => {
  const storageKey = "instant_quote_session_id";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) {
    return existingId;
  }

  const nextId = `quote-session-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 可选按钮组组件：将下拉选择改为平铺的按钮选择，模仿 PCBWay 交互。
 * @param {Object} props 组件属性
 * @param {string} props.name 字段名
 * @param {any} props.value 当前选中值
 * @param {Array} props.options 选项列表 [{value, label} | string | number]
 * @param {Function} props.onChange 切换回调
 * @param {boolean} props.disabled 是否禁用
 * @param {string} props.groupClassName 按钮组扩展样式
 * @param {string} props.buttonClassName 按钮扩展样式
 * @param {React.ReactNode} [props.trailingContent] 选项组末尾追加内容
 * @param {boolean} [props.options[].rowBreakAfter] 是否在该选项后强制换行
 */
const SelectableGroup = ({
  name,
  value,
  options,
  onChange,
  disabled,
  vertical = false,
  groupClassName = "",
  buttonClassName = "",
  trailingContent = null,
}) => {
  return (
    <div className={`flex ${vertical ? "flex-col items-start" : "flex-wrap"} gap-2 ${groupClassName}`}>
      {options.map((opt) => {
        const optValue = typeof opt === "object" ? opt.value : opt;
        const optLabel = typeof opt === "object" ? opt.label : opt;
        const optIcon = typeof opt === "object" ? opt.icon : null;
        const optIconClassName =
          typeof opt === "object" ? opt.iconClassName : null;
        const optSwatch = typeof opt === "object" ? opt.swatch : null;
        const optButtonClassName =
          typeof opt === "object" ? opt.optionClassName : null;
        const shouldBreakAfter =
          typeof opt === "object" ? opt.rowBreakAfter : false;
        const isOptDisabled = typeof opt === "object" ? opt.disabled : false;
        const isSelected = String(value) === String(optValue);

        return (
          <React.Fragment key={optValue}>
            <button
              type="button"
              disabled={disabled || isOptDisabled}
              onClick={() => onChange({ target: { name, value: optValue } })}
              title={isOptDisabled ? "This option is not available for the current configuration" : ""}
              className={`group relative flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-all ${
                isSelected
                  ? "border-green-600 bg-white text-gray-900 ring-1 ring-green-600"
                : "border-gray-300 bg-white text-gray-700 hover:border-green-400 hover:text-green-600"
              } ${disabled || isOptDisabled ? "opacity-40 cursor-not-allowed grayscale" : ""} ${buttonClassName} ${optButtonClassName || ""}`}
            >
              {optIcon && (
                <img
                  src={optIcon}
                  alt=""
                  className={optIconClassName || "h-4 w-6 object-contain"}
                />
              )}
              {optSwatch && (
                <span
                  aria-hidden="true"
                  className="h-5 w-5 flex-none border border-gray-300"
                  style={{ background: optSwatch }}
                />
              )}
              <span>{optLabel}</span>
              {isSelected && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-600 flex items-center justify-center rounded-tl-sm overflow-hidden">
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </button>
            {shouldBreakAfter ? (
              <span className="quote-option-row-break" aria-hidden="true" />
            ) : null}
          </React.Fragment>
        );
      })}
      {trailingContent}
    </div>
  );
};

/**
 * 为颜色选项补充对应色块，保持接口返回值和原有选择逻辑不变。
 * @param {Array} options 颜色选项列表
 * @param {Object} swatches 选项值与色块背景的映射
 * @returns {Array} 带色块信息的选项列表
 */
const addColorSwatches = (options = [], swatches = {}) => (
  options.map((option) => {
    const value = typeof option === "object" ? option.value : option;
    const label = typeof option === "object" ? option.label : option;

    return {
      value,
      label: PCB_COLOR_LABELS[value] || label,
      swatch: swatches[value],
    };
  })
);

/**
 * 工艺联动校验：根据当前选择的参数，计算哪些选项应该被禁用。
 * @param {string} fieldName 正在处理的字段名
 * @param {Object} pcbSpec 当前的 PCB 规范
 * @returns {Function} 返回一个函数，用于判断某个选项值是否应被禁用
 */
const getOptionRestriction = (fieldName, pcbSpec) => {
  const { layers, thickness, finishedCopper, minTrackSpacing } = pcbSpec;
  const layerCount = Number(layers);
  const copperOz = Number(String(finishedCopper).replace(/oz/i, "")) || 1;

  return (optValue) => {
    const val = typeof optValue === "object" ? optValue.value : optValue;

    // 1. 层数与厚度联动 (Layers vs Thickness)
    if (fieldName === "thickness") {
      const t = Number(val);
      if (layerCount >= 10 && t < 0.8) return true; // 10层及以上通常不建议小于0.8mm
      if (layerCount === 1 && t < 0.4) return true; // 单面板通常不提供超薄工艺
    }

    // 2. 铜厚与线宽联动 (Copper Weight vs Track/Spacing)
    if (fieldName === "minTrackSpacing") {
      const spacingMil = Number(String(val).split("/")[0]) || 6;
      if (copperOz >= 2 && spacingMil < 5) return true; // 2oz铜厚通常要求线宽线距 >= 5mil
      if (copperOz >= 3 && spacingMil < 6) return true; // 3oz铜厚通常要求线宽线距 >= 6mil
    }

    // 3. 线宽对铜厚的反向限制
    if (fieldName === "finishedCopper") {
      const spacingMil = Number(String(minTrackSpacing).split("/")[0]) || 6;
      const optCopperOz = Number(String(val).replace(/oz/i, "")) || 1;
      if (spacingMil < 5 && optCopperOz >= 2) return true;
      if (spacingMil < 6 && optCopperOz >= 3) return true;
    }

    return false;
  };
};

/**
 * 格式化成品铜厚选项，使接口字符串和本地配置保持统一展示。
 * @param {Array} options 铜厚选项列表
 * @returns {Array} 标准化后的铜厚选项
 */
const formatCopperOptions = (options = []) => (
  options
    .map((option) => {
      const value = typeof option === "object" ? option.value : option;
      const ounceValue = String(value).replace(/oz$/i, "");

      return {
        value,
        ounceValue: Number(ounceValue),
        label: String(value).toLowerCase() === "0oz"
          ? "Bare Board (0 oz Cu)"
          : `${ounceValue} oz Cu`,
      };
    })
    .sort((firstOption, secondOption) => firstOption.ounceValue - secondOption.ounceValue)
);

/**
 * 报价字段提示按钮：支持鼠标悬停、键盘聚焦和移动端点按查看说明。
 * @param {Object} props 组件属性
 * @param {string} props.content 提示内容
 * @param {string} props.label 字段名称，用于无障碍文本
 * @param {string} [props.image] 参考图 URL
 */
const HelpTooltip = ({ content, label, image }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipId = `quote-help-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <span
      className="relative inline-flex flex-none"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        aria-label={`${label} help`}
        aria-describedby={tooltipId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(true)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        className="quote-help-button flex h-4 w-4 min-w-4 flex-none items-center justify-center rounded-full border border-[#8cc63f] bg-[#a8cf45] text-[11px] font-bold leading-none text-white outline-none transition-colors hover:bg-[#8cc63f] focus:bg-[#8cc63f] focus:ring-2 focus:ring-lime-200"
      >
        ?
      </button>
      <div
        id={tooltipId}
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-72 -translate-x-1/2 rounded-md bg-gray-900 p-3 text-left text-xs font-normal leading-5 text-white shadow-lg transition-opacity ${
          isOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        {image && (
          <div className="mb-2 overflow-hidden rounded-sm border border-gray-700 bg-white">
            <img src={image} alt={`${label} reference`} className="w-full object-contain" />
          </div>
        )}
        <p>{content}</p>
      </div>
    </span>
  );
};

/**
 * Standard PCB 字段标签：统一标签宽度、对齐方式和帮助提示入口。
 * @param {Object} props 组件属性
 * @param {string} props.label 字段显示名称
 * @param {string} props.help 提示内容
 * @param {string} [props.image] 参考图 URL
 * @param {boolean} props.alignTop 是否与顶部控件对齐
 */
const QuoteFieldLabel = ({ label, help, image, alignTop = false }) => (
  <div className={`flex w-48 flex-none items-center gap-1 text-sm font-semibold text-gray-600 ${alignTop ? "md:pt-2" : ""}`}>
    <span>{label}</span>
    <HelpTooltip content={help} label={label} image={image} />
  </div>
);

/**
 * Assembly 高级服务字段标签：显示目标站风格的绿色问号提示。
 * @param {Object} props 组件属性
 * @param {string} props.label 字段名称
 * @param {string} props.help 帮助提示内容
 */
const AssemblyHelpLabel = ({ label, help }) => (
  <span className="flex items-center gap-1 whitespace-pre-line text-sm leading-5 text-gray-600">
    <span>{label}</span>
    <HelpTooltip content={help} label={label} />
  </span>
);

/**
 * Assembly 高级服务选择按钮：统一选中边框和右下角勾选标记。
 * @param {Object} props 组件属性
 * @param {boolean} props.selected 是否选中
 * @param {Function} props.onClick 点击处理函数
 * @param {React.ReactNode} props.children 按钮内容
 */
const AssemblyChoiceButton = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative overflow-hidden border px-3 py-2 text-sm ${
      selected
        ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
        : "border-gray-300 text-gray-500"
    }`}
  >
    {children}
    {selected ? (
      <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center bg-[#b9a175] text-[10px] leading-none text-white">
        ✓
      </span>
    ) : null}
  </button>
);

/**
 * 不同拼板设计示例：支持鼠标悬停、键盘聚焦和移动端点按查看图片。
 */
const DifferentDesignExample = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        aria-label="Show different design example"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(true)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        className="border-0 bg-transparent p-0 text-sm font-medium text-blue-500 underline decoration-dotted underline-offset-4 outline-none hover:text-blue-700 focus:text-blue-700"
      >
        e.g.
      </button>
      <span
        className={`absolute left-0 top-full z-40 mt-2 border border-gray-300 bg-white p-3 shadow-lg transition-opacity sm:left-auto sm:right-0 ${
          isOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <img
          src="/img/different.gif"
          alt="Different PCB designs in one panel example"
          className="h-auto w-[278px] max-w-none"
        />
      </span>
    </span>
  );
};

/**
 * 获取选项的稳定唯一值。
 * @param {Object|string|number} option 选项对象或基础值
 * @returns {string} 用于去重的选项值
 */
const getOptionValue = (option) => {
  if (option && typeof option === "object") {
    return String(option.value ?? option.key ?? option.label ?? "");
  }

  return String(option);
};

/**
 * 合并接口配置和本地兜底配置，接口旧数据不会覆盖新增选项。
 * @param {Array} apiOptions 接口返回的选项列表
 * @param {Array} fallbackOptions 本地兜底选项列表
 * @returns {Array} 去重后的完整选项列表
 */
const mergeOptions = (apiOptions = [], fallbackOptions = []) => {
  const mergedOptions = [];
  const seenValues = new Set();

  [...apiOptions, ...fallbackOptions].forEach((option) => {
    const optionValue = getOptionValue(option);
    if (!optionValue || seenValues.has(optionValue)) {
      return;
    }

    seenValues.add(optionValue);
    mergedOptions.push(option);
  });

  return mergedOptions;
};

/**
 * 生成 Standard PCB 页面使用的完整配置。
 * @param {Object} apiConfig 接口返回配置
 * @returns {Object} 合并后的 Standard PCB 配置
 */
const buildStandardPcbConfig = (apiConfig = {}) => {
  return Object.entries(STANDARD_PCB_FALLBACK_CONFIG).reduce(
    (nextConfig, [key, fallbackOptions]) => ({
      ...nextConfig,
      [key]:
        key === "advancedOptions"
          ? fallbackOptions
          : mergeOptions(apiConfig[key], fallbackOptions),
    }),
    {}
  );
};

/**
 * 渲染 Advanced PCB 材料选项的动态提示。
 * @param {string} material 当前材料选项
 * @returns {JSX.Element|null} 材料提示节点
 */
const renderAdvancedMaterialHint = (material) => {
  const hint = ADVANCED_MATERIAL_HINTS[material];

  if (!hint) {
    return null;
  }

  return (
    <p className="text-xs leading-5 text-amber-600">
      {hint.prefix}{" "}
      <span className="font-semibold text-green-600">{hint.highlight}</span>
      {hint.suffix ? ` ${hint.suffix}` : ""}
    </p>
  );
};

/**
 * Surface finish 联动字段标签：保持与普通报价字段相同的左侧宽度。
 * @param {Object} props 组件属性
 * @param {string} props.label 字段显示名称
 * @returns {JSX.Element} 标签节点
 */
const SurfaceFinishDetailLabel = ({ label }) => (
  <div className="quote-surface-finish-detail-label flex w-48 flex-none items-center text-sm font-semibold text-gray-600 md:pt-2">
    <span className="whitespace-pre-line">{label}:</span>
  </div>
);

/**
 * 渲染 Surface finish 的动态联动选项。
 * @param {string} surfaceFinish 当前表面处理
 * @param {Object} detail 当前表面处理明细
 * @param {Function} onChange 明细变更回调
 * @param {boolean} disabled 是否禁用
 * @returns {JSX.Element|null} 联动选项节点
 */
const renderSurfaceFinishDetail = (
  surfaceFinish,
  detail = {},
  onChange,
  disabled
) => {
  const config = SURFACE_FINISH_DETAIL_CONFIG[surfaceFinish];

  if (!config) {
    return null;
  }

  return (
    <>
      {config.fields?.length ? (
        <>
          {config.fields.map((field) => (
            <div key={field.name} className="quote-surface-finish-detail-field flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
              <SurfaceFinishDetailLabel label={field.label} />
              <div className="min-w-0 flex-grow">
                <SelectableGroup
                  name={field.name}
                  value={detail[field.name] || field.options[0]}
                  options={field.options}
                  onChange={onChange}
                  disabled={disabled}
                  groupClassName="quote-pcbway-options"
                  buttonClassName="quote-pcbway-option quote-surface-finish-detail-option justify-center"
                  trailingContent={
                    field.actionLabel ? (
                      <button
                        type="button"
                        disabled={disabled}
                        // TODO: 对接目标站的其它金/镍厚度弹窗选择逻辑。
                        className="quote-pcbway-option quote-surface-finish-detail-option quote-surface-finish-detail-action justify-center border border-gray-300 bg-white text-gray-700 transition-all hover:border-green-400 hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {field.actionLabel}
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>
          ))}
        </>
      ) : null}
      {config.notice ? (
        <div className="quote-surface-finish-notice-row flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
          <div className="hidden w-48 flex-none md:block" />
          <p className="quote-surface-finish-notice min-w-0 flex-grow">
            {config.notice}
          </p>
        </div>
      ) : null}
    </>
  );
};

/**
 * 格式化附件大小。
 * @param {number} size 文件大小（字节）
 * @returns {string} 可读文件大小
 */
const formatAttachmentSize = (size = 0) => {
  if (size >= 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  if (size >= 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${size || 0} B`;
};

/**
 * 根据文件名推断附件用途。
 * @param {File} file 浏览器文件对象
 * @param {string} fallback 默认用途
 * @returns {string} 附件用途
 */
const inferAttachmentCategory = (file, fallback = "other") => {
  const fileName = String(file?.name || "").toLowerCase();

  if (fileName.includes("bom") || /\.(xlsx|xls|csv)$/.test(fileName)) {
    return "bom";
  }

  if (
    fileName.includes("pick") ||
    fileName.includes("place") ||
    fileName.includes("cpl") ||
    fileName.includes("centroid")
  ) {
    return "pick_place";
  }

  if (/\.(step|stp|x_t|iges|igs|sldprt|stl|obj)$/.test(fileName)) {
    return "cad";
  }

  if (/\.(pdf|dwg|dxf)$/.test(fileName)) {
    return "drawing";
  }

  if (/\.(zip|rar|gbr|ger|gtl|gbl|gto|gbo|gts|gbs|gml|gm1|gko|drl|xln|txt)$/.test(fileName)) {
    return "gerber";
  }

  return fallback || "other";
};

/**
 * 构建附件状态项。
 * @param {File} file 浏览器文件对象
 * @param {string} fallbackCategory 默认用途
 * @returns {Object} 附件状态项
 */
const buildAttachmentItem = (file, fallbackCategory = "other") => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
  file,
  category: inferAttachmentCategory(file, fallbackCategory),
});

/**
 * 获取当前报价类型的附件要求。
 * @param {string} quoteType 报价类型
 * @returns {Object} 附件要求
 */
const getAttachmentRequirement = (quoteType) => {
  return QUOTE_ATTACHMENT_REQUIREMENTS[quoteType] || QUOTE_ATTACHMENT_REQUIREMENTS.standard_pcb;
};

/**
 * 渲染报价附件列表。
 * @param {Object} props 组件属性
 * @returns {JSX.Element|null} 附件列表
 */
const QuoteAttachmentList = ({
  attachments,
  disabled,
  onCategoryChange,
  onRemove,
  compact = false,
}) => {
  if (!attachments.length) {
    return null;
  }

  return (
    <div className={compact ? "quote-cnc-file-list" : "mt-3 grid grid-cols-1 gap-2"}>
      {attachments.map((item) => (
        <div
          key={item.id}
          className={
            compact
              ? "flex items-center justify-between gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-xs"
              : "flex flex-col gap-2 rounded border border-green-100 bg-green-50 px-3 py-2 text-xs text-green-800 md:flex-row md:items-center"
          }
        >
          <span className="min-w-0 flex-1 truncate font-semibold">
            {item.file.name}
          </span>
          <span className="text-gray-500">{formatAttachmentSize(item.file.size)}</span>
          <select
            value={item.category}
            onChange={(e) => onCategoryChange(item.id, e.target.value)}
            disabled={disabled}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
          >
            {Object.entries(ATTACHMENT_CATEGORIES)
              .filter(([value]) => value !== "auto")
              .map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
          </select>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            disabled={disabled}
            className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-600 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
};

/**
 * 即时报价表单组件：负责参数录入、在线计算、保存报价和加入购物车。
 */
function FormGetQuote() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [attachments, setAttachments] = useState([]);
  const [savedQuote, setSavedQuote] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [isQuoteDirty, setQuoteDirty] = useState(true);
  const [isParsing, setIsParsing] = useState(false);
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showPcbSpecifications, setShowPcbSpecifications] = useState(true);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showAssemblyOtherParameters, setShowAssemblyOtherParameters] = useState(false);
  const [showAssemblyAdvancedOptions, setShowAssemblyAdvancedOptions] = useState(false);
  const [showInchConversion, setShowInchConversion] = useState(false);
  const [showCustomThickness, setShowCustomThickness] = useState(false);
  const [showGoldThicknessModal, setShowGoldThicknessModal] = useState(false);
  const [goldThicknessDraft, setGoldThicknessDraft] = useState({
    selection: "10,120",
    au: "10",
    ni: "120",
  });
  const [inchSize, setInchSize] = useState({ length: 1, width: 1 });
  const [selectedDeliveryOptionId, setSelectedDeliveryOptionId] =
    useState("standard");
  const sessionId = useMemo(() => getQuoteSessionId(), []);

  const { data: configResponse, isLoading: isConfigLoading } =
    useGetInstantQuoteConfigQuery();
  const [calculateInstantQuote, { isLoading: isCalculating }] =
    useCalculateInstantQuoteMutation();
  const [createQuote, { isLoading: isCreatingQuote }] = useCreateQuoteMutation();
  const [updateQuote, { isLoading: isUpdatingQuote }] = useUpdateQuoteMutation();
  const [submitQuote, { isLoading: isSubmittingQuote }] = useSubmitQuoteMutation();
  const [addQuoteToCart, { isLoading: isAddingQuoteToCart }] =
    useAddQuoteToCartMutation();

  const config = useMemo(
    () => configResponse?.config || {},
    [configResponse?.config]
  );
  const standardPcbConfig = useMemo(
    () => buildStandardPcbConfig(config),
    [config]
  );
  const quoteCategories = config.quoteTypes || QUOTE_CATEGORIES;
  const isStandardPcb = formValues.quoteType === "standard_pcb";
  const isAdvancedPcb = formValues.quoteType === "advanced_pcb";
  const isFpcRigidFlex = formValues.quoteType === "fpc_rigid_flex";
  const isAssemblyQuote = formValues.quoteType === "assembly";
  const isSmdStencilQuote = formValues.quoteType === "smd_stencil";
  const isCnc3dQuote = formValues.quoteType === "cnc_3d";
  const isPcbQuoteForm = isStandardPcb || isAdvancedPcb || isFpcRigidFlex;
  const hasPcbSpecifications = isPcbQuoteForm || isAssemblyQuote;
  const isSavingQuote = isCreatingQuote || isUpdatingQuote;
  const isBusy =
    isCalculating || isSavingQuote || isSubmittingQuote || isAddingQuoteToCart;
  const isShippingPending =
    quoteResult?.pricing?.shippingMethod === "Pending Manual Review" ||
    (quoteResult?.reviewFlags || []).includes("SHIPPING_PENDING_CONFIRMATION");
  const isManualReviewRequired =
    (quoteResult?.reviewFlags || []).includes("MANUAL_REVIEW_REQUIRED");
  const deliveryOptions = quoteResult?.pricing?.deliveryOptions || [];
  const selectedDeliveryOption =
    quoteResult?.pricing?.selectedDeliveryOption ||
    deliveryOptions.find((item) => item.id === selectedDeliveryOptionId) ||
    null;
  const attachmentRequirement = getAttachmentRequirement(formValues.quoteType);

  useEffect(() => {
    if (!user) {
      return;
    }

    setFormValues((prevState) => ({
      ...prevState,
      customerInfo: {
        ...prevState.customerInfo,
        name: prevState.customerInfo.name || user?.name || "",
        email: prevState.customerInfo.email || user?.email || "",
      },
    }));
  }, [user]);

  /**
   * 切换当前报价品类。
   * @param {string} quoteType 报价品类
   */
  const handleQuoteTypeChange = (quoteType) => {
    setFormValues((prevState) => ({
      ...prevState,
      quoteType,
      spec: DEFAULT_SPEC_BY_CATEGORY[quoteType] || {},
      pcbSpec:
        quoteType === "advanced_pcb"
          ? {
              ...prevState.pcbSpec,
              material:
                prevState.pcbSpec.advancedPcbType === "hdi_buried_blind_vias"
                  ? "HDI"
                  : "tg140_fr4",
              layers:
                prevState.pcbSpec.advancedPcbType ===
                "hdi_buried_blind_vias"
                  ? Math.max(4, Number(prevState.pcbSpec.layers) || 4)
                  : prevState.pcbSpec.layers,
            }
          : quoteType === "fpc_rigid_flex"
          ? {
              ...prevState.pcbSpec,
              ...DEFAULT_SPEC_BY_CATEGORY.fpc_rigid_flex,
            }
          : quoteType === "standard_pcb"
          ? {
              ...prevState.pcbSpec,
              material: "FR4",
              layers: 2,
              thickness: 1.6,
              minTrackSpacing: "6/6mil",
              boardType: "single_pieces",
            }
          : prevState.pcbSpec,
    }));
    setSavedQuote(null);
    setQuoteResult(null);
    setSelectedDeliveryOptionId("standard");
    setAttachments([]);
    setQuoteDirty(true);
    setShowCustomerInfo(false);
  };

  /**
   * 更新客户信息字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      customerInfo: {
        ...prevState.customerInfo,
        [name]: value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新运输信息字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      shipping: {
        ...prevState.shipping,
        [name]: value.toUpperCase(),
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 PCB 规格字段。
   * @param {Object} e 包含 name 和 value 的事件对象
   */
  const handlePcbSpecChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        [name]:
          ["differentDesignCount", "quantity", "layers", "thickness"].includes(
            name
          )
            ? Number(value)
            : value,
        ...(name === "surfaceFinish"
          ? {
              surfaceFinishDetail: {
                ...SURFACE_FINISH_DETAIL_DEFAULTS[value],
              },
            }
          : {}),
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 Surface finish 联动明细字段。
   * @param {Object} e 包含 name 和 value 的事件对象
   */
  const handleSurfaceFinishDetailChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        surfaceFinishDetail: {
          ...prevState.pcbSpec.surfaceFinishDetail,
          [name]: value,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 Advanced PCB 首屏基础字段，并按 HDI 类型同步材料和最低层数。
   * @param {Object} e 包含 name 和 value 的事件对象
   */
  const handleAdvancedPcbBaseChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => {
      const nextPcbSpec = {
        ...prevState.pcbSpec,
        [name]: value,
      };

      if (name === "advancedPcbType" && value === "hdi_buried_blind_vias") {
        nextPcbSpec.material = "HDI";
        nextPcbSpec.layers = Math.max(4, Number(nextPcbSpec.layers) || 4);
      }

      return {
        ...prevState,
        pcbSpec: nextPcbSpec,
      };
    });
    setQuoteDirty(true);
  };

  /**
   * 更新 PCB 预设厚度，并关闭自定义厚度输入。
   * @param {Object} e 包含厚度值的事件对象
   */
  const handleThicknessChange = (e) => {
    setShowCustomThickness(false);
    handlePcbSpecChange(e);
  };

  /**
   * 打开高级 PCB 其它板厚输入，保留当前预设值直到用户输入自定义值。
   */
  const handleOpenAdvancedCustomThickness = () => {
    setShowCustomThickness(true);
  };

  /**
   * 打开 1.7-8.0mm 自定义厚度输入，并修正超出范围的当前值。
   */
  const handleOpenCustomThickness = () => {
    const currentThickness = Number(formValues.pcbSpec.thickness);

    setShowCustomThickness(true);
    if (currentThickness < 1.7 || currentThickness > 8) {
      handlePcbSpecChange({
        target: { name: "thickness", value: 1.7 },
      });
    }
  };

  /**
   * 更新尺寸字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleSizeChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        size: {
          ...prevState.pcbSpec.size,
          [name]: Number(value),
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 打开英寸转毫米弹窗，并使用当前毫米尺寸作为英寸输入参考值。
   */
  const handleOpenInchConversion = () => {
    setInchSize({
      length: Number((Number(formValues.pcbSpec.size.length) / MM_PER_INCH).toFixed(4)),
      width: Number((Number(formValues.pcbSpec.size.width) / MM_PER_INCH).toFixed(4)),
    });
    setShowInchConversion(true);
  };

  /**
   * 更新英寸转换弹窗中的长宽输入值。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleInchSizeChange = (e) => {
    const { name, value } = e.target;
    setInchSize((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /**
   * 将弹窗中的英寸尺寸转换为毫米并写回报价表单。
   */
  const handleInchConversionSubmit = () => {
    const length = Number(inchSize.length);
    const width = Number(inchSize.width);

    if (
      !Number.isFinite(length) ||
      !Number.isFinite(width) ||
      length <= 0 ||
      width <= 0
    ) {
      toast.error("Please enter valid inch dimensions");
      return;
    }

    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        size: {
          ...prevState.pcbSpec.size,
          length: convertInchesToMillimeters(length),
          width: convertInchesToMillimeters(width),
          unit: "mm",
        },
      },
    }));
    setQuoteDirty(true);
    setShowInchConversion(false);
  };

  /**
   * 打开金手指 Au/Ni 厚度选择弹窗，并从当前表单值恢复草稿。
   */
  const handleOpenGoldThicknessModal = () => {
    const currentThickness =
      formValues.pcbSpec.edgeConnectorGoldNickelThickness ||
      formValues.pcbSpec.edgeConnectorCustomGoldThickness;
    const matchedValues = String(currentThickness).match(
      /Au:([0-9]+(?:\.[0-9]+)?)U"\/Ni:([0-9]+)U"/i
    );
    const au = matchedValues?.[1] || "10";
    const ni = matchedValues?.[2] || "120";
    const presetValue = `${au},${ni}`;
    const hasPreset = EDGE_CONNECTOR_GOLD_THICKNESS_OPTIONS.some(
      (option) => option.value === presetValue
    );

    setGoldThicknessDraft({
      selection: hasPreset ? presetValue : "custom",
      au,
      ni,
    });
    setShowGoldThicknessModal(true);
  };

  /**
   * 选择弹窗中的预设 Au/Ni 厚度组合。
   * @param {string} value 逗号分隔的 Au、Ni 厚度值
   */
  const handleGoldThicknessPresetChange = (value) => {
    const [au, ni] = value.split(",");
    setGoldThicknessDraft({ selection: value, au, ni });
  };

  /**
   * 更新弹窗中的自定义 Au 或 Ni 厚度。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleGoldThicknessInputChange = (e) => {
    const { name, value } = e.target;
    setGoldThicknessDraft((prevState) => ({
      ...prevState,
      selection: "custom",
      [name]: value,
    }));
  };

  /**
   * 校验并提交 Au/Ni 厚度，将目标网站的选择结果写回现有报价字段。
   */
  const handleGoldThicknessSubmit = () => {
    const au = String(goldThicknessDraft.au).trim();
    const ni = String(goldThicknessDraft.ni).trim();

    if (!/^[0-9]+(?:\.[0-9]+)?$/.test(au)) {
      toast.error("Please enter a valid Au thickness");
      return;
    }

    if (!/^[1-9][0-9]*$/.test(ni)) {
      toast.error("Please enter a valid Ni thickness");
      return;
    }

    const formattedThickness = `Au:${au}U"/Ni:${ni}U"`;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        edgeConnectorGoldThickness:
          au === "10" && ni === "120" ? "standard" : "custom",
        edgeConnectorCustomGoldThickness: formattedThickness,
        edgeConnectorGoldNickelThickness: formattedThickness,
      },
    }));
    setQuoteDirty(true);
    setShowGoldThicknessModal(false);
  };

  /**
   * 更新高级选项中的复选框、下拉框和文本字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} e 输入事件
   */
  const handleAdvancedOptionChange = (e) => {
    const { name, checked, type, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        advancedOptions: {
          ...prevState.pcbSpec.advancedOptions,
          [name]: type === "checkbox" ? checked : value,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新最终检查报告选项，默认报告保持免费勾选。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleInspectionReportChange = (e) => {
    const { value, checked, disabled } = e.target;

    if (disabled) {
      return;
    }

    setFormValues((prevState) => {
      const currentReports = prevState.pcbSpec.advancedOptions.inspectionReports || [];
      const nextReports = checked
        ? Array.from(new Set([...currentReports, value]))
        : currentReports.filter((item) => item !== value);

      return {
        ...prevState,
        pcbSpec: {
          ...prevState.pcbSpec,
          advancedOptions: {
            ...prevState.pcbSpec.advancedOptions,
            inspectionReports: nextReports.length ? nextReports : ["default"],
          },
        },
      };
    });
    setQuoteDirty(true);
  };

  /**
   * 更新 PCB 快捷服务和合规确认开关。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handlePcbToggleChange = (e) => {
    const { name, checked } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        [name]: checked,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新随 PCB 一起下单的钢网参数。
   * @param {Object} e 包含 name、value、checked 和 type 的输入事件
   */
  const handleStencilSpecChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        stencilSpec: {
          ...prevState.pcbSpec.stencilSpec,
          [name]:
            type === "checkbox"
              ? checked
              : type === "number"
              ? Math.max(1, Number(value))
              : value,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 切换钢网类型，并同步目标站对应的默认尺寸和阶梯钢网状态。
   * @param {string} stencilType 钢网类型
   */
  const handleStencilTypeChange = (stencilType) => {
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        stencilSpec: {
          ...prevState.pcbSpec.stencilSpec,
          stencilType,
          size: stencilType === "non_framework" ? "custom" : "370x470",
          multiLevel: false,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新随 PCB 一起下单的组装服务参数。
   * @param {Object} e 包含 name、value、checked 和 type 的输入事件
   */
  const handleAssemblySpecChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        assemblySpec: {
          ...prevState.pcbSpec.assemblySpec,
          [name]:
            type === "checkbox"
              ? checked
              : type === "number"
              ? value === ""
                ? ""
                : name === "quantity"
                ? Math.max(1, Number(value))
                : Math.max(0, Number(value))
              : value,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新通用品类规格字段。
   * @param {Object} e 包含 name, value, checked, type 的对象
   */
  const handleGenericSpecChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? Number(value)
            : value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新独立 SMD-Stencil 报价规格字段。
   * @param {Object} e 包含 name、value、checked 和 type 的输入事件
   */
  const handleStandaloneStencilSpecChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? Math.max(1, Number(value))
            : value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 切换独立 SMD-Stencil 类型，并同步目标站对应默认尺寸。
   * @param {string} stencilType 钢网类型
   */
  const handleStandaloneStencilTypeChange = (stencilType) => {
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        stencilType,
        size: stencilType === "non_framework" ? "custom" : "370x470",
        multiLevel: false,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新独立 Assembly 报价规格字段。
   * @param {Object} e 包含 name、value、checked 和 type 的输入事件
   */
  const handleStandaloneAssemblySpecChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? value === ""
              ? ""
              : name === "quantity"
              ? Math.max(1, Number(value))
              : Math.max(0, Number(value))
            : value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新通用品类尺寸字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleGenericSizeChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        size: {
          ...prevState.spec.size,
          [name]: Number(value),
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 CNC/3D 尺寸单位，单位属于 size 对象但不是数字。
   * @param {string} unit 设计单位
   */
  const handleCnc3dUnitChange = (unit) => {
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        size: {
          ...prevState.spec.size,
          unit,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 切换 CNC/3D 制造服务类型。
   * @param {string} serviceType 制造服务类型
   */
  const handleCnc3dServiceChange = (serviceType) => {
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        serviceType,
        material:
          serviceType === "sheet_metal"
            ? (SHEET_METAL_MATERIAL_OPTIONS.includes(prevState.spec.material) ? prevState.spec.material : "Aluminum")
            : serviceType === "injection_molding"
            ? (getInjectionMaterialOptions(prevState.spec).includes(prevState.spec.material) ? prevState.spec.material : "ABS")
            : serviceType === "3d_printing"
            ? (prevState.spec.serviceType === "3d_printing" && THREE_D_PRINTING_MATERIAL_OPTIONS.includes(prevState.spec.material) ? prevState.spec.material : "Resin")
            : serviceType === "cnc" && !CNC_3D_MATERIAL_OPTIONS.includes(prevState.spec.material)
            ? "Aluminum"
            : prevState.spec.material,
        materialGrade:
          serviceType === "sheet_metal" &&
          (prevState.spec.material === "Aluminum" || !SHEET_METAL_MATERIAL_OPTIONS.includes(prevState.spec.material))
            ? "Aluminum 5052"
            : serviceType === "injection_molding" &&
              (prevState.spec.material === "ABS" || !INJECTION_MOLDING_MATERIAL_OPTIONS.includes(prevState.spec.material))
            ? "ABS"
            : serviceType === "3d_printing" &&
              (prevState.spec.serviceType !== "3d_printing" || prevState.spec.material === "Resin" || !THREE_D_PRINTING_MATERIAL_OPTIONS.includes(prevState.spec.material))
            ? "Standard white material (UTR 8360)"
            : serviceType === "cnc" && prevState.spec.material === "Aluminum"
            ? prevState.spec.materialGrade || "Aluminum 6061"
            : serviceType === "sheet_metal"
            ? ""
            : prevState.spec.materialGrade,
        color:
          serviceType === "sheet_metal"
            ? "Silver white"
            : serviceType === "injection_molding"
            ? getInjectionColorOptions(prevState.spec)[0]?.value || "Black"
            : serviceType === "3d_printing"
            ? (prevState.spec.serviceType === "3d_printing" ? get3dPrintingColorOptions(prevState.spec.material)[0]?.value : "Matte white") || "Matte white"
            : serviceType === "cnc" && getCncColorOptions(prevState.spec.material).length
            ? prevState.spec.color || getCncColorOptions(prevState.spec.material)[0].value
            : prevState.spec.color,
        process:
          serviceType === "3d_printing"
            ? prevState.spec.process || "SLA"
            : prevState.spec.process,
        thicknessMode:
          serviceType === "sheet_metal"
            ? prevState.spec.thicknessMode || "subject_to_3d_file"
            : prevState.spec.thicknessMode,
        injectionMoldingType:
          serviceType === "injection_molding"
            ? prevState.spec.injectionMoldingType || "steel_mold"
            : prevState.spec.injectionMoldingType,
        annualVolume:
          serviceType === "injection_molding"
            ? prevState.spec.annualVolume || "≤10,000pcs"
            : prevState.spec.annualVolume,
        materialAdditive:
          serviceType === "injection_molding"
            ? prevState.spec.materialAdditive || "None"
            : prevState.spec.materialAdditive,
        spiFinish:
          serviceType === "injection_molding"
            ? prevState.spec.spiFinish || "SPI B-3"
            : prevState.spec.spiFinish,
        toolRequirement:
          serviceType === "injection_molding"
            ? prevState.spec.toolRequirement || "order_new_mold"
            : prevState.spec.toolRequirement,
        moldDisposition:
          serviceType === "injection_molding"
            ? prevState.spec.moldDisposition || "keep_in_factory"
            : prevState.spec.moldDisposition,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 切换 Injection molding 子选项，并同步 Vacuum casting 的默认字段。
   * @param {string} injectionMoldingType Injection molding 子选项
   */
  const handleInjectionMoldingTypeChange = (injectionMoldingType) => {
    const isVacuumCasting = injectionMoldingType === "silicone_mold";

    setFormValues((prevState) => {
      const nextMaterialOptions = isVacuumCasting
        ? VACUUM_CASTING_MATERIAL_OPTIONS
        : INJECTION_MOLDING_MATERIAL_OPTIONS;
      const nextColorOptions = isVacuumCasting
        ? VACUUM_CASTING_COLOR_OPTIONS
        : INJECTION_MOLDING_COLOR_OPTIONS;
      const nextSpec = {
        ...prevState.spec,
        injectionMoldingType,
        material: !nextMaterialOptions.includes(prevState.spec.material)
          ? "ABS"
          : prevState.spec.material,
        materialGrade: !nextMaterialOptions.includes(prevState.spec.material) || prevState.spec.material === "ABS"
          ? "ABS"
          : prevState.spec.materialGrade,
        color: !nextColorOptions.some((option) => option.value === prevState.spec.color)
          ? "Black"
          : prevState.spec.color,
        surfaceFinish: isVacuumCasting && !VACUUM_CASTING_SURFACE_FINISH_OPTIONS.includes(prevState.spec.surfaceFinish)
          ? ""
          : prevState.spec.surfaceFinish,
      };

      return {
        ...prevState,
        spec: nextSpec,
      };
    });
    setQuoteDirty(true);
  };

  /**
   * 更新 CNC/3D 材料，并同步 CNC machining 与 Sheet metal 的 Aluminum 默认子选项。
   * @param {string} material 材料名称
   */
  const handleCnc3dMaterialChange = (material) => {
    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        material,
        materialGrade:
          prevState.spec.serviceType === "sheet_metal" && material === "Aluminum"
            ? "Aluminum 5052"
            : prevState.spec.serviceType === "injection_molding" && material === "ABS"
            ? "ABS"
            : prevState.spec.serviceType === "3d_printing" && material === "Resin"
            ? "Standard white material (UTR 8360)"
            : prevState.spec.serviceType === "cnc" && material === "Aluminum"
            ? prevState.spec.materialGrade || "Aluminum 6061"
            : "",
        color:
          prevState.spec.serviceType === "sheet_metal"
            ? "Silver white"
          : prevState.spec.serviceType === "injection_molding"
            ? getInjectionColorOptions(prevState.spec)[0]?.value || "Black"
            : prevState.spec.serviceType === "3d_printing" && get3dPrintingColorOptions(material).length
            ? get3dPrintingColorOptions(material)[0].value
            : prevState.spec.serviceType === "cnc" && getCncColorOptions(material).length
            ? getCncColorOptions(material)[0].value
            : "",
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 记录 CNC 技术图纸文件名，实际文件仍以用户上传附件为准。
   * @param {React.ChangeEvent<HTMLInputElement>} e 文件选择事件
   */
  const handleCncTechnicalDrawingChange = (e) => {
    const file = e.target.files?.[0];

    setFormValues((prevState) => ({
      ...prevState,
      spec: {
        ...prevState.spec,
        technicalDrawingFileName: file?.name || "",
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 CNC Part Marking 复选项。
   * @param {string} value 复选项值
   * @param {boolean} checked 是否选中
   */
  const handleCncPartMarkingChange = (value, checked) => {
    setFormValues((prevState) => {
      const currentValues = prevState.spec.partMarking || [];
      const nextValues = checked
        ? Array.from(new Set([...currentValues, value]))
        : currentValues.filter((item) => item !== value);

      return {
        ...prevState,
        spec: {
          ...prevState.spec,
          partMarking: nextValues,
        },
      };
    });
    setQuoteDirty(true);
  };

  /**
   * 校验并添加报价附件。
   * @param {React.ChangeEvent<HTMLInputElement>} e 文件选择事件
   * @param {string} category 默认附件用途
   */
  const handleAttachmentChange = (e, category) => {
    const selectedFiles = Array.from(e.target.files || []);
    const requirement = getAttachmentRequirement(formValues.quoteType);
    const fallbackCategory = category || requirement.defaultCategory || "other";

    if (!selectedFiles.length) {
      return;
    }

    const validFiles = [];
    selectedFiles.forEach((file) => {
      if (file.size > MAX_QUOTE_ATTACHMENT_SIZE) {
        toast.error(`${file.name} exceeds 200MB.`);
        return;
      }

      validFiles.push(file);
    });

    setAttachments((prevItems) => {
      const existingKeys = new Set(
        prevItems.map((item) => `${item.file.name}-${item.file.size}-${item.file.lastModified}`)
      );
      const nextItems = [...prevItems];

      validFiles.forEach((file) => {
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        if (existingKeys.has(fileKey)) {
          return;
        }

        if (nextItems.length >= MAX_QUOTE_ATTACHMENT_COUNT) {
          toast.error(`You can upload up to ${MAX_QUOTE_ATTACHMENT_COUNT} files.`);
          return;
        }

        nextItems.push(buildAttachmentItem(file, fallbackCategory));
        existingKeys.add(fileKey);
      });

      return nextItems;
    });

    setIsParsing(false);
    setQuoteDirty(true);
    e.target.value = "";
  };

  /**
   * 更新附件用途。
   * @param {string} attachmentId 附件状态 ID
   * @param {string} category 附件用途
   */
  const handleAttachmentCategoryChange = (attachmentId, category) => {
    setAttachments((prevItems) =>
      prevItems.map((item) =>
        item.id === attachmentId ? { ...item, category } : item
      )
    );
    setQuoteDirty(true);
  };

  /**
   * 移除一个待上传附件。
   * @param {string} attachmentId 附件状态 ID
   */
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments((prevItems) =>
      prevItems.filter((item) => item.id !== attachmentId)
    );
    setQuoteDirty(true);
  };

  /**
   * 校验 Injection molding 的必选按钮组和首批数量。
   * @returns {boolean} 是否通过 Injection molding 参数校验
   */
  const validateInjectionMoldingSpec = () => {
    const isVacuumCasting = isVacuumCastingSpec(formValues.spec);
    const quantityValue = isVacuumCasting
      ? formValues.spec.quantity
      : formValues.spec.firstBatchQuantity;

    if (Number(quantityValue) <= 0) {
      toast.error(`${isVacuumCasting ? "Quantity" : "First batch quantity"} must be greater than 0.`);
      return false;
    }

    const requiredSelectedFields = [
      { name: "injectionMoldingType", label: "Injection molding type" },
      { name: "material", label: "Material" },
      { name: "color", label: "Color" },
      { name: "partsAssembly", label: "Parts assembly" },
      { name: "finishedAppearance", label: "Finished appearance" },
      { name: "inspection", label: "Inspection" },
      { name: "productDescription", label: "Product Desc" },
    ];

    if (isVacuumCasting) {
      requiredSelectedFields.push({ name: "surfaceFinish", label: "Surface finish" });
    } else {
      requiredSelectedFields.push(
        { name: "annualVolume", label: "Expected annual volumes" },
        { name: "materialAdditive", label: "Material Additives" },
        { name: "spiFinish", label: "SPI Finish" },
        { name: "toolRequirement", label: "Tool Requirements" },
        { name: "moldDisposition", label: "mold handling" }
      );
    }

    if (!String(formValues.spec.size?.unit || "").trim()) {
      toast.error("Please select at least one Design Units option.");
      return false;
    }

    if (
      formValues.spec.material === "ABS" &&
      !String(formValues.spec.materialGrade || "").trim()
    ) {
      toast.error("Please select at least one Type of ABS option.");
      return false;
    }

    const emptyField = requiredSelectedFields.find(
      (field) => !String(formValues.spec[field.name] || "").trim()
    );

    if (emptyField) {
      toast.error(`Please select at least one ${emptyField.label} option.`);
      return false;
    }

    return true;
  };

  /**
   * 校验最基础的报价参数。
   * @param {boolean} checkEmail 是否校验邮箱（计算报价时不需要，保存/下单时需要）
   * @returns {boolean} 是否通过校验
   */
  const validateQuoteForm = (checkEmail = false) => {
    if (checkEmail && !user && !formValues.customerInfo.email.trim()) {
      toast.error("Please enter your email to save or submit this quote.");
      setShowCustomerInfo(true);
      return false;
    }

    if (!formValues.shipping.countryCode.trim()) {
      toast.error("Please enter shipping country code.");
      return false;
    }

    if (
      hasPcbSpecifications &&
      (Number(formValues.pcbSpec.size.length) <= 0 ||
        Number(formValues.pcbSpec.size.width) <= 0)
    ) {
      toast.error("PCB size must be greater than 0.");
      return false;
    }

    if (hasPcbSpecifications && Number(formValues.pcbSpec.quantity) <= 0) {
      toast.error("PCB quantity must be greater than 0.");
      return false;
    }

    if (
      isSmdStencilQuote &&
      ["custom", "custom_large"].includes(formValues.spec.size)
    ) {
      const maxWidth = formValues.spec.size === "custom_large" ? 550 : 190;
      const maxHeight = formValues.spec.size === "custom_large" ? 550 : 290;
      const customWidth = Number(formValues.spec.customWidth);
      const customHeight = Number(formValues.spec.customHeight);

      if (
        customWidth <= 0 ||
        customHeight <= 0 ||
        customWidth > maxWidth ||
        customHeight > maxHeight
      ) {
        toast.error(`Custom stencil size must be within ${maxWidth}×${maxHeight}mm.`);
        return false;
      }
    }

    if (
      formValues.pcbSpec.orderWithStencil &&
      ["custom", "custom_large"].includes(formValues.pcbSpec.stencilSpec.size)
    ) {
      const maxWidth = formValues.pcbSpec.stencilSpec.size === "custom_large" ? 550 : 190;
      const maxHeight = formValues.pcbSpec.stencilSpec.size === "custom_large" ? 550 : 290;
      const customWidth = Number(formValues.pcbSpec.stencilSpec.customWidth);
      const customHeight = Number(formValues.pcbSpec.stencilSpec.customHeight);

      if (
        customWidth <= 0 ||
        customHeight <= 0 ||
        customWidth > maxWidth ||
        customHeight > maxHeight
      ) {
        toast.error(`Custom stencil size must be within ${maxWidth}×${maxHeight}mm.`);
        return false;
      }
    }

    if (
      isCnc3dQuote &&
      formValues.spec.serviceType === "injection_molding" &&
      !validateInjectionMoldingSpec()
    ) {
      return false;
    }

    if (isCnc3dQuote && Number(formValues.spec.quantity) <= 0) {
      toast.error("Quantity must be greater than 0.");
      return false;
    }

    return true;
  };

  /**
   * 生成报价请求负载。
   * @returns {Object} 标准报价请求
   */
  const buildQuotePayload = () => {
    const normalizedShipping = {
      ...formValues.shipping,
      countryCode: String(formValues.shipping.countryCode || "").toUpperCase(),
    };
    const pricingPreference = {
      deliveryOptionId: selectedDeliveryOptionId || "standard",
    };

    if (isAdvancedPcb) {
      return {
        quoteType: formValues.quoteType,
        customerInfo: formValues.customerInfo,
        shipping: normalizedShipping,
        pricingPreference,
        spec: {
          ...formValues.spec,
          boardType: formValues.pcbSpec.boardType,
          pcbType: formValues.pcbSpec.advancedPcbType,
          boardSpec: formValues.pcbSpec.advancedBoardSpec,
          size: formValues.pcbSpec.size,
          quantity: formValues.pcbSpec.quantity,
          layers: formValues.pcbSpec.layers,
          material: formValues.pcbSpec.material,
          thickness: formValues.pcbSpec.thickness,
          surfaceFinish: formValues.pcbSpec.surfaceFinish,
          surfaceFinishDetail: formValues.pcbSpec.surfaceFinishDetail,
          finishedCopper: formValues.pcbSpec.finishedCopper,
          impedanceControl:
            formValues.pcbSpec.advancedOptions.impedanceControl,
          customStackup: formValues.pcbSpec.advancedOptions.customStackup,
          viaInPad: formValues.pcbSpec.advancedOptions.viaInPad,
          resinFilledVias: formValues.pcbSpec.advancedOptions.resinFilledVias,
          edgePlating: formValues.pcbSpec.advancedOptions.edgePlating,
          goldFingers:
            formValues.pcbSpec.edgeConnector === "yes" ||
            formValues.pcbSpec.advancedOptions.edgeConnector,
          blindBuriedVias:
            formValues.pcbSpec.advancedPcbType === "hdi_buried_blind_vias",
          eTest: true,
          orderWithStencil: formValues.pcbSpec.orderWithStencil,
          stencilSpec: formValues.pcbSpec.orderWithStencil
            ? formValues.pcbSpec.stencilSpec
            : undefined,
          orderWithAssembly: formValues.pcbSpec.orderWithAssembly,
          assemblySpec: formValues.pcbSpec.orderWithAssembly
            ? formValues.pcbSpec.assemblySpec
            : undefined,
          specialRequest: formValues.pcbSpec.specialRequest,
        },
        sessionId,
      };
    }

    if (isFpcRigidFlex) {
      return {
        quoteType: formValues.quoteType,
        customerInfo: formValues.customerInfo,
        shipping: normalizedShipping,
        pricingPreference,
        spec: {
          ...DEFAULT_SPEC_BY_CATEGORY.fpc_rigid_flex,
          ...formValues.pcbSpec,
          pcbType: formValues.pcbSpec.flexPcbType,
          specialRequest: formValues.pcbSpec.specialRequest,
        },
        sessionId,
      };
    }

    if (isAssemblyQuote) {
      return {
        quoteType: formValues.quoteType,
        customerInfo: formValues.customerInfo,
        shipping: normalizedShipping,
        pricingPreference,
        spec: {
          ...formValues.spec,
          pcbSpec: formValues.pcbSpec,
        },
        sessionId,
      };
    }

    if (!isStandardPcb) {
      return {
        quoteType: formValues.quoteType,
        customerInfo: formValues.customerInfo,
        shipping: normalizedShipping,
        pricingPreference,
        spec: formValues.spec,
        sessionId,
      };
    }

    return {
      ...formValues,
      sessionId,
      shipping: normalizedShipping,
      pricingPreference,
    };
  };

  /**
   * 构建保存报价时使用的 FormData。
   * @returns {FormData} 表单数据
   */
  const buildQuoteFormData = () => {
    const nextFormData = new FormData();
    const attachmentMeta = attachments.map((item) => ({
      name: item.file.name,
      size: item.file.size,
      category: item.category,
    }));
    nextFormData.append(
      "payload",
      JSON.stringify({
        ...buildQuotePayload(),
        attachmentMeta,
      })
    );

    attachments.forEach((item) => {
      nextFormData.append("attachments", item.file);
    });

    return nextFormData;
  };

  /**
   * 在线计算报价结果。
   */
  const handleCalculate = async () => {
    if (!validateQuoteForm(false)) {
      return;
    }

    try {
      const response = await calculateInstantQuote(buildQuotePayload()).unwrap();
      const nextQuote = response?.quote || null;
      const nextDeliveryOptionId =
        nextQuote?.pricing?.selectedDeliveryOption?.id || selectedDeliveryOptionId;

      setQuoteResult(nextQuote);
      setSelectedDeliveryOptionId(nextDeliveryOptionId);
      toast.success("Quote calculated successfully");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to calculate quote");
    }
  };

  /**
   * 切换当前报价的交期价格选项。
   * @param {Object} option 交期价格选项
   */
  const handleDeliveryOptionChange = (option) => {
    if (!quoteResult?.pricing || !option?.id) {
      return;
    }

    const currentPricing = quoteResult.pricing;
    const shippingCost = Number(currentPricing.shippingCost || 0);
    const discountAmount = Number(currentPricing.discountAmount || 0);
    const nextTotalAmount = Number(
      (Number(option.manufacturingCost || 0) + shippingCost - discountAmount).toFixed(2)
    );
    const nextDetails = (currentPricing.details || []).filter(
      (item) => !String(item.label || "").includes("Production")
    );

    if (Number(option.surcharge || 0) !== 0) {
      nextDetails.push({
        label:
          Number(option.surcharge) > 0
            ? `${option.label} Production`
            : `${option.label} Production Adjustment`,
        amount: Number(option.surcharge),
      });
    }

    setSelectedDeliveryOptionId(option.id);
    setQuoteDirty(true);
    setSavedQuote(null);
    setQuoteResult({
      ...quoteResult,
      pricing: {
        ...currentPricing,
        pcbCost: Number(option.manufacturingCost || 0),
        deliverySurcharge: Number(option.surcharge || 0),
        selectedDeliveryOption: option,
        totalAmount: nextTotalAmount,
        leadTimeDays: option.leadTimeDays,
        details: nextDetails,
      },
      quoteSnapshot: {
        ...(quoteResult.quoteSnapshot || {}),
        totalAmount: nextTotalAmount,
        leadTimeDays: option.leadTimeDays,
        selectedDeliveryOption: option,
      },
    });
  };

  /**
   * 保存或更新报价单。
   * @returns {Promise<Object|null>} 已保存的报价单
   */
  const handleSaveQuote = async () => {
    if (!validateQuoteForm(true)) {
      return null;
    }

    try {
      const formData = buildQuoteFormData();
      const response = savedQuote?._id
        ? await updateQuote({ id: savedQuote._id, formData }).unwrap()
        : await createQuote(formData).unwrap();
      const nextQuote = response?.quote || null;

      setSavedQuote(nextQuote);
      setQuoteResult(nextQuote);
      setQuoteDirty(false);
      toast.success(savedQuote?._id ? "Quote updated successfully" : "Quote saved successfully");
      return nextQuote;
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save quote");
      return null;
    }
  };

  /**
   * 确保当前页面存在最新的已保存报价单。
   * @returns {Promise<Object|null>} 最新报价单
   */
  const ensureSavedQuote = async () => {
    if (!savedQuote?._id || isQuoteDirty) {
      return handleSaveQuote();
    }

    return savedQuote;
  };

  /**
   * 将报价单加入购物车，并跳转到购物车页面。
   */
  const handleAddToCart = async () => {
    const currentQuote = await ensureSavedQuote();
    if (!currentQuote?._id) {
      return;
    }

    const quoteManualReviewRequired =
      (currentQuote?.reviewFlags || []).includes("MANUAL_REVIEW_REQUIRED");
    if (quoteManualReviewRequired) {
      toast.error("This quote requires manual review. Please submit RFQ.");
      return;
    }

    const quoteShippingPending =
      currentQuote?.pricing?.shippingMethod === "Pending Manual Review" ||
      (currentQuote?.reviewFlags || []).includes("SHIPPING_PENDING_CONFIRMATION");
    if (quoteShippingPending) {
      toast.error("Shipping needs manual confirmation. Please submit RFQ.");
      return;
    }

    try {
      const response = await addQuoteToCart({
        id: currentQuote._id,
        sessionId,
      }).unwrap();
      dispatch(setCartItem(response?.cartItem));
      toast.success("Quote added to cart");
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add quote to cart");
    }
  };

  /**
   * 提交报价单到人工跟进流程。
   */
  const handleSubmitQuote = async () => {
    const currentQuote = await ensureSavedQuote();
    if (!currentQuote?._id) {
      return;
    }

    try {
      const response = await submitQuote({
        id: currentQuote._id,
        message: isPcbQuoteForm
          ? formValues.pcbSpec.specialRequest
          : formValues.spec.specialRequest,
        sessionId,
      }).unwrap();
      setSavedQuote(response?.quote || currentQuote);
      toast.success("Quote submitted for manual review");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to submit quote");
    }
  };

  /**
   * 渲染 CNC/3D 制造类型卡片。
   * @param {Object} option 制造类型配置
   * @returns {JSX.Element} 制造类型按钮
   */
  const renderCnc3dServiceCard = (option) => {
    const isSelected = formValues.spec.serviceType === option.value;

    return (
      <button
        key={option.value}
        type="button"
        onClick={() => handleCnc3dServiceChange(option.value)}
        disabled={isBusy || isConfigLoading}
        className={`quote-cnc-service-card ${isSelected ? "is-active" : ""}`}
      >
        <span className="quote-cnc-service-copy">
          <strong>{option.title}</strong>
          <span>{option.description}</span>
        </span>
      </button>
    );
  };

  /**
   * 渲染 Injection molding 的模具类型子选项。
   * @param {Object} option 模具类型配置
   * @returns {JSX.Element} 模具类型按钮
   */
  const renderInjectionMoldingTypeCard = (option) => {
    const isSelected = formValues.spec.injectionMoldingType === option.value;

    return (
      <button
        key={option.value}
        type="button"
        onClick={() => handleInjectionMoldingTypeChange(option.value)}
        disabled={isBusy || isConfigLoading}
        className={`quote-injection-type-card ${isSelected ? "is-active" : ""}`}
      >
        <span>
          <strong>{option.title}</strong>
          <em>({option.description})</em>
        </span>
      </button>
    );
  };

  /**
   * 渲染 CNC/3D RFQ 摘要卡片。
   * @returns {JSX.Element} RFQ 摘要卡片
   */
  const renderCnc3dRfqCard = () => (
    <div className="quote-cnc-rfq-card">
      <div className="quote-cnc-rfq-head">
        <strong>Subtotal <span>({attachments.length} part)</span></strong>
        <b>RFQ</b>
      </div>
      <p>VAT and freight excluded</p>
      <div className="quote-cnc-lead-time">
        <span>Lead Time</span>
        <strong>3-5 Business Days</strong>
        <small>Estimated shipment after file review</small>
      </div>
      <button type="button" disabled={isBusy || isConfigLoading}>
        <FiUpload className="h-5 w-5" aria-hidden="true" />
        Add Another Part
      </button>
    </div>
  );

  /**
   * 渲染 CNC 单选项。
   * @param {Object} props 单选项配置
   * @param {string} props.name 字段名
   * @param {string|boolean} props.value 选项值
   * @param {string|boolean} props.currentValue 当前值
   * @param {string} props.label 选项标签
   * @returns {JSX.Element} 单选项节点
   */
  const renderCncRadioOption = ({ name, value, currentValue, label }) => (
    <button
      type="button"
      onClick={() => handleGenericSpecChange({ target: { name, value } })}
      disabled={isBusy || isConfigLoading}
      className={`quote-cnc-radio-option ${currentValue === value ? "is-active" : ""}`}
    >
      <span aria-hidden="true" />
      {label}
    </button>
  );

  /**
   * 渲染 Sheet metal 专属制造补充选项。
   * @returns {JSX.Element} Sheet metal 制造补充选项节点
   */
  const renderSheetMetalManufacturingOptions = () => (
    <>
      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Threads and<br />Tapped Holes:</span>
        <div className="quote-cnc-process-copy">
          <strong>Do your parts need to tap threads?</strong>
          <p>Please specify if your part has any internal or external threads. Creatyea will not bear any assembly risk if it is a non-standard thread, unless all assembly parts are produced and assembled here. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {renderCncRadioOption({
              name: "hasThreads",
              value: false,
              currentValue: Boolean(formValues.spec.hasThreads),
              label: "No",
            })}
            {renderCncRadioOption({
              name: "hasThreads",
              value: true,
              currentValue: Boolean(formValues.spec.hasThreads),
              label: "Yes",
            })}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Inserts:</span>
        <div className="quote-cnc-process-copy">
          <p>Please specify the standard inserts in your part. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {renderCncRadioOption({
              name: "hasInserts",
              value: false,
              currentValue: Boolean(formValues.spec.hasInserts),
              label: "No",
            })}
            {renderCncRadioOption({
              name: "hasInserts",
              value: true,
              currentValue: Boolean(formValues.spec.hasInserts),
              label: "Yes",
            })}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Tolerance:</span>
        <div className="quote-cnc-process-copy">
          <p>Tolerances in sheet metal will be controlled with <strong><u>ISO 2768-1</u></strong> Coarse class (C). For other tighter tolerances, technical drawing will be required to indicate critical dimensions.</p>
          <div className="quote-cnc-radio-stack">
            {renderCncRadioOption({
              name: "toleranceRequirement",
              value: "iso_2768_1",
              currentValue: formValues.spec.toleranceRequirement,
              label: "No tighter tolerances required (ISO 2768-1)",
            })}
            <div className="quote-cnc-radio-line">
              {renderCncRadioOption({
                name: "toleranceRequirement",
                value: "tighter",
                currentValue: formValues.spec.toleranceRequirement,
                label: "Tighter tolerances required",
              })}
              <button type="button" className="quote-cnc-select-link">Select</button>
            </div>
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Welding:</span>
        <div className="quote-cnc-process-copy">
          <div className="quote-cnc-radio-group">
            {renderCncRadioOption({
              name: "hasWelding",
              value: false,
              currentValue: Boolean(formValues.spec.hasWelding),
              label: "No",
            })}
            {renderCncRadioOption({
              name: "hasWelding",
              value: true,
              currentValue: Boolean(formValues.spec.hasWelding),
              label: "Yes",
            })}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-marking-field">
        <span>Part Marking:<HelpTooltip content="Choose markings that should be added to the manufactured part." label="Part Marking" /></span>
        <div className="quote-cnc-marking-options">
          {CNC_PART_MARKING_OPTIONS.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={(formValues.spec.partMarking || []).includes(option.value)}
                onChange={(e) => handleCncPartMarkingChange(option.value, e.target.checked)}
                disabled={isBusy || isConfigLoading}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Parts assembly:<HelpTooltip content="Specify whether this sheet metal order includes assembly requirements." label="Parts assembly" /></span>
        <div className="quote-cnc-process-copy">
          <p>Please specify the assembly requirements. Creatyea does not bear any assembly risk if you choose No assembly requirement. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {CNC_PARTS_ASSEMBLY_OPTIONS.map((option) => renderCncRadioOption({
              name: "partsAssembly",
              value: option.value,
              currentValue: formValues.spec.partsAssembly,
              label: option.label,
            }))}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span>Finished appearance:</span>
        <div className="quote-cnc-process-copy quote-cnc-appearance-options">
          <div>
            {renderCncRadioOption({
              name: "finishedAppearance",
              value: "standard",
              currentValue: formValues.spec.finishedAppearance,
              label: "Standard",
            })}
            <p>Normal processing traces or scratches caused during production, or subtle traces left after surface finish. <a href="#quote-gerber-upload">e.g.</a></p>
          </div>
          <div>
            {renderCncRadioOption({
              name: "finishedAppearance",
              value: "premium",
              currentValue: formValues.spec.finishedAppearance,
              label: "Premium (Extra charges)",
            })}
            <p>Higher appearance requirements, the surface is clean and smooth without obvious flaws. (Except for the inherent anodizing rack marks). Photos will be sent to confirm before shipment. <a href="#quote-gerber-upload">e.g.</a></p>
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span>Inspection:</span>
        <div className="quote-cnc-process-copy">
          <p>The inspection report will not be sent out with your parts unless you need it.</p>
          <div className="quote-cnc-radio-stack quote-cnc-inspection-options">
            {CNC_INSPECTION_OPTIONS.map((option) => (
              <div key={option.value}>
                {renderCncRadioOption({
                  name: "inspection",
                  value: option.value,
                  currentValue: formValues.spec.inspection,
                  label: option.label,
                })}
                <ul>
                  {option.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-product-desc-field">
        <span><em>*</em> Product Desc:<HelpTooltip content="Choose the product application category for engineering review." label="Product Desc" /></span>
        <select
          name="productDescription"
          value={formValues.spec.productDescription || ""}
          onChange={handleGenericSpecChange}
          disabled={isBusy || isConfigLoading}
        >
          <option value="">Select</option>
          {CNC_PRODUCT_DESC_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <label className="quote-cnc-field quote-cnc-other-request-field">
        <span>Other special<br />request:</span>
        <textarea
          name="specialRequest"
          rows="4"
          value={formValues.spec.specialRequest || ""}
          onChange={handleGenericSpecChange}
          disabled={isBusy || isConfigLoading}
          placeholder="Fill in any requirements about production, package, or shipment here."
        />
      </label>
    </>
  );

  /**
   * 渲染 Injection molding 专属模具与制造补充选项。
   * @param {Object} selectedMaterialDetail 当前材料展示信息
   * @param {string} selectedMaterialTitle 当前材料展示标题
   * @returns {JSX.Element} Injection molding 补充选项节点
   */
  const renderInjectionMoldingManufacturingOptions = (
    selectedMaterialDetail,
    selectedMaterialTitle
  ) => {
    const isVacuumCasting = isVacuumCastingSpec(formValues.spec);

    return (
    <>
      {!isVacuumCasting ? (
        <div className="quote-cnc-field quote-injection-tool-field">
          <span><em>*</em> Tool Requirements:</span>
          <div className="quote-injection-tool-content">
            {renderCncRadioOption({
              name: "toolRequirement",
              value: "order_new_mold",
              currentValue: formValues.spec.toolRequirement,
              label: "Order a new mold",
            })}
            <textarea
              name="moldRequirementDetails"
              rows="3"
              value={formValues.spec.moldRequirementDetails || ""}
              onChange={handleGenericSpecChange}
              disabled={isBusy || isConfigLoading}
              placeholder="Please provide the mold requirements, such as surface requirements, mold strength, machine size, cavity number and mold design requirements, etc."
            />
            <div className="quote-injection-mold-options">
              <div>
                {renderCncRadioOption({
                  name: "moldDisposition",
                  value: "keep_in_factory",
                  currentValue: formValues.spec.moldDisposition,
                  label: "Keep the molds in the factory",
                })}
                <HelpTooltip content="The factory keeps the mold for later production batches." label="Keep the molds in the factory" />
                <p>Note: Invoice accompanying the first batch of finished products will include all costs (including mold cost) which will be spread over the products cost.</p>
              </div>
              <div>
                {renderCncRadioOption({
                  name: "moldDisposition",
                  value: "ship_molds",
                  currentValue: formValues.spec.moldDisposition,
                  label: "I need the molds to be shipped",
                })}
                <p>Note: Higher mold cost</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="quote-cnc-material-review quote-injection-material-review">
        <div className="quote-cnc-selected-material">
          <span className="quote-cnc-review-title">Selected material</span>
          <div className="quote-cnc-material-card">
            <img src={selectedMaterialDetail.image} alt="" />
            <div>
              <strong>{selectedMaterialTitle}</strong>
              <button type="button">Show material description</button>
              <span className="quote-cnc-rating">★ {selectedMaterialDetail.rating} <em>({selectedMaterialDetail.reviews})</em></span>
            </div>
          </div>
        </div>
        <div className="quote-cnc-surface-finish">
          <div className="quote-cnc-surface-title">
            <span>Surface finish - {isVacuumCasting ? 2 : 0} options</span>
            <button type="button">⚠ Discrepancy warning</button>
          </div>
          <div className="quote-cnc-surface-select-row">
            <select
              name="surfaceFinish"
              value={formValues.spec.surfaceFinish || ""}
              onChange={handleGenericSpecChange}
              disabled={isBusy || isConfigLoading || !isVacuumCasting}
            >
              <option value=""></option>
              {isVacuumCasting
                ? VACUUM_CASTING_SURFACE_FINISH_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))
                : null}
            </select>
            <span aria-hidden="true">×</span>
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-technical-drawing-field">
        <span>Technical drawing:</span>
        <div className="quote-cnc-technical-drawing">
          <p>
            Please note that your 3D file and part specifications selected on this screen (eg. surface finish) will take priority over your technical drawing. Please ensure your technical drawing is up-to-date.
          </p>
          <label>
            <input
              type="file"
              onChange={handleCncTechnicalDrawingChange}
              disabled={isBusy || isConfigLoading}
            />
            Upload a technical drawing
          </label>
          {formValues.spec.technicalDrawingFileName ? (
            <small>{formValues.spec.technicalDrawingFileName}</small>
          ) : null}
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Threads and<br />Tapped Holes:</span>
        <div className="quote-cnc-process-copy">
          <strong>Do your parts need to tap threads?</strong>
          <p>Please specify if your part has any internal or external threads. Creatyea will not bear any assembly risk if it is a non-standard thread, unless all assembly parts are produced and assembled here. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {renderCncRadioOption({
              name: "hasThreads",
              value: false,
              currentValue: Boolean(formValues.spec.hasThreads),
              label: "No",
            })}
            {renderCncRadioOption({
              name: "hasThreads",
              value: true,
              currentValue: Boolean(formValues.spec.hasThreads),
              label: "Yes",
            })}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Inserts:</span>
        <div className="quote-cnc-process-copy">
          <p>Please specify the standard inserts in your part. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {renderCncRadioOption({
              name: "hasInserts",
              value: false,
              currentValue: Boolean(formValues.spec.hasInserts),
              label: "No",
            })}
            {renderCncRadioOption({
              name: "hasInserts",
              value: true,
              currentValue: Boolean(formValues.spec.hasInserts),
              label: "Yes",
            })}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Tolerance:</span>
        <div className="quote-cnc-process-copy">
          <p>Tolerances will be controlled with <strong><u>ISO 2768-1</u></strong>. For other tighter tolerances, technical drawing will be required to indicate critical dimensions.</p>
          <div className="quote-cnc-radio-stack">
            {renderCncRadioOption({
              name: "toleranceRequirement",
              value: "iso_2768_1",
              currentValue: formValues.spec.toleranceRequirement,
              label: "No tighter tolerances required (ISO 2768-1)",
            })}
            <div className="quote-cnc-radio-line">
              {renderCncRadioOption({
                name: "toleranceRequirement",
                value: "tighter",
                currentValue: formValues.spec.toleranceRequirement,
                label: "Tighter tolerances required",
              })}
              <button type="button" className="quote-cnc-select-link">Select</button>
            </div>
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-marking-field">
        <span>Part Marking:<HelpTooltip content="Choose markings that should be added to the manufactured part." label="Part Marking" /></span>
        <div className="quote-cnc-marking-options">
          {CNC_PART_MARKING_OPTIONS.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={(formValues.spec.partMarking || []).includes(option.value)}
                onChange={(e) => handleCncPartMarkingChange(option.value, e.target.checked)}
                disabled={isBusy || isConfigLoading}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span><em>*</em> Parts assembly:<HelpTooltip content="Specify whether this injection molding order includes assembly requirements." label="Parts assembly" /></span>
        <div className="quote-cnc-process-copy">
          <p>Please specify the assembly requirements. Creatyea does not bear any assembly risk if you choose No assembly requirement. <a href="#quote-gerber-upload">e.g.</a></p>
          <div className="quote-cnc-radio-group">
            {CNC_PARTS_ASSEMBLY_OPTIONS.map((option) => renderCncRadioOption({
              name: "partsAssembly",
              value: option.value,
              currentValue: formValues.spec.partsAssembly,
              label: option.label,
            }))}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span>Finished appearance:</span>
        <div className="quote-cnc-process-copy quote-cnc-appearance-options">
          <div>
            {renderCncRadioOption({
              name: "finishedAppearance",
              value: "standard",
              currentValue: formValues.spec.finishedAppearance,
              label: "Standard",
            })}
            <p>Normal processing traces or scratches caused during production, or subtle traces left after surface finish. <a href="#quote-gerber-upload">e.g.</a></p>
          </div>
          <div>
            {renderCncRadioOption({
              name: "finishedAppearance",
              value: "premium",
              currentValue: formValues.spec.finishedAppearance,
              label: "Premium (Extra charges)",
            })}
            <p>Higher appearance requirements, the surface is clean and smooth without obvious flaws. (Except for the inherent anodizing rack marks). Photos will be sent to confirm before shipment. <a href="#quote-gerber-upload">e.g.</a></p>
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-radio-field">
        <span>Inspection:</span>
        <div className="quote-cnc-process-copy">
          <p>The inspection report will not be sent out with your parts unless you need it.</p>
          <div className="quote-cnc-radio-stack quote-cnc-inspection-options">
            {INJECTION_INSPECTION_OPTIONS.map((option) => (
              <div key={option.value}>
                {renderCncRadioOption({
                  name: "inspection",
                  value: option.value,
                  currentValue: formValues.spec.inspection,
                  label: option.label,
                })}
                <ul>
                  {option.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="quote-cnc-field quote-cnc-product-desc-field">
        <span><em>*</em> Product Desc:<HelpTooltip content="Choose the product application category for engineering review." label="Product Desc" /></span>
        <select
          name="productDescription"
          value={formValues.spec.productDescription || ""}
          onChange={handleGenericSpecChange}
          disabled={isBusy || isConfigLoading}
        >
          <option value="">Select</option>
          {CNC_PRODUCT_DESC_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <label className="quote-cnc-field quote-cnc-other-request-field">
        <span>Other special<br />request:</span>
        <textarea
          name="specialRequest"
          rows="4"
          value={formValues.spec.specialRequest || ""}
          onChange={handleGenericSpecChange}
          disabled={isBusy || isConfigLoading}
          placeholder="Fill in any requirements about production, package, or shipment here."
        />
      </label>
    </>
    );
  };

  /**
   * 渲染 CNC/3D 内置报价页。
   * @returns {JSX.Element} CNC/3D 报价表单节点
   */
  const renderCnc3dSpec = () => (
    <section className="quote-section-card quote-cnc-page border border-gray-200 bg-white">
      {(() => {
        const isSheetMetalService = formValues.spec.serviceType === "sheet_metal";
        const is3dPrintingService = formValues.spec.serviceType === "3d_printing";
        const isInjectionMoldingService = formValues.spec.serviceType === "injection_molding";
        const isVacuumCasting = isInjectionMoldingService && isVacuumCastingSpec(formValues.spec);
        const materialOptions = isSheetMetalService
          ? SHEET_METAL_MATERIAL_OPTIONS
          : isInjectionMoldingService
          ? getInjectionMaterialOptions(formValues.spec)
          : is3dPrintingService
          ? THREE_D_PRINTING_MATERIAL_OPTIONS
          : CNC_3D_MATERIAL_OPTIONS;
        const aluminumTypeOptions = isSheetMetalService ? SHEET_METAL_ALUMINUM_TYPE_OPTIONS : CNC_ALUMINUM_TYPE_OPTIONS;
        const cncColorOptions = isSheetMetalService
          ? CNC_COLOR_OPTIONS
          : is3dPrintingService
          ? get3dPrintingColorOptions(formValues.spec.material)
          : getCncColorOptions(formValues.spec.material);
        const surfaceFinishOptions = isSheetMetalService
          ? SHEET_METAL_SURFACE_FINISH_OPTIONS
          : is3dPrintingService
          ? THREE_D_SURFACE_FINISH_OPTIONS
          : CNC_SURFACE_FINISH_OPTIONS;
        const surfaceFinishCount = isSheetMetalService ? 24 : is3dPrintingService ? 5 : 19;
        const selectedMaterialDetail =
          (isSheetMetalService
            ? SHEET_METAL_MATERIAL_DETAILS
            : isInjectionMoldingService
            ? INJECTION_MOLDING_MATERIAL_DETAILS
            : is3dPrintingService
            ? THREE_D_MATERIAL_DETAILS
            : CNC_MATERIAL_DETAILS)[formValues.spec.material] || {
            title: formValues.spec.material || "Custom material",
            image: "/img/imgnotavailable.png",
            rating: isInjectionMoldingService ? "5.0" : "4.9",
            reviews: isInjectionMoldingService ? "5 Reviews" : "126 Reviews",
          };
        const selectedMaterialTitle =
          is3dPrintingService && formValues.spec.material === "Resin" && formValues.spec.materialGrade
            ? formValues.spec.materialGrade
            : formValues.spec.material === "Aluminum" && formValues.spec.materialGrade
            ? formValues.spec.materialGrade
            : selectedMaterialDetail.title;

        return (
        <>
      <div className="quote-cnc-service-grid">
        {CNC_3D_SERVICE_OPTIONS.map(renderCnc3dServiceCard)}
      </div>

      {isInjectionMoldingService ? (
        <div className={`quote-injection-type-grid ${isVacuumCasting ? "is-vacuum-casting" : ""}`}>
          {INJECTION_MOLDING_TYPE_OPTIONS.map(renderInjectionMoldingTypeCard)}
        </div>
      ) : (
        <>
          <div className="quote-cnc-step-title">
            <span>1</span>
            <strong>Upload Design and configure parts to get a quote</strong>
          </div>

          <div id="quote-gerber-upload" className="quote-cnc-upload">
            <input
              type="file"
              multiple
              accept={attachmentRequirement.accept}
              onChange={(e) => handleAttachmentChange(e, attachmentRequirement.defaultCategory)}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            />
            <div className="quote-cnc-upload-icon">
              <FiUpload className="h-7 w-7" aria-hidden="true" />
            </div>
            <p className="quote-cnc-upload-main">Drag and drop here or select files</p>
            <p className="quote-cnc-upload-sub">
              File size &lt; <strong>200 MB</strong> (3D CAD: *.step, *.stp, *.x_t, *.iges, *.igs, *.sldprt)
            </p>
            <p className="quote-cnc-upload-note">
              You can add up to 24 rows of files. To ensure the efficiency of order review, do NOT upload compressed ZIP folder.
            </p>
            <p className="quote-cnc-upload-warning">
              Note: By default, recessed corners are made in round corners(fillets), sharp edges and burrs would be removed.
            </p>
          </div>

          <QuoteAttachmentList
            attachments={attachments}
            disabled={isBusy || isConfigLoading}
            onCategoryChange={handleAttachmentCategoryChange}
            onRemove={handleRemoveAttachment}
            compact
          />

          <p className="quote-cnc-compliance">
            Uploading files for weapons, military items, or other export-controlled items is strictly prohibited under our Terms of Service. Your files will be kept strictly confidential.
          </p>
        </>
      )}

      <div className="quote-cnc-form-grid">
        <div className={`quote-cnc-form-main ${isVacuumCasting ? "quote-vacuum-casting" : ""}`}>
          <label className="quote-cnc-field">
            <span>
              <em>*</em> {isInjectionMoldingService ? (
                isVacuumCasting
                  ? <>Quantity:<HelpTooltip content="Enter the quantity for Vacuum casting review." label="Quantity" /></>
                  : <>First batch quantity:<HelpTooltip content="Enter the first production batch quantity for injection molding review." label="First batch quantity" /></>
              ) : "Quantity:"}
            </span>
            <input
              type="number"
              min="1"
              name={isInjectionMoldingService && !isVacuumCasting ? "firstBatchQuantity" : "quantity"}
              value={isInjectionMoldingService && !isVacuumCasting ? formValues.spec.firstBatchQuantity || "" : formValues.spec.quantity || ""}
              onChange={handleGenericSpecChange}
              disabled={isBusy || isConfigLoading}
            />
          </label>

          {isInjectionMoldingService && !isVacuumCasting ? (
            <div className="quote-cnc-field quote-injection-annual-field">
              <span>Expected annual<br />volumes:</span>
              <div className="quote-cnc-options">
                {INJECTION_ANNUAL_VOLUME_OPTIONS.map((annualVolume) => (
                  <button
                    key={annualVolume}
                    type="button"
                    onClick={() => handleGenericSpecChange({
                      target: { name: "annualVolume", value: annualVolume },
                    })}
                    disabled={isBusy || isConfigLoading}
                    className={formValues.spec.annualVolume === annualVolume ? "is-active" : ""}
                  >
                    {annualVolume}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="quote-cnc-field">
            <span>Design Units:</span>
            <div className="quote-cnc-options">
              {CNC_3D_UNIT_OPTIONS.map((unit) => (
                <button
                  key={unit}
                  type="button"
                  onClick={() => handleCnc3dUnitChange(unit)}
                  disabled={isBusy || isConfigLoading}
                  className={formValues.spec.size?.unit === unit ? "is-active" : ""}
                >
                  {unit}
                </button>
              ))}
            </div>
          </div>

          <div className="quote-cnc-field quote-cnc-material-field">
            <span><em>*</em> Material:</span>
            <div className="quote-cnc-options">
              {materialOptions.map((material) => (
                <button
                  key={material}
                  type="button"
                  onClick={() => handleCnc3dMaterialChange(material)}
                  disabled={isBusy || isConfigLoading}
                  className={formValues.spec.material === material ? "is-active" : ""}
                >
                  {material}
                </button>
              ))}
              <input
                type="text"
                name="material"
                value={
                  materialOptions.includes(formValues.spec.material)
                    ? ""
                    : formValues.spec.material || ""
                }
                onChange={(e) => handleCnc3dMaterialChange(e.target.value)}
                disabled={isBusy || isConfigLoading}
                placeholder="please enter custom materials"
              />
            </div>
          </div>

          {is3dPrintingService && formValues.spec.material === "Resin" ? (
            <div className="quote-cnc-field quote-cnc-material-subfield quote-cnc-resin-type-field">
              <span>Type of Resin</span>
              <div>
                <p className="quote-cnc-inline-warning">⚠ Deformation warning.(For non-metal parts with easily deformable structure.)</p>
                <div className="quote-cnc-options">
                  {THREE_D_RESIN_TYPE_OPTIONS.map((materialGrade) => (
                    <button
                      key={materialGrade}
                      type="button"
                      onClick={() => handleGenericSpecChange({
                        target: { name: "materialGrade", value: materialGrade },
                      })}
                      disabled={isBusy || isConfigLoading}
                      className={formValues.spec.materialGrade === materialGrade ? "is-active" : ""}
                    >
                      {materialGrade}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {isInjectionMoldingService && formValues.spec.material === "ABS" ? (
            <div className="quote-cnc-field quote-cnc-material-subfield">
              <span>Type of ABS</span>
              <div className="quote-cnc-options">
                {INJECTION_MOLDING_ABS_TYPE_OPTIONS.map((materialGrade) => (
                  <button
                    key={materialGrade}
                    type="button"
                    onClick={() => handleGenericSpecChange({
                      target: { name: "materialGrade", value: materialGrade },
                    })}
                    disabled={isBusy || isConfigLoading}
                    className={formValues.spec.materialGrade === materialGrade ? "is-active" : ""}
                  >
                    {materialGrade}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {(formValues.spec.serviceType === "cnc" || isSheetMetalService) && formValues.spec.material === "Aluminum" ? (
            <>
              <div className="quote-cnc-field quote-cnc-material-subfield">
                <span>Type of Aluminum</span>
                <div className="quote-cnc-options">
                  {aluminumTypeOptions.map((materialGrade) => (
                    <button
                      key={materialGrade}
                      type="button"
                      onClick={() => handleGenericSpecChange({
                        target: { name: "materialGrade", value: materialGrade },
                      })}
                      disabled={isBusy || isConfigLoading}
                      className={formValues.spec.materialGrade === materialGrade ? "is-active" : ""}
                    >
                      {materialGrade}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {(formValues.spec.serviceType === "cnc" || isSheetMetalService) && cncColorOptions.length ? (
              <div className="quote-cnc-field quote-cnc-color-field">
                <span><em>*</em> Color:</span>
                <div className="quote-cnc-options">
                  {cncColorOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleGenericSpecChange({
                        target: { name: "color", value: option.value },
                      })}
                      disabled={isBusy || isConfigLoading}
                      className={`quote-cnc-color-option ${formValues.spec.color === option.value ? "is-active" : ""}`}
                    >
                      <span
                        aria-hidden="true"
                        className="quote-cnc-color-swatch"
                        style={{ background: option.swatch }}
                      />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
          ) : null}

          {is3dPrintingService && cncColorOptions.length ? (
            <div className="quote-cnc-field quote-cnc-color-field">
              <span><em>*</em> Color:</span>
              <div className="quote-cnc-options">
                {cncColorOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleGenericSpecChange({
                      target: { name: "color", value: option.value },
                    })}
                    disabled={isBusy || isConfigLoading}
                    className={`quote-cnc-color-option ${formValues.spec.color === option.value ? "is-active" : ""}`}
                  >
                    <span
                      aria-hidden="true"
                      className="quote-cnc-color-swatch"
                      style={{ background: option.swatch }}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {isInjectionMoldingService ? (
            <>
              <div className="quote-cnc-field quote-cnc-color-field">
                <span><em>*</em> Color:</span>
                <div className="quote-cnc-options">
                  {getInjectionColorOptions(formValues.spec).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleGenericSpecChange({
                        target: { name: "color", value: option.value },
                      })}
                      disabled={isBusy || isConfigLoading}
                      className={`quote-cnc-color-option ${formValues.spec.color === option.value ? "is-active" : ""}`}
                    >
                      <span
                        aria-hidden="true"
                        className="quote-cnc-color-swatch"
                        style={{ background: option.swatch }}
                      />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {!isVacuumCasting ? (
                <>
                  <div className="quote-cnc-field">
                    <span><em>*</em> Material Additives:</span>
                    <div className="quote-cnc-options">
                      {INJECTION_MATERIAL_ADDITIVE_OPTIONS.map((materialAdditive) => (
                        <button
                          key={materialAdditive}
                          type="button"
                          onClick={() => handleGenericSpecChange({
                            target: { name: "materialAdditive", value: materialAdditive },
                          })}
                          disabled={isBusy || isConfigLoading}
                          className={formValues.spec.materialAdditive === materialAdditive ? "is-active" : ""}
                        >
                          {materialAdditive}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="quote-cnc-field">
                    <span><em>*</em> SPI Finish:</span>
                    <div className="quote-cnc-options">
                      {INJECTION_SPI_FINISH_OPTIONS.map((spiFinish) => (
                        <button
                          key={spiFinish}
                          type="button"
                          onClick={() => handleGenericSpecChange({
                            target: { name: "spiFinish", value: spiFinish },
                          })}
                          disabled={isBusy || isConfigLoading}
                          className={formValues.spec.spiFinish === spiFinish ? "is-active" : ""}
                        >
                          {spiFinish}
                        </button>
                      ))}
                      <a className="quote-injection-example-link" href="#quote-gerber-upload">e.g.</a>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : null}

          {is3dPrintingService ? (
            <div className="quote-cnc-field quote-cnc-process-field">
              <span><em>*</em> Process:</span>
              <div className="quote-cnc-options">
                {THREE_D_PROCESS_OPTIONS.map((process) => (
                  <button
                    key={process}
                    type="button"
                    onClick={() => handleGenericSpecChange({
                      target: { name: "process", value: process },
                    })}
                    disabled={isBusy || isConfigLoading}
                    className={formValues.spec.process === process ? "is-active" : ""}
                  >
                    {process}
                  </button>
                ))}
                <span className="quote-cnc-warning-text">⚠ Support surface</span>
              </div>
            </div>
          ) : null}

          {isSheetMetalService ? (
            <>
              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Thickness:</span>
                <div className="quote-cnc-process-copy">
                  <div className="quote-cnc-radio-stack">
                    {renderCncRadioOption({
                      name: "thicknessMode",
                      value: "subject_to_3d_file",
                      currentValue: formValues.spec.thicknessMode,
                      label: "Subject to 3D file",
                    })}
                    <div className="quote-cnc-radio-line">
                      {renderCncRadioOption({
                        name: "thicknessMode",
                        value: "standard",
                        currentValue: formValues.spec.thicknessMode,
                        label: "Standard thickness",
                      })}
                      <span className="quote-cnc-warning-text">⚠ Prioritize its application in production.</span>
                    </div>
                  </div>
                  <div className="quote-cnc-options quote-cnc-thickness-options">
                    {SHEET_METAL_THICKNESS_OPTIONS.map((thickness) => (
                      <button
                        key={thickness}
                        type="button"
                        onClick={() => handleGenericSpecChange({
                          target: { name: "standardThickness", value: thickness },
                        })}
                        disabled={isBusy || isConfigLoading}
                        className={formValues.spec.standardThickness === thickness ? "is-active" : ""}
                      >
                        {thickness}
                      </button>
                    ))}
                  </div>
                  <div className="quote-cnc-thickness-input-row">
                    <input
                      type="text"
                      name="customThickness"
                      value={formValues.spec.customThickness || ""}
                      onChange={handleGenericSpecChange}
                      disabled={isBusy || isConfigLoading}
                      placeholder="Custom Thickness"
                    />
                    <span>* Unit: mm</span>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {isInjectionMoldingService
            ? renderInjectionMoldingManufacturingOptions(
                selectedMaterialDetail,
                selectedMaterialTitle
              )
            : null}

          {(formValues.spec.serviceType === "cnc" || isSheetMetalService || is3dPrintingService) ? (
            <>
              <div className="quote-cnc-material-review">
                <div className="quote-cnc-selected-material">
                  <span className="quote-cnc-review-title">Selected material</span>
                  <div className="quote-cnc-material-card">
                    <img src={selectedMaterialDetail.image} alt="" />
                    <div>
                      <strong>{selectedMaterialTitle}</strong>
                      <button type="button">Show material description</button>
                      <span className="quote-cnc-rating">★ {selectedMaterialDetail.rating} <em>({selectedMaterialDetail.reviews})</em></span>
                    </div>
                  </div>
                </div>
                <div className="quote-cnc-surface-finish">
                  <div className="quote-cnc-surface-title">
                    <span>Surface finish - {surfaceFinishCount} options</span>
                    <button type="button">⚠ Discrepancy warning</button>
                  </div>
                  <div className="quote-cnc-surface-select-row">
                    <select
                      name="surfaceFinish"
                      value={formValues.spec.surfaceFinish || ""}
                      onChange={handleGenericSpecChange}
                      disabled={isBusy || isConfigLoading}
                    >
                      <option value="">Please select surface finish</option>
                      {surfaceFinishOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <span aria-hidden="true">×</span>
                  </div>
                </div>
              </div>

              {(formValues.spec.serviceType === "cnc" || isSheetMetalService || is3dPrintingService) ? (
              <div className="quote-cnc-field quote-cnc-technical-drawing-field">
                <span>Technical drawing:</span>
                <div className="quote-cnc-technical-drawing">
                  <p>
                    Please note that your 3D file and part specifications selected on this screen (eg. surface finish) will take priority over your technical drawing. Please ensure your technical drawing is up-to-date.
                  </p>
                  <label>
                    <input
                      type="file"
                      onChange={handleCncTechnicalDrawingChange}
                      disabled={isBusy || isConfigLoading}
                    />
                    Upload a technical drawing
                  </label>
                  {formValues.spec.technicalDrawingFileName ? (
                    <small>{formValues.spec.technicalDrawingFileName}</small>
                  ) : null}
                </div>
              </div>
              ) : null}

              {isSheetMetalService ? renderSheetMetalManufacturingOptions() : null}

              {is3dPrintingService ? (
                <>
                  <div className="quote-cnc-field quote-cnc-radio-field">
                    <span><em>*</em> Threads and<br />Tapped Holes:</span>
                    <div className="quote-cnc-process-copy">
                      <strong>Do your parts need to tap threads?</strong>
                      <p>Please specify if your part has any internal or external threads. Creatyea will not bear any assembly risk if it is a non-standard thread, unless all assembly parts are produced and assembled here. <a href="#quote-gerber-upload">e.g.</a></p>
                      <div className="quote-cnc-radio-group">
                        {renderCncRadioOption({
                          name: "hasThreads",
                          value: false,
                          currentValue: Boolean(formValues.spec.hasThreads),
                          label: "No",
                        })}
                        {renderCncRadioOption({
                          name: "hasThreads",
                          value: true,
                          currentValue: Boolean(formValues.spec.hasThreads),
                          label: "Yes",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="quote-cnc-field quote-cnc-radio-field">
                    <span><em>*</em> Inserts:</span>
                    <div className="quote-cnc-process-copy">
                      <p>Please specify the standard inserts in your part. <a href="#quote-gerber-upload">e.g.</a></p>
                      <div className="quote-cnc-radio-group">
                        {renderCncRadioOption({
                          name: "hasInserts",
                          value: false,
                          currentValue: Boolean(formValues.spec.hasInserts),
                          label: "No",
                        })}
                        {renderCncRadioOption({
                          name: "hasInserts",
                          value: true,
                          currentValue: Boolean(formValues.spec.hasInserts),
                          label: "Yes",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="quote-cnc-field quote-cnc-marking-field">
                    <span>Part Marking:<HelpTooltip content="Choose markings that should be added to the manufactured part." label="Part Marking" /></span>
                    <div className="quote-cnc-marking-options">
                      {CNC_PART_MARKING_OPTIONS.map((option) => (
                        <label key={option.value}>
                          <input
                            type="checkbox"
                            checked={(formValues.spec.partMarking || []).includes(option.value)}
                            onChange={(e) => handleCncPartMarkingChange(option.value, e.target.checked)}
                            disabled={isBusy || isConfigLoading}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="quote-cnc-field quote-cnc-radio-field">
                    <span><em>*</em> Parts assembly:<HelpTooltip content="Specify whether this 3D printing order includes assembly requirements." label="Parts assembly" /></span>
                    <div className="quote-cnc-process-copy">
                      <p>Please specify the assembly requirements. Creatyea does not bear any assembly risk if you choose No assembly requirement. <a href="#quote-gerber-upload">e.g.</a></p>
                      <div className="quote-cnc-radio-group">
                        {CNC_PARTS_ASSEMBLY_OPTIONS.map((option) => renderCncRadioOption({
                          name: "partsAssembly",
                          value: option.value,
                          currentValue: formValues.spec.partsAssembly,
                          label: option.label,
                        }))}
                      </div>
                    </div>
                  </div>

                  <div className="quote-cnc-field quote-cnc-printing-risk-field">
                    <span>Printing risk:</span>
                    <div className="quote-cnc-printing-risk-content">
                      <p>The parameter is checked by default. You can uncheck it and we will email you with issues when the part has a specific printing risk.</p>
                      <label>
                        <input
                          type="checkbox"
                          name="wallThicknessRiskTaken"
                          checked={formValues.spec.wallThicknessRiskTaken !== false}
                          onChange={handleGenericSpecChange}
                          disabled={isBusy || isConfigLoading}
                        />
                        <span>
                          <strong>Wall thickness risk taken</strong>
                          <em>Customer knows about the thin walls designed do not meet the wall thickness standard, but needs to print as-is and take all risks. <a href="#quote-gerber-upload">e.g.</a></em>
                        </span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="nonStandardPrintedThreadsRiskTaken"
                          checked={formValues.spec.nonStandardPrintedThreadsRiskTaken !== false}
                          onChange={handleGenericSpecChange}
                          disabled={isBusy || isConfigLoading}
                        />
                        <span>
                          <strong>Non-standard printed threads risk taken</strong>
                          <em>Customer knows about the threads designed do not meet the thread printing standard, but needs to print as-is and take all risks. <a href="#quote-gerber-upload">e.g.</a></em>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="quote-cnc-field quote-cnc-product-desc-field">
                    <span><em>*</em> Product Desc:<HelpTooltip content="Choose the product application category for engineering review." label="Product Desc" /></span>
                    <select
                      name="productDescription"
                      value={formValues.spec.productDescription || ""}
                      onChange={handleGenericSpecChange}
                      disabled={isBusy || isConfigLoading}
                    >
                      <option value="">Select</option>
                      {CNC_PRODUCT_DESC_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <label className="quote-cnc-field quote-cnc-other-request-field">
                    <span>Other special<br />request:</span>
                    <textarea
                      name="specialRequest"
                      rows="4"
                      value={formValues.spec.specialRequest || ""}
                      onChange={handleGenericSpecChange}
                      disabled={isBusy || isConfigLoading}
                      placeholder="Fill in any requirements about production, package, or shipment here."
                    />
                  </label>
                </>
              ) : null}

              {formValues.spec.serviceType === "cnc" ? (
                <>
              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Threads and<br />Tapped Holes:</span>
                <div className="quote-cnc-process-copy">
                  <strong>Do your parts need to tap threads?</strong>
                  <p>Please specify if your part has any internal or external threads. Creatyea will not bear any assembly risk if it is a non-standard thread, unless all assembly parts are produced and assembled here. <a href="#quote-gerber-upload">e.g.</a></p>
                  <div className="quote-cnc-radio-group">
                    {renderCncRadioOption({
                      name: "hasThreads",
                      value: false,
                      currentValue: Boolean(formValues.spec.hasThreads),
                      label: "No",
                    })}
                    {renderCncRadioOption({
                      name: "hasThreads",
                      value: true,
                      currentValue: Boolean(formValues.spec.hasThreads),
                      label: "Yes",
                    })}
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Inserts:</span>
                <div className="quote-cnc-process-copy">
                  <p>Please specify the standard inserts in your part. <a href="#quote-gerber-upload">e.g.</a></p>
                  <div className="quote-cnc-radio-group">
                    {renderCncRadioOption({
                      name: "hasInserts",
                      value: false,
                      currentValue: Boolean(formValues.spec.hasInserts),
                      label: "No",
                    })}
                    {renderCncRadioOption({
                      name: "hasInserts",
                      value: true,
                      currentValue: Boolean(formValues.spec.hasInserts),
                      label: "Yes",
                    })}
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Tolerance:</span>
                <div className="quote-cnc-process-copy">
                  <p>Tolerances will be controlled with <strong><u>ISO 2768-1</u></strong>. For other tighter tolerances, technical drawing will be required to indicate critical dimensions.</p>
                  <div className="quote-cnc-radio-stack">
                    {renderCncRadioOption({
                      name: "toleranceRequirement",
                      value: "iso_2768_1",
                      currentValue: formValues.spec.toleranceRequirement,
                      label: "No tighter tolerances required (ISO 2768-1)",
                    })}
                    <div className="quote-cnc-radio-line">
                      {renderCncRadioOption({
                        name: "toleranceRequirement",
                        value: "tighter",
                        currentValue: formValues.spec.toleranceRequirement,
                        label: "Tighter tolerances required",
                      })}
                      <button type="button" className="quote-cnc-select-link">Select</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Surface Roughness:</span>
                <div className="quote-cnc-process-copy">
                  <p>Surface roughness will be controlled into <strong>250uin/6.3um Ra</strong> unless you have other surface roughness required. <a href="#quote-gerber-upload">e.g.</a></p>
                  <div className="quote-cnc-radio-group quote-cnc-roughness-options">
                    {CNC_SURFACE_ROUGHNESS_OPTIONS.map((option) => renderCncRadioOption({
                      name: "surfaceRoughness",
                      value: option.value,
                      currentValue: formValues.spec.surfaceRoughness,
                      label: option.label,
                    }))}
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-marking-field">
                <span>Part Marking:<HelpTooltip content="Choose markings that should be added to the manufactured part." label="Part Marking" /></span>
                <div className="quote-cnc-marking-options">
                  {CNC_PART_MARKING_OPTIONS.map((option) => (
                    <label key={option.value}>
                      <input
                        type="checkbox"
                        checked={(formValues.spec.partMarking || []).includes(option.value)}
                        onChange={(e) => handleCncPartMarkingChange(option.value, e.target.checked)}
                        disabled={isBusy || isConfigLoading}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span><em>*</em> Parts assembly:<HelpTooltip content="Specify whether this CNC order includes assembly requirements." label="Parts assembly" /></span>
                <div className="quote-cnc-process-copy">
                  <p>Please specify the assembly requirements. Creatyea does not bear any assembly risk if you choose No assembly requirement. <a href="#quote-gerber-upload">e.g.</a></p>
                  <div className="quote-cnc-radio-group">
                    {CNC_PARTS_ASSEMBLY_OPTIONS.map((option) => renderCncRadioOption({
                      name: "partsAssembly",
                      value: option.value,
                      currentValue: formValues.spec.partsAssembly,
                      label: option.label,
                    }))}
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span>Finished appearance:</span>
                <div className="quote-cnc-process-copy quote-cnc-appearance-options">
                  <div>
                    {renderCncRadioOption({
                      name: "finishedAppearance",
                      value: "standard",
                      currentValue: formValues.spec.finishedAppearance,
                      label: "Standard",
                    })}
                    <p>Normal processing traces or scratches caused during production, or subtle traces left after surface finish. <a href="#quote-gerber-upload">e.g.</a></p>
                  </div>
                  <div>
                    {renderCncRadioOption({
                      name: "finishedAppearance",
                      value: "premium",
                      currentValue: formValues.spec.finishedAppearance,
                      label: "Premium (Extra charges)",
                    })}
                    <p>Higher appearance requirements, the surface is clean and smooth without obvious flaws. (Except for the inherent anodizing rack marks). Photos will be sent to confirm before shipment. <a href="#quote-gerber-upload">e.g.</a></p>
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-radio-field">
                <span>Inspection:</span>
                <div className="quote-cnc-process-copy">
                  <p>The inspection report will not be sent out with your parts unless you need it.</p>
                  <div className="quote-cnc-radio-stack quote-cnc-inspection-options">
                    {CNC_INSPECTION_OPTIONS.map((option) => (
                      <div key={option.value}>
                        {renderCncRadioOption({
                          name: "inspection",
                          value: option.value,
                          currentValue: formValues.spec.inspection,
                          label: option.label,
                        })}
                        <ul>
                          {option.details.map((detail) => (
                            <li key={detail}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="quote-cnc-field quote-cnc-product-desc-field">
                <span><em>*</em> Product Desc:<HelpTooltip content="Choose the product application category for engineering review." label="Product Desc" /></span>
                <select
                  name="productDescription"
                  value={formValues.spec.productDescription || ""}
                  onChange={handleGenericSpecChange}
                  disabled={isBusy || isConfigLoading}
                >
                  <option value="">Select</option>
                  {CNC_PRODUCT_DESC_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <label className="quote-cnc-field quote-cnc-other-request-field">
                <span>Other special<br />request:</span>
                <textarea
                  name="specialRequest"
                  rows="4"
                  value={formValues.spec.specialRequest || ""}
                  onChange={handleGenericSpecChange}
                  disabled={isBusy || isConfigLoading}
                  placeholder="Fill in any requirements about production, package, or shipment here."
                />
              </label>
                </>
              ) : null}
            </>
          ) : null}

          {formValues.spec.serviceType !== "cnc" && !isSheetMetalService && !is3dPrintingService && !isInjectionMoldingService ? (
            <>
              <div className="quote-cnc-field">
                <span>Bounding Size:</span>
                <div className="quote-cnc-size-group">
                  {["length", "width", "height"].map((name) => (
                    <input
                      key={name}
                      type="number"
                      min="0"
                      name={name}
                      value={formValues.spec.size?.[name] || ""}
                      onChange={handleGenericSizeChange}
                      disabled={isBusy || isConfigLoading}
                      placeholder={name}
                    />
                  ))}
                </div>
              </div>

              <label className="quote-cnc-field">
                <span>Special Request:</span>
                <textarea
                  name="specialRequest"
                  rows="4"
                  value={formValues.spec.specialRequest || ""}
                  onChange={handleGenericSpecChange}
                  disabled={isBusy || isConfigLoading}
                  placeholder="Describe tolerance, finish, color, threads, inserts, inspection, or other manufacturing notes."
                />
              </label>
            </>
          ) : null}
        </div>
      </div>
      </>
        );
      })()}
    </section>
  );

  /**
   * 渲染通用品类字段。
   * @param {Object} field 字段配置
   * @returns {JSX.Element} 通用字段节点
   */
  const renderGenericField = (field) => {
    if (field.type === "select") {
      return (
        <SelectableGroup
          name={field.name}
          value={formValues.spec[field.name] || ""}
          options={field.options || []}
          onChange={handleGenericSpecChange}
          disabled={isBusy || isConfigLoading}
        />
      );
    }

    if (field.type === "checkbox") {
      return (
        <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            name={field.name}
            checked={Boolean(formValues.spec[field.name])}
            onChange={handleGenericSpecChange}
            disabled={isBusy || isConfigLoading}
          />
          {field.label}
        </label>
      );
    }

    return (
      <input
        type={field.type || "text"}
        name={field.name}
        min={field.type === "number" ? "0" : undefined}
        step={field.type === "number" ? "0.01" : undefined}
        value={formValues.spec[field.name] || ""}
        onChange={handleGenericSpecChange}
        disabled={isBusy || isConfigLoading}
        className="w-full rounded-md border border-gray-300 px-3 py-2"
      />
    );
  };

  /**
   * 渲染独立 Assembly 报价的目标站风格选项区。
   * @returns {JSX.Element} Assembly Service 表单节点
   */
  const renderStandaloneAssemblySpec = () => (
    <section className="quote-section-card border border-gray-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FiTool className="h-6 w-6 flex-none text-gray-700" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">Assembly Service</h3>
        <span className="ml-auto text-sm font-bold text-green-600">▶ How it works (4 steps)</span>
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600">3 flexible options:</span>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {[
              { value: "turnkey", title: "Turnkey", description: "Creatyea supply parts" },
              { value: "consigned", title: "Kitted or Consigned", description: "Customer supply parts" },
              { value: "combo", title: "Combo", description: "You supply some parts we do the rest" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleStandaloneAssemblySpecChange({
                  target: { name: "sourcingType", value: option.value, type: "text" },
                })}
                className={`flex min-h-[78px] items-center gap-3 border px-4 py-3 text-left ${
                  formValues.spec.sourcingType === option.value
                    ? "border-green-600 bg-green-50 shadow-[inset_0_0_0_1px_#16a34a]"
                    : "border-gray-300"
                }`}
              >
                <span className={`h-6 w-6 flex-none rounded-full border-2 ${
                  formValues.spec.sourcingType === option.value
                    ? "border-green-600 bg-green-600 shadow-[inset_0_0_0_4px_white]"
                    : "border-gray-300"
                }`}></span>
                <span>
                  <strong className="block text-sm text-gray-700">{option.title}</strong>
                  <span className="block text-xs text-gray-500">{option.description}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Board type:"
            help="Choose Single pieces for separate boards, or Panelized PCBs when boards are delivered in panels for assembly."
          />
          <div>
            <div className="flex flex-wrap gap-3">
              {[
                { value: "single_pieces", label: "Single pieces" },
                { value: "panelized", label: "Panelized PCBs" },
              ].map((option) => (
                <AssemblyChoiceButton
                  key={option.value}
                  onClick={() => handleStandaloneAssemblySpecChange({
                    target: { name: "boardType", value: option.value, type: "text" },
                  })}
                  selected={formValues.spec.boardType === option.value}
                >
                  {option.label}
                </AssemblyChoiceButton>
              ))}
            </div>
            <p className="mt-3 max-w-4xl text-xs text-amber-600">
              We suggest choose to do panel if single PCB qty is more than 20pcs or any side of single board is smaller than 50mm.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Assembly side(s):"
            help="Choose the side that requires component assembly."
          />
          <div className="flex flex-wrap gap-3">
            {[
              { value: "top", label: "Top side" },
              { value: "bottom", label: "Bottom side" },
              { value: "both", label: "Both sides" },
            ].map((option) => (
              <AssemblyChoiceButton
                key={option.value}
                onClick={() => handleStandaloneAssemblySpecChange({
                  target: { name: "assemblySide", value: option.value, type: "text" },
                })}
                selected={formValues.spec.assemblySide === option.value}
              >
                {option.label}
              </AssemblyChoiceButton>
            ))}
          </div>
        </div>

        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="* Quantity:"
            help="Please fill in total quantity of single PCBs."
          />
          <span>
            <span className="flex max-w-xs">
              <input
                type="number"
                min="1"
                name="quantity"
                value={formValues.spec.quantity}
                onChange={handleStandaloneAssemblySpecChange}
                className="min-w-0 flex-1 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-600"
              />
              <span className="border border-l-0 border-gray-300 bg-gray-100 px-4 py-2 text-sm text-gray-600">pcs</span>
            </span>
            <span className="mt-2 block text-xs text-amber-600">Please fill in total quantity of single PCBs.</span>
          </span>
        </label>

        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600"><span className="text-red-600">*</span> Pay attention:</span>
          <div className="space-y-5">
            {[
              { name: "sensitiveComponents", label: "Contains sensitive components/parts" },
              { name: "acceptAlternatives", label: "Do you accept alternatives/substitutes made in China?" },
            ].map((field) => (
              <div key={field.name}>
                <AssemblyHelpLabel label={field.label} help="Select Yes only when this condition applies to the assembly order." />
                <div className="mt-2 flex gap-7">
                  {[false, true].map((value) => (
                    <button
                      key={String(value)}
                      type="button"
                      onClick={() => handleStandaloneAssemblySpecChange({
                        target: { name: field.name, checked: value, type: "checkbox" },
                      })}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <span className={`h-6 w-6 rounded-full border-2 ${
                        formValues.spec[field.name] === value
                          ? "border-green-600 bg-green-600 shadow-[inset_0_0_0_4px_white]"
                          : "border-gray-300"
                      }`}></span>
                      {value ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {[
            {
              title: "Other Parameters",
              hint: "(Fill in to get the exact price, or leave it blank to wait for final quote.)",
              open: showAssemblyOtherParameters,
              toggle: () => setShowAssemblyOtherParameters((visible) => !visible),
            },
            {
              title: "Customized Services and Advanced Options",
              hint: "",
              open: showAssemblyAdvancedOptions,
              toggle: () => setShowAssemblyAdvancedOptions((visible) => !visible),
            },
          ].map((section) => (
            <div key={section.title}>
              <button
                type="button"
                onClick={section.toggle}
                className="flex w-full items-center justify-between bg-gray-100 px-4 py-3 text-left"
              >
                <span className="font-bold text-gray-800">
                  <span className="mr-2 text-xl">{section.open ? "-" : "+"}</span>
                  {section.title}
                  {section.hint ? (
                    <span className="ml-2 font-normal text-amber-600">{section.hint}</span>
                  ) : null}
                </span>
                <span className="flex items-center gap-1 text-sm font-bold text-gray-800">
                  {section.open ? "Hide" : "More"}
                  <FiChevronDown
                    className={`h-4 w-4 transition-transform ${section.open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </span>
              </button>

              {section.open ? (
                <div className="border border-t-0 border-gray-200 p-4">
                  {section.title === "Other Parameters" ? (
                    <div className="space-y-4">
                      <p className="text-sm text-amber-600">
                        They can be left blank if you're not sure about the number of Unique Parts, SMT Parts, BGA/QFP Parts or THT parts.
                      </p>
                      <div className="space-y-3">
                        {[
                          {
                            name: "uniquePartsCount",
                            label: "Number of\nUnique Parts:",
                            imageContent: (
                              <>
                                <img
                                  src="/img/electronic_01.png"
                                  alt="Unique parts example"
                                  className="h-auto w-[245px] object-contain"
                                />
                              </>
                            ),
                          },
                          {
                            name: "smdPartsCount",
                            label: "Number of\nSMD Parts:",
                            placeholder: "Total number",
                            imageContent: (
                              <img
                                src="/img/04_furn_smd2.png"
                                alt="SMD resistor part example"
                                className="h-[70px] w-[70px] object-contain"
                              />
                            ),
                          },
                          {
                            name: "bgaQfpPartsCount",
                            label: "Number of\nBGA/QFP Parts:",
                            imageContent: (
                              <>
                                <img
                                  src="/img/04_furn_smd1.png"
                                  alt="QFP part example"
                                  className="h-[70px] w-[70px] object-contain"
                                />
                                <img
                                  src="/img/04_furn_bga.png"
                                  alt="BGA part example"
                                  className="h-[70px] w-[70px] object-contain"
                                />
                              </>
                            ),
                          },
                          {
                            name: "throughHolePartsCount",
                            label: "Number of\nThrough-Hole\nParts:",
                            placeholder: "Total number",
                            imageContent: (
                              <>
                                <img
                                  src="/img/04_furn_dip1.png"
                                  alt="Through-hole capacitor part example"
                                  className="h-[70px] w-[70px] object-contain"
                                />
                                <img
                                  src="/img/04_furn_dip3.png"
                                  alt="Through-hole transistor part example"
                                  className="h-[70px] w-[70px] object-contain"
                                />
                              </>
                            ),
                          },
                        ].map((field) => (
                          <label
                            key={field.name}
                            className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_225px_1fr]"
                          >
                            <AssemblyHelpLabel
                              label={field.label}
                              help="Fill in this value to help calculate a more accurate assembly quote."
                            />
                            <input
                              type="number"
                              min="0"
                              name={field.name}
                              value={formValues.spec[field.name] || ""}
                              onChange={handleStandaloneAssemblySpecChange}
                              placeholder={field.placeholder || ""}
                              className="border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-600"
                            />
                            <span className="flex min-h-12 items-center gap-2">
                              {field.imageContent}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-amber-600">
                        Below options, their cost is not included in the online quotation
                      </p>
                      <div className="grid grid-cols-1 gap-x-12 gap-y-4 lg:grid-cols-[410px_410px]">
                        <div className="space-y-4">
                          {[
                            { name: "depanelBoards", label: "Depanel the\nboards to delivery:" },
                            { name: "conformalCoating", label: "Conformal coating:" },
                            { name: "pressFitAssembly", label: "Press-fit assembly:" },
                            { name: "cableHarnessAssembly", label: "Cable wire harness\nassembly:" },
                          ].map((field) => (
                            <div key={field.name} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr] sm:gap-4">
                              <AssemblyHelpLabel
                                label={field.label}
                                help="Select Yes when this customized service is required."
                              />
                              <div className="flex gap-3">
                                {[false, true].map((value) => {
                                  const isSelected = Boolean(formValues.spec[field.name]) === value;
                                  return (
                                    <button
                                      key={String(value)}
                                      type="button"
                                      onClick={() => handleStandaloneAssemblySpecChange({
                                        target: { name: field.name, checked: value, type: "checkbox" },
                                      })}
                                      className={`relative overflow-hidden border px-3 py-2 text-sm ${
                                        isSelected
                                          ? "border-green-600 text-gray-800 shadow-[inset_0_0_0_1px_#16a34a]"
                                          : "border-gray-300 text-gray-500"
                                      }`}
                                    >
                                      {value ? "Yes" : "No"}
                                      {isSelected ? (
                                        <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center bg-green-600 text-[10px] leading-none text-white">
                                          ✓
                                        </span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}

                          <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr] sm:gap-4">
                            <AssemblyHelpLabel
                              label="Package box:"
                              help="Choose Neutral box for standard packaging, or Custom box when special packaging is required."
                            />
                            <div className="flex flex-wrap gap-3">
                              {[
                                { value: "neutral", label: "Neutral box" },
                                { value: "custom", label: "Custom box" },
                              ].map((option) => {
                                const isSelected = (formValues.spec.packageBox || "neutral") === option.value;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleStandaloneAssemblySpecChange({
                                      target: { name: "packageBox", value: option.value, type: "text" },
                                    })}
                                    className={`relative overflow-hidden border px-3 py-2 text-sm ${
                                      isSelected
                                        ? "border-green-600 text-gray-800 shadow-[inset_0_0_0_1px_#16a34a]"
                                        : "border-gray-300 text-gray-500"
                                    }`}
                                  >
                                    {option.label}
                                    {isSelected ? (
                                      <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center bg-green-600 text-[10px] leading-none text-white">
                                        ✓
                                      </span>
                                    ) : null}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr] sm:gap-4">
                            <AssemblyHelpLabel
                              label={"Flying Probe\nTesting:"}
                              help="Select Yes when the assembled boards require flying probe electrical testing."
                            />
                            <div className="flex gap-3">
                              {[false, true].map((value) => {
                                const isSelected = Boolean(formValues.spec.flyingProbeTesting) === value;
                                return (
                                  <button
                                    key={String(value)}
                                    type="button"
                                    onClick={() => handleStandaloneAssemblySpecChange({
                                      target: { name: "flyingProbeTesting", checked: value, type: "checkbox" },
                                    })}
                                    className={`relative overflow-hidden border px-3 py-2 text-sm ${
                                      isSelected
                                        ? "border-green-600 text-gray-800 shadow-[inset_0_0_0_1px_#16a34a]"
                                        : "border-gray-300 text-gray-500"
                                    }`}
                                  >
                                    {value ? "Yes" : "No"}
                                    {isSelected ? (
                                      <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center bg-green-600 text-[10px] leading-none text-white">
                                        ✓
                                      </span>
                                    ) : null}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <label className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr] sm:gap-4">
                            <AssemblyHelpLabel
                              label={"Number of X-ray\ntest:"}
                              help="Enter the number of boards or locations that require X-ray inspection."
                            />
                            <input
                              type="number"
                              min="0"
                              name="xrayTestCount"
                              value={formValues.spec.xrayTestCount || ""}
                              onChange={handleStandaloneAssemblySpecChange}
                              className="max-w-[250px] border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-600"
                            />
                          </label>
                        </div>

                        <div className="space-y-4">
                          {[
                            { name: "functionTest", label: "Function test:" },
                            { name: "firmwareLoading", label: "Firmware loading:" },
                            { name: "boxBuildAssembly", label: "Box build assembly:" },
                          ].map((field) => (
                            <div key={field.name} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr] sm:gap-4">
                              <AssemblyHelpLabel
                                label={field.label}
                                help="Select Yes when this customized service is required."
                              />
                              <div className="flex gap-3">
                                {[false, true].map((value) => {
                                  const isSelected = Boolean(formValues.spec[field.name]) === value;
                                  return (
                                    <button
                                      key={String(value)}
                                      type="button"
                                      onClick={() => handleStandaloneAssemblySpecChange({
                                        target: { name: field.name, checked: value, type: "checkbox" },
                                      })}
                                      className={`relative overflow-hidden border px-3 py-2 text-sm ${
                                        isSelected
                                          ? "border-green-600 text-gray-800 shadow-[inset_0_0_0_1px_#16a34a]"
                                          : "border-gray-300 text-gray-500"
                                      }`}
                                    >
                                      {value ? "Yes" : "No"}
                                      {isSelected ? (
                                        <span className="absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center bg-green-600 text-[10px] leading-none text-white">
                                          ✓
                                        </span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600 md:pt-2">Detailed information<br />of assembly:</span>
          <textarea
            name="detailedInformation"
            rows="4"
            value={formValues.spec.detailedInformation || ""}
            onChange={handleStandaloneAssemblySpecChange}
            className="w-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-green-600"
            placeholder="Please fill in detailed technical information about the PCB assembly, other shell assembly, cable soldering, fuse wires, rivets, etc."
          />
        </label>
      </div>
    </section>
  );

  /**
   * 渲染独立 SMD-Stencil 报价的目标站风格选项区。
   * @returns {JSX.Element} SMD-Stencil 表单节点
   */
  const renderStandaloneSmdStencilSpec = () => (
    <section className="quote-section-card border border-gray-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FiGrid className="h-6 w-6 flex-none text-gray-700" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">SMD-Stencil</h3>
        <img
          src="/img/stencil-sm.png"
          alt="SMD stencil framework and non-framework examples"
          className="ml-auto hidden h-auto w-[360px] object-contain lg:block"
        />
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600 md:pt-2">Stencil type:</span>
          <div>
            <div className="flex flex-wrap gap-3">
              {[
                { value: "framework", label: "Framework" },
                { value: "non_framework", label: "Non-framework" },
              ].map((option) => (
                <AssemblyChoiceButton
                  key={option.value}
                  onClick={() => handleStandaloneStencilTypeChange(option.value)}
                  selected={formValues.spec.stencilType === option.value}
                >
                  {option.label}
                </AssemblyChoiceButton>
              ))}
            </div>
            <p className="mt-2 text-sm text-amber-600">
              Allow combining several PCBs on a single SMD stencil.
            </p>
          </div>
        </div>

        {formValues.spec.stencilType === "framework" ? (
          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
            <AssemblyHelpLabel
              label={"Multi-level/\nStep stencil:"}
              help="Choose Yes when the stencil needs step-up or step-down thickness areas."
            />
            <div className="flex gap-3">
              {[true, false].map((value) => (
                <AssemblyChoiceButton
                  key={String(value)}
                  onClick={() => handleStandaloneStencilSpecChange({
                    target: { name: "multiLevel", checked: value, type: "checkbox" },
                  })}
                  selected={formValues.spec.multiLevel === value}
                >
                  {value ? "Yes" : "No"}
                </AssemblyChoiceButton>
              ))}
            </div>
          </div>
        ) : null}

        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600">Size (mm):</span>
          <select
            name="size"
            value={formValues.spec.size || "370x470"}
            onChange={handleStandaloneStencilSpecChange}
            className="w-full max-w-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-green-500"
          >
            {STENCIL_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        {["custom", "custom_large"].includes(formValues.spec.size) ? (
          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
            <span className="text-sm text-gray-600">Custom Size (mm):</span>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min="1"
                name="customWidth"
                value={formValues.spec.customWidth}
                onChange={handleStandaloneStencilSpecChange}
                className="w-28 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                aria-label="Custom stencil width in millimeters"
              />
              <span className="text-gray-500">×</span>
              <input
                type="number"
                min="1"
                name="customHeight"
                value={formValues.spec.customHeight}
                onChange={handleStandaloneStencilSpecChange}
                className="w-28 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                aria-label="Custom stencil height in millimeters"
              />
              <span className="text-xs text-amber-600">
                {formValues.spec.size === "custom_large"
                  ? "*Maximum valid area: 550×550mm"
                  : "*Maximum valid area: 190×290mm"}
              </span>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Stencil side:"
            help="Choose the PCB side that requires solder paste printing. Select a combined or separate stencil when both sides are needed."
          />
          <div className="flex flex-wrap gap-3">
            {STENCIL_SIDE_OPTIONS.map((option) => (
              <AssemblyChoiceButton
                key={option.value}
                onClick={() => handleStandaloneStencilSpecChange({
                  target: { name: "side", value: option.value, type: "text" },
                })}
                selected={formValues.spec.side === option.value}
              >
                {option.label}
              </AssemblyChoiceButton>
            ))}
          </div>
        </div>

        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600">Quantity:</span>
          <span className="flex max-w-xs">
            <input
              type="number"
              min="1"
              name="quantity"
              value={formValues.spec.quantity}
              onChange={handleStandaloneStencilSpecChange}
              className="min-w-0 flex-1 border border-gray-300 bg-amber-50 px-3 py-2 text-sm outline-none focus:border-green-500"
            />
            <span className="border border-l-0 border-gray-300 bg-gray-100 px-4 py-2 text-sm text-gray-600">Pieces</span>
          </span>
        </label>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Thickness:"
            help="Select the stainless-steel stencil thickness. The appropriate value depends on component pitch, aperture size and required solder paste volume."
          />
          <div className="flex flex-wrap gap-3">
            {STENCIL_THICKNESS_OPTIONS.map((value) => (
              <AssemblyChoiceButton
                key={value}
                onClick={() => handleStandaloneStencilSpecChange({
                  target: { name: "thickness", value, type: "text" },
                })}
                selected={formValues.spec.thickness === value}
              >
                {value}mm
              </AssemblyChoiceButton>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Existing fiducials:"
            help="Choose whether fiducial marks should be absent, half lasered or lasered through for stencil alignment."
          />
          <div className="flex flex-wrap gap-3">
            {[
              { value: "none", label: "None" },
              { value: "half_lasered", label: "half lasered" },
              { value: "lasered_through", label: "lasered through" },
            ].map((option) => (
              <AssemblyChoiceButton
                key={option.value}
                onClick={() => handleStandaloneStencilSpecChange({
                  target: { name: "fiducials", value: option.value, type: "text" },
                })}
                selected={formValues.spec.fiducials === option.value}
              >
                {option.label}
              </AssemblyChoiceButton>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <AssemblyHelpLabel
            label="Electropolishing:"
            help="Electropolishing smooths stencil aperture walls to improve solder paste release, especially for fine-pitch components."
          />
          <div className="flex gap-3">
            {[true, false].map((value) => (
              <AssemblyChoiceButton
                key={String(value)}
                onClick={() => handleStandaloneStencilSpecChange({
                  target: { name: "electropolishing", checked: value, type: "checkbox" },
                })}
                selected={formValues.spec.electropolishing === value}
              >
                {value ? "Yes" : "No"}
              </AssemblyChoiceButton>
            ))}
          </div>
        </div>

        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
          <span className="text-sm text-gray-600">PO No.:</span>
          <input
            type="text"
            name="poNumber"
            maxLength="200"
            value={formValues.spec.poNumber || ""}
            onChange={handleStandaloneStencilSpecChange}
            className="w-full max-w-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
            placeholder="Optional purchase order number"
          />
        </label>

        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
          <span className="md:pt-2">
            <AssemblyHelpLabel
              label="Other Special request:"
              help="Describe special stencil requirements such as stepped areas, custom apertures, markings or other manufacturing notes."
            />
          </span>
          <textarea
            name="specialRequest"
            rows="4"
            value={formValues.spec.specialRequest || ""}
            onChange={handleStandaloneStencilSpecChange}
            className="w-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-green-500"
            placeholder="Fill in any Stencil details to make it as clear as possible for us to understand your requirements. e.g. Multi-level /Step stencil, please specify the region and thickness for step-up/step-down in the gerber."
          />
        </label>
      </div>
    </section>
  );


  return (
    <div className="quote-page-shell grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_310px]">
      <div className="quote-primary-column space-y-4">
        <Title />
        <div className="quote-workbench space-y-4 bg-white">
          <section className="quote-progress-section border border-gray-200 bg-white">
            <div className="quote-progress-track relative flex min-w-[700px] items-start justify-between">
              {/* Progress Line */}
              <div className="absolute left-8 right-8 top-4 z-0 h-px bg-gray-200"></div>
              
              {QUOTE_STEPS.map((step, index) => {
                const isCompleted = index < (savedQuote?._id ? 1 : 0);
                const isActive = index === (savedQuote?._id ? 1 : 0);
                
                return (
                  <div key={step} className="quote-progress-step group relative z-10 flex flex-col items-center">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-all ${
                        isCompleted
                          ? "bg-green-600 text-white shadow-md"
                          : isActive
                          ? "bg-green-600 text-white ring-4 ring-green-50 shadow-sm"
                          : "bg-white border-2 border-gray-200 text-gray-400"
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className={`mt-2 text-[10px] md:text-xs font-semibold whitespace-nowrap ${
                      isActive ? "text-green-700" : isCompleted ? "text-gray-600" : "text-gray-400"
                    }`}>
                      {step}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="quote-service-tabs border border-gray-200 bg-white">
            <div className="quote-service-tab-list flex w-full items-stretch">
              {quoteCategories.map((category) => {
                const isActive = formValues.quoteType === category.value;
                return (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => handleQuoteTypeChange(category.value)}
                    className={`quote-service-tab flex items-center gap-2 px-5 py-3 text-sm font-bold transition-all ${
                      isActive
                        ? "is-active bg-green-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50 hover:text-green-700"
                    }`}
                  >
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Customer Information Section - Hidden by default, required only on Save/Checkout */}
          {!user && (savedQuote || quoteResult || showCustomerInfo) && (
            <section className="quote-section-card border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Customer Information (Required to Save/Submit)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formValues.customerInfo.name}
                    onChange={handleCustomerInfoChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formValues.customerInfo.email}
                    onChange={handleCustomerInfoChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formValues.customerInfo.phone}
                    onChange={handleCustomerInfoChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formValues.customerInfo.company}
                    onChange={handleCustomerInfoChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white"
                  />
                </div>
              </div>
            </section>
          )}

          {hasPcbSpecifications ? (
          <section className="quote-section-card quote-specification-card border border-gray-200 bg-white">
            <div className="flex min-h-[92px] items-center justify-between gap-4 border-b border-gray-200 px-7 py-5 shadow-sm">
              <button
                type="button"
                onClick={() => setShowPcbSpecifications((visible) => !visible)}
                className="flex min-w-0 items-center gap-3 text-left"
                aria-expanded={showPcbSpecifications}
              >
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded border border-blue-600 bg-blue-600 text-xs leading-none text-white" aria-hidden="true">
                  ✓
                </span>
                <FiLayers className="h-9 w-9 flex-none text-gray-700" aria-hidden="true" />
                <span className="min-w-0 text-xl font-bold text-green-600">
                  PCB Specifications
                </span>
                <FiChevronDown
                  className={`h-4 w-4 flex-none text-gray-500 transition-transform ${
                    showPcbSpecifications ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
              <a
                href="#quote-gerber-upload"
                className="flex flex-none items-center gap-2 text-lg font-bold text-green-600 hover:text-green-700"
              >
                <FiSearch className="h-7 w-7 text-gray-700" aria-hidden="true" />
                <span>Online Gerber Viewer</span>
              </a>
            </div>
            {showPcbSpecifications ? (
            <div className="quote-fields space-y-0 p-4 md:p-5">
              {isFpcRigidFlex ? (
                <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                  <QuoteFieldLabel label="PCB Type:" help="Choose Flexible PCB for pure FPC orders, or Rigid-Flex Board when rigid and flexible sections are combined." alignTop />
                  <div className="flex-grow">
                    <SelectableGroup
                      name="flexPcbType"
                      value={formValues.pcbSpec.flexPcbType}
                      options={FPC_PCB_TYPE_OPTIONS}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                      groupClassName="quote-pcbway-options quote-fpc-main-options"
                      buttonClassName="quote-pcbway-option"
                    />
                  </div>
                </div>
              ) : null}

              {isAdvancedPcb ? (
                <>
                  <div className="flex flex-col gap-4 border-b border-gray-50 pb-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="PCB Type" help="Choose through-hole board for normal advanced PCB manufacturing, or HDI when buried/blind vias are required." alignTop />
                    <div className="flex-grow">
                      <SelectableGroup
                        name="advancedPcbType"
                        value={formValues.pcbSpec.advancedPcbType}
                        options={ADVANCED_PCB_TYPE_OPTIONS}
                        onChange={handleAdvancedPcbBaseChange}
                        disabled={isBusy || isConfigLoading}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 border-b border-gray-50 pb-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="BoardSpec" help="Select the manufacturing quality standard required for this Advanced PCB order." alignTop />
                    <div className="flex-grow">
                      <SelectableGroup
                        name="advancedBoardSpec"
                        value={formValues.pcbSpec.advancedBoardSpec}
                        options={ADVANCED_BOARD_SPEC_OPTIONS}
                        onChange={handleAdvancedPcbBaseChange}
                        disabled={isBusy || isConfigLoading}
                      />
                    </div>
                  </div>
                </>
              ) : null}

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isAdvancedPcb ? "Board type :" : "Board Type"} help={STANDARD_PCB_HELP_TEXT.boardType} image={STANDARD_PCB_HELP_IMAGES.boardType} alignTop />
                <div className="flex-grow">
                  <SelectableGroup
                    name="boardType"
                    value={formValues.pcbSpec.boardType}
                    options={isFpcRigidFlex ? FPC_BOARD_TYPE_OPTIONS : standardPcbConfig.boardTypes || []}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={isFpcRigidFlex ? "quote-pcbway-options quote-fpc-main-options" : ""}
                    buttonClassName={isFpcRigidFlex ? "quote-pcbway-option" : ""}
                  />
                </div>
              </div>

              {!isAdvancedPcb && !isFpcRigidFlex && formValues.pcbSpec.boardType !== "single_pieces" ? (
                <div className="space-y-4 border-b border-gray-100 pb-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="Panel requirements:" help="Describe the panel array, break-away rail size and other panelization requirements." alignTop />
                    <textarea
                      name="panelRequirements"
                      rows="2"
                      value={formValues.pcbSpec.panelRequirements}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                      className="min-w-0 flex-grow border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-600"
                      placeholder="e.g. Panel in 2×3, break-away rail size and total panel quantity"
                    />
                  </div>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="Break-away rail:" help="Choose whether the supplied panel includes break-away rails." alignTop />
                    <SelectableGroup
                      name="breakAwayRail"
                      value={formValues.pcbSpec.breakAwayRail}
                      options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="Route Process:" help="Choose the preferred panel separation process." alignTop />
                    <SelectableGroup
                      name="routeProcess"
                      value={formValues.pcbSpec.routeProcess}
                      options={PANEL_ROUTE_PROCESS_OPTIONS}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <QuoteFieldLabel label="X-out Allowance in Panel:" help="Choose whether defective individual boards are acceptable in a supplied panel." alignTop />
                    <SelectableGroup
                      name="xOutAllowance"
                      value={formValues.pcbSpec.xOutAllowance}
                      options={[{ value: "accept", label: "Accept" }, { value: "not_accept", label: "Not Accept" }]}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col md:flex-row md:items-center gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="Different design in panel :" help={STANDARD_PCB_HELP_TEXT.differentDesignCount} />
                <div className="flex-grow flex flex-wrap items-center gap-2">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => handlePcbSpecChange({ target: { name: "differentDesignCount", value: num } })}
                      className={`px-4 py-1.5 border rounded-md text-sm transition-all ${
                        Number(formValues.pcbSpec.differentDesignCount) === num
                          ? "border-green-600 bg-white ring-1 ring-green-600"
                          : "border-gray-300 bg-white hover:border-green-400"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                  <div className="flex items-center gap-2 ml-2">
                    <input
                      type="number"
                      name="differentDesignCount"
                      min="1"
                      placeholder="Custom"
                      value={[1, 2, 3, 4, 5, 6].includes(Number(formValues.pcbSpec.differentDesignCount)) ? "" : formValues.pcbSpec.differentDesignCount}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                      className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-1 focus:ring-green-500 outline-none"
                    />
                    <DifferentDesignExample />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="* Size ( single ):" help={STANDARD_PCB_HELP_TEXT.size} image={STANDARD_PCB_HELP_IMAGES.size} />
                <div className="flex-grow flex flex-wrap items-center gap-3">
                  <div className="flex items-center bg-orange-50 rounded-md border border-orange-100 overflow-hidden">
                    <input
                      type="number"
                      name="length"
                      min="1"
                      step="1"
                      placeholder="Length"
                      value={formValues.pcbSpec.size.length}
                      onChange={handleSizeChange}
                      disabled={isBusy || isConfigLoading}
                      className="w-24 bg-transparent px-3 py-2 text-sm focus:outline-none"
                    />
                    <span className="px-2 text-gray-400 bg-white h-full flex items-center">X</span>
                    <input
                      type="number"
                      name="width"
                      min="1"
                      step="1"
                      placeholder="Width"
                      value={formValues.pcbSpec.size.width}
                      onChange={handleSizeChange}
                      disabled={isBusy || isConfigLoading}
                      className="w-24 bg-transparent px-3 py-2 text-sm focus:outline-none"
                    />
                    <span className="px-3 py-2 bg-gray-100 text-gray-600 text-sm border-l border-orange-100">mm</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenInchConversion}
                    disabled={isBusy || isConfigLoading}
                    className="quote-size-unit-toggle border border-gray-300 bg-white px-2.5 text-sm text-gray-700 shadow-sm hover:border-green-500 hover:text-green-700 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    inch'↔mm
                  </button>
                  <img
                    src="/img/xy-sizepanel.png"
                    alt="Single PCB size and panel size measurement reference"
                    className="h-auto w-[153px] flex-none object-contain"
                  />
                </div>

                {showInchConversion ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="inch-conversion-title"
                  >
                    <div className="w-full max-w-xl border border-gray-300 bg-white shadow-xl">
                      <div className="flex items-center justify-between border-b border-gray-300 bg-gray-50 px-4 py-3">
                        <h4 id="inch-conversion-title" className="text-base font-bold text-gray-700">
                          Inch ⇒ mm Conversion
                        </h4>
                        <button
                          type="button"
                          onClick={() => setShowInchConversion(false)}
                          aria-label="Close inch conversion"
                          className="flex h-7 w-7 items-center justify-center border-0 bg-transparent p-0 text-xl font-bold text-gray-500 hover:text-gray-800"
                        >
                          ×
                        </button>
                      </div>
                      <div className="p-6">
                        <p className="mb-7 text-sm leading-6 text-gray-700">
                          Enter dimensions in inches and click Submit. The converted millimeter values will be filled into the quotation form.
                        </p>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                          <label className="flex items-center gap-1 text-sm text-gray-800">
                            X=
                            <input
                              type="number"
                              name="length"
                              min="0.01"
                              step="0.01"
                              value={inchSize.length}
                              onChange={handleInchSizeChange}
                              className="w-28 border border-gray-400 px-2 py-1 outline-none focus:border-green-600"
                            />
                            inch
                          </label>
                          <label className="flex items-center gap-1 text-sm text-gray-800">
                            Y=
                            <input
                              type="number"
                              name="width"
                              min="0.01"
                              step="0.01"
                              value={inchSize.width}
                              onChange={handleInchSizeChange}
                              className="w-28 border border-gray-400 px-2 py-1 outline-none focus:border-green-600"
                            />
                            inch
                          </label>
                        </div>
                        <div className="mt-7 flex justify-end">
                          <button
                            type="button"
                            onClick={handleInchConversionSubmit}
                            className="border border-gray-400 bg-gray-100 px-8 py-2 text-sm text-gray-800 hover:bg-gray-200"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="* Quantity ( single ):" help={STANDARD_PCB_HELP_TEXT.quantity} image={STANDARD_PCB_HELP_IMAGES.quantity} />
                <div className="flex-grow flex flex-wrap items-center gap-2">
                  <select
                    name="quantity"
                    value={STANDARD_QUANTITY_OPTIONS.includes(Number(formValues.pcbSpec.quantity)) ? formValues.pcbSpec.quantity : "other"}
                    onChange={(event) => handlePcbSpecChange({
                      target: {
                        name: "quantity",
                        value: event.target.value === "other" ? 1 : event.target.value,
                      },
                    })}
                    disabled={isBusy || isConfigLoading}
                    className="w-32 border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-600"
                  >
                    {STANDARD_QUANTITY_OPTIONS.map((quantity) => (
                      <option key={quantity} value={quantity}>{quantity}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {!STANDARD_QUANTITY_OPTIONS.includes(Number(formValues.pcbSpec.quantity)) ? (
                    <input
                      type="number"
                      name="quantity"
                      min="1"
                      value={formValues.pcbSpec.quantity}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                      className="w-32 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-600"
                      aria-label="Custom PCB quantity"
                    />
                  ) : null}
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-md border border-gray-200">pcs</span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="Layers" help={STANDARD_PCB_HELP_TEXT.layers} image={STANDARD_PCB_HELP_IMAGES.layers} alignTop />
                <div className="min-w-0 flex-grow space-y-3">
                  <SelectableGroup
                    name="layers"
                    value={formValues.pcbSpec.layers}
                    options={(isAdvancedPcb
                      ? ADVANCED_LAYER_OPTIONS
                      : isFpcRigidFlex
                      ? FPC_LAYER_OPTIONS
                      : standardPcbConfig.layers || []
                    ).map(l => ({
                      value: l,
                      label: isAdvancedPcb
                        ? l
                        : `${l} Layer${l > 1 ? "s" : ""}`,
                    }))}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={
                      isAdvancedPcb
                        ? "quote-advanced-layer-options"
                        : isFpcRigidFlex
                        ? "quote-pcbway-options quote-fpc-layer-options"
                        : ""
                    }
                    buttonClassName={
                      isAdvancedPcb
                        ? "quote-advanced-layer-option justify-center"
                        : isFpcRigidFlex
                        ? "quote-pcbway-option justify-center"
                        : ""
                    }
                  />
                  {isFpcRigidFlex ? (
                    <a
                      href="/pcb_prototype/Stack_up_for_FPC.html"
                      className="inline-block text-sm font-bold text-green-600 hover:text-green-700 hover:underline"
                    >
                      Stack-up for FPC
                    </a>
                  ) : null}
                  {Number(formValues.pcbSpec.layers) === 1 && !isFpcRigidFlex ? (
                    <div className="grid grid-cols-1 gap-3 pt-1 lg:grid-cols-3">
                      <label className="flex items-center gap-2">
                        <span className="flex min-w-[110px] items-center gap-1 text-sm font-medium text-gray-600">
                          Copper layer
                          <HelpTooltip
                            content={STANDARD_PCB_HELP_TEXT.copperLayerSide}
                            label="Copper layer"
                          />
                        </span>
                        <select
                          name="copperLayerSide"
                          value={formValues.pcbSpec.copperLayerSide}
                          onChange={handlePcbSpecChange}
                          disabled={isBusy || isConfigLoading}
                          className="min-w-0 flex-1 border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-600"
                        >
                          <option value="">--</option>
                          <option value="top">Top layer</option>
                          <option value="bottom">Bottom layer</option>
                          <option value="none">None</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-2">
                        <span className="flex min-w-[100px] items-center gap-1 text-sm font-medium text-gray-600">
                          Soldermask
                          <HelpTooltip
                            content={STANDARD_PCB_HELP_TEXT.solderMaskSide}
                            label="Soldermask side"
                          />
                        </span>
                        <select
                          name="solderMaskSide"
                          value={formValues.pcbSpec.solderMaskSide}
                          onChange={handlePcbSpecChange}
                          disabled={isBusy || isConfigLoading}
                          className="min-w-0 flex-1 border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-600"
                        >
                          <option value="">--</option>
                          <option value="top">Top side</option>
                          <option value="bottom">Bottom side</option>
                          <option value="both">Both sides</option>
                          <option value="none">None</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-2">
                        <span className="flex min-w-[100px] items-center gap-1 text-sm font-medium text-gray-600">
                          Silkscreen
                          <HelpTooltip
                            content={STANDARD_PCB_HELP_TEXT.silkscreenSide}
                            label="Silkscreen side"
                          />
                        </span>
                        <select
                          name="silkscreenSide"
                          value={formValues.pcbSpec.silkscreenSide}
                          onChange={handlePcbSpecChange}
                          disabled={isBusy || isConfigLoading}
                          className="min-w-0 flex-1 border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-600"
                        >
                          <option value="">--</option>
                          <option value="top">Top side</option>
                          <option value="bottom">Bottom side</option>
                          <option value="both">Both sides</option>
                          <option value="none">None</option>
                        </select>
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isFpcRigidFlex ? "Polyimide base material:" : "Material:"} help={STANDARD_PCB_HELP_TEXT.material} image={STANDARD_PCB_HELP_IMAGES.material} alignTop />
                <div className="min-w-0 flex-grow space-y-3">
                  <SelectableGroup
                    name="material"
                    value={formValues.pcbSpec.material}
                    options={(isAdvancedPcb
                      ? ADVANCED_MATERIAL_OPTIONS
                      : isFpcRigidFlex
                      ? FPC_BASE_MATERIAL_OPTIONS
                      : standardPcbConfig.materials || []
                    ).map(m => {
                      const mValue = typeof m === 'object' ? m.value : m;
                      const mLabel = typeof m === 'object' ? m.label : m;
                      return {
                        ...(typeof m === "object" ? m : {}),
                        value: mValue,
                        label: mLabel,
                        icon: isAdvancedPcb ? null : PCB_MATERIAL_IMAGES[mValue],
                        iconClassName: isAdvancedPcb
                          ? null
                          : "h-6 w-10 flex-none object-contain",
                      };
                    })}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={
                      isAdvancedPcb || isFpcRigidFlex
                        ? "quote-pcbway-options"
                        : "flex-nowrap overflow-x-auto pb-1"
                    }
                    buttonClassName={
                      isAdvancedPcb || isFpcRigidFlex
                        ? "quote-pcbway-option"
                        : "flex-none whitespace-nowrap px-2 py-1 text-xs"
                    }
                  />
                  {isAdvancedPcb ? (
                    renderAdvancedMaterialHint(formValues.pcbSpec.material)
                  ) : isFpcRigidFlex ? null : (
                    <p className="text-xs leading-5 text-amber-600">
                      *Material model can be remarked below. HDI is available for 4-layer or more.
                    </p>
                  )}
                </div>
              </div>

              {!isAdvancedPcb && !isFpcRigidFlex ? (
              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="FR4-TG" help={STANDARD_PCB_HELP_TEXT.fr4Tg} image={STANDARD_PCB_HELP_IMAGES.fr4Tg} alignTop />
                <div className="flex-grow space-y-2">
                  <SelectableGroup
                    name="fr4Tg"
                    value={formValues.pcbSpec.fr4Tg}
                    options={(standardPcbConfig.fr4TgOptions || []).map((option) => {
                      const optionValue =
                        typeof option === "object" ? option.value : option;

                      return {
                        ...(typeof option === "object"
                          ? option
                          : { value: optionValue }),
                        label:
                          FR4_TG_LABELS[optionValue] ||
                          (typeof option === "object"
                            ? option.label
                            : optionValue),
                      };
                    })}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                  />
                  {getFr4TgHint(
                    formValues.pcbSpec.fr4Tg,
                    formValues.pcbSpec.layers
                  ) ? (
                    <p className="text-xs leading-5 text-amber-600">
                      *
                      {getFr4TgHint(
                        formValues.pcbSpec.fr4Tg,
                        formValues.pcbSpec.layers
                      )}
                    </p>
                  ) : null}
                </div>
              </div>
              ) : null}

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isFpcRigidFlex ? "FPC Thickness:" : isAdvancedPcb ? "Thickness:" : "Thickness (mm)"} help={STANDARD_PCB_HELP_TEXT.thickness} image={STANDARD_PCB_HELP_IMAGES.thickness} alignTop />
                <div className="min-w-0 flex-grow space-y-3">
                  <SelectableGroup
                    name="thickness"
                    value={formValues.pcbSpec.thickness}
                    options={(isAdvancedPcb
                      ? ADVANCED_THICKNESS_OPTIONS
                      : isFpcRigidFlex
                      ? FPC_THICKNESS_OPTIONS
                      : TARGET_THICKNESS_OPTIONS
                    ).map((option) => {
                      const optionValue =
                        typeof option === "object" ? option.value : option;
                      const optionLabel =
                        typeof option === "object"
                          ? option.label
                          : isAdvancedPcb && Number.isInteger(option)
                          ? option.toFixed(1)
                          : Number.isInteger(option)
                          ? option.toFixed(1)
                          : String(option);

                      return {
                        ...(typeof option === "object" ? option : {}),
                        value: optionValue,
                        label: optionLabel,
                        disabled: getOptionRestriction(
                          "thickness",
                          formValues.pcbSpec
                        )(optionValue),
                      };
                    })}
                    onChange={handleThicknessChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={
                      isAdvancedPcb
                        ? "quote-advanced-thickness-options"
                        : isFpcRigidFlex
                        ? "quote-fpc-thickness-options"
                        : "quote-thickness-options"
                    }
                    buttonClassName={
                      isAdvancedPcb
                        ? "quote-pcbway-option quote-advanced-thickness-option justify-center"
                        : isFpcRigidFlex
                        ? "quote-pcbway-option quote-fpc-thickness-option justify-center"
                        : "quote-thickness-option justify-center whitespace-nowrap"
                    }
                    trailingContent={
                      isAdvancedPcb ? (
                        <>
                          <button
                            type="button"
                            onClick={handleOpenAdvancedCustomThickness}
                            disabled={isBusy || isConfigLoading}
                            className={`quote-pcbway-option quote-advanced-thickness-option quote-advanced-thickness-wide-option quote-advanced-thickness-other-button group relative flex items-center justify-center gap-2 border bg-white px-3 py-1.5 text-sm transition-all ${
                              showCustomThickness
                                ? "border-[#b9a175] text-gray-900 shadow-[inset_0_0_0_1px_#b9a175]"
                                : "border-[#dbe4ef] text-[#526f8e] hover:border-green-400 hover:text-green-600"
                            } ${isBusy || isConfigLoading ? "cursor-not-allowed opacity-40 grayscale" : ""}`}
                          >
                            <span>Other (reviewed)</span>
                            {showCustomThickness ? (
                              <div className="absolute bottom-0 right-0 flex h-3 w-3 items-center justify-center overflow-hidden rounded-tl-sm bg-[#b9a175]">
                                <svg
                                  className="h-2.5 w-2.5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            ) : null}
                          </button>
                          <input
                            type="number"
                            name="thickness"
                            min="0.1"
                            step="0.1"
                            value={
                              showCustomThickness
                                ? getAdvancedCustomThicknessValue(formValues.pcbSpec.thickness)
                                : ""
                            }
                            onChange={handlePcbSpecChange}
                            onFocus={handleOpenAdvancedCustomThickness}
                            disabled={isBusy || isConfigLoading}
                            className="quote-advanced-thickness-input border border-[#dbe4ef] bg-white px-3 text-sm text-gray-700 outline-none focus:border-[#00a0e9] focus:ring-1 focus:ring-[#00a0e9]"
                            aria-label="Custom advanced PCB thickness in millimeters"
                          />
                          <span className="quote-advanced-thickness-unit">
                            <span>* Unit: mm</span>
                            <PcbThicknessIllustration />
                          </span>
                        </>
                      ) : null
                    }
                  />
                  {getThicknessHint(
                    formValues.pcbSpec.thickness,
                    formValues.pcbSpec.material,
                    formValues.pcbSpec.layers
                  ) ? (
                    <p className="text-xs leading-5 text-amber-600">
                      *
                      {getThicknessHint(
                        formValues.pcbSpec.thickness,
                        formValues.pcbSpec.material,
                        formValues.pcbSpec.layers
                      )}
                    </p>
                  ) : null}
                  {!isAdvancedPcb && !isFpcRigidFlex ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleOpenCustomThickness}
                      disabled={isBusy || isConfigLoading}
                      className={`border px-3 py-1.5 text-sm transition-colors ${
                        showCustomThickness
                          ? "border-green-600 text-gray-900 ring-1 ring-green-600"
                          : "border-gray-300 bg-white text-gray-700 hover:border-green-500"
                      }`}
                    >
                      ≥1.7-8.0
                    </button>
                    {showCustomThickness ? (
                      <input
                        type="number"
                        name="thickness"
                        min="1.7"
                        max="8"
                        step="0.1"
                        value={formValues.pcbSpec.thickness}
                        onChange={handlePcbSpecChange}
                        disabled={isBusy || isConfigLoading}
                        className="w-24 border border-gray-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-green-600"
                        aria-label="Custom PCB thickness in millimeters"
                      />
                    ) : null}
                    <span className="text-sm text-gray-500">* Unit: mm</span>
                    <PcbThicknessIllustration />
                  </div>
                  ) : null}
                  {isFpcRigidFlex ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs leading-5 text-amber-600">
                      <span className="text-gray-500">* Unit: mm</span>
                      <PcbThicknessIllustration />
                      <span>*The FPC thickness does not include the thickness of stiffener and 3M tape.</span>
                    </div>
                  ) : null}
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isAdvancedPcb || isFpcRigidFlex ? "Min track/spacing:" : "Min Track / Spacing"} help={STANDARD_PCB_HELP_TEXT.minTrackSpacing} image={STANDARD_PCB_HELP_IMAGES.minTrackSpacing} alignTop />
                <div className="flex flex-grow flex-wrap items-center gap-4">
                  <SelectableGroup
                    name="minTrackSpacing"
                    value={formValues.pcbSpec.minTrackSpacing}
                    options={(isAdvancedPcb
                      ? ADVANCED_TRACK_SPACING_OPTIONS
                      : isFpcRigidFlex
                      ? FPC_TRACK_SPACING_OPTIONS
                      : standardPcbConfig.trackSpacingOptions || []
                    ).map(opt => {
                      const optionValue = typeof opt === "object" ? opt.value : opt;
                      return {
                        ...(typeof opt === 'object' ? opt : { value: opt, label: opt }),
                        disabled: getOptionRestriction("minTrackSpacing", formValues.pcbSpec)(optionValue)
                      };
                    })}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={
                      isAdvancedPcb || isFpcRigidFlex
                        ? "quote-pcbway-options quote-pcbway-inline-options"
                        : ""
                    }
                    buttonClassName={isAdvancedPcb || isFpcRigidFlex ? "quote-pcbway-option justify-center" : ""}
                  />
                  <img
                    src="/img/track.png"
                    alt="PCB minimum track and spacing measurement illustration"
                    className="quote-track-example-image h-auto flex-none object-contain"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isAdvancedPcb ? "Min hole size:" : "Min Hole Size"} help={STANDARD_PCB_HELP_TEXT.minHoleSize} image={STANDARD_PCB_HELP_IMAGES.minHoleSize} alignTop />
                <div className="flex flex-grow flex-wrap items-center gap-4">
                  <SelectableGroup
                    name="minHoleSize"
                    value={formValues.pcbSpec.minHoleSize}
                    options={isAdvancedPcb ? ADVANCED_HOLE_SIZE_OPTIONS : standardPcbConfig.holeSizeOptions || []}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={isAdvancedPcb ? "quote-pcbway-options quote-pcbway-inline-options" : ""}
                    buttonClassName={isAdvancedPcb ? "quote-pcbway-option justify-center" : ""}
                  />
                  <img
                    src="/img/hole.png"
                    alt="PCB minimum hole size measurement illustration"
                    className="quote-hole-example-image h-auto flex-none object-contain"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isAdvancedPcb ? "Solder mask:" : "Solder mask"} help={STANDARD_PCB_HELP_TEXT.solderMask} image={STANDARD_PCB_HELP_IMAGES.solderMask} alignTop />
                <div className="flex-grow">
                  <SelectableGroup
                    name="solderMask"
                    value={formValues.pcbSpec.solderMask}
                    options={addColorSwatches(
                      isAdvancedPcb
                        ? ADVANCED_SOLDER_MASK_OPTIONS
                        : standardPcbConfig.solderMaskOptions,
                      SOLDER_MASK_SWATCHES
                    )}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={isAdvancedPcb ? "quote-pcbway-options" : ""}
                    buttonClassName={isAdvancedPcb ? "quote-pcbway-option quote-pcbway-color-option quote-solder-mask-option" : ""}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label={isAdvancedPcb ? "Silkscreen:" : "Silkscreen"} help={STANDARD_PCB_HELP_TEXT.silkscreen} image={STANDARD_PCB_HELP_IMAGES.silkscreen} alignTop />
                <div className="flex-grow">
                  <SelectableGroup
                    name="silkscreen"
                    value={formValues.pcbSpec.silkscreen}
                    options={addColorSwatches(
                      isAdvancedPcb
                        ? ADVANCED_SILKSCREEN_OPTIONS
                        : standardPcbConfig.silkscreenOptions,
                      SILKSCREEN_SWATCHES
                    )}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={isAdvancedPcb ? "quote-pcbway-options" : ""}
                    buttonClassName={isAdvancedPcb ? "quote-pcbway-option quote-pcbway-color-option" : ""}
                  />
                </div>
              </div>

              {!isAdvancedPcb && !isFpcRigidFlex ? (
              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="UV Printing Multi-color" help={STANDARD_PCB_HELP_TEXT.uvPrinting} image={STANDARD_PCB_HELP_IMAGES.uvPrinting} alignTop />
                <div className="flex-grow space-y-2">
                  <SelectableGroup
                    name="uvPrinting"
                    value={formValues.pcbSpec.uvPrinting}
                    options={standardPcbConfig.uvPrintingOptions || []}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                  />
                  <p className="text-xs leading-5 text-amber-600">
                    *The uploaded PCB file should contain color renderings,{" "}
                    <a
                      href="#quote-gerber-upload"
                      className="font-medium text-green-600 hover:text-green-700 hover:underline"
                    >
                      learn more &gt;&gt;
                    </a>
                  </p>
                </div>
              </div>
              ) : null}

              {!isFpcRigidFlex ? (
              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="Edge connector:" help={STANDARD_PCB_HELP_TEXT.edgeConnector} image={STANDARD_PCB_HELP_IMAGES.edgeConnector} alignTop />
                <div className="flex-grow space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <SelectableGroup
                      name="edgeConnector"
                      value={formValues.pcbSpec.edgeConnector}
                      options={[
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                      ]}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                      groupClassName={isAdvancedPcb ? "quote-pcbway-options" : ""}
                      buttonClassName={isAdvancedPcb ? "quote-pcbway-option justify-center" : ""}
                    />
                    {formValues.pcbSpec.edgeConnector === "yes" ? (
                      <div className="flex items-center gap-2">
                        <label htmlFor="edgeConnectorBevelling" className="text-sm text-gray-600">
                          Bevelling:
                        </label>
                        <select
                          id="edgeConnectorBevelling"
                          name="edgeConnectorBevelling"
                          value={formValues.pcbSpec.edgeConnectorBevelling}
                          onChange={handlePcbSpecChange}
                          disabled={isBusy || isConfigLoading}
                          className="min-h-[30px] border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-green-600 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {(standardPcbConfig.edgeConnectorBevellingOptions || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <img
                          src="/img/chamfering.png"
                          alt="Bevelling angle"
                          className="h-8 w-auto flex-none object-contain"
                        />
                      </div>
                    ) : null}
                  </div>
                  {formValues.pcbSpec.edgeConnector === "yes" ? (
                    <div className="space-y-2">
                      <SelectableGroup
                        name="edgeConnectorSurfaceFinish"
                        value={formValues.pcbSpec.edgeConnectorSurfaceFinish}
                        options={EDGE_CONNECTOR_SURFACE_FINISH_OPTIONS}
                        onChange={handlePcbSpecChange}
                        disabled={isBusy || isConfigLoading}
                      />
                      <p className="text-xs text-amber-600">
                        *The above options are only Edge connector Surface Finish
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              ) : null}

              {formValues.pcbSpec.edgeConnector === "yes" &&
              formValues.pcbSpec.edgeConnectorSurfaceFinish === "enig" &&
              !isFpcRigidFlex ? (
                <div className="flex flex-col gap-4 border-b border-gray-50 pb-4 md:flex-row md:items-start">
                  <QuoteFieldLabel label="Thickness of Immersion Gold:" help="Choose the immersion gold thickness for the edge connector contacts." alignTop />
                  <div className="flex-grow">
                    <SelectableGroup
                      name="edgeConnectorImmersionGoldThickness"
                      value={formValues.pcbSpec.edgeConnectorImmersionGoldThickness}
                      options={['1U"', '2U"', '3U"']}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                </div>
              ) : null}

              {formValues.pcbSpec.edgeConnector === "yes" &&
              formValues.pcbSpec.edgeConnectorSurfaceFinish === "hard_gold" &&
              !isFpcRigidFlex ? (
                <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                  <QuoteFieldLabel label="Au/Ni thickness:" help="Choose the gold and nickel thickness for the edge connector contacts." alignTop />
                  <div className="flex flex-grow flex-wrap items-start gap-2">
                    <div className="relative border border-green-600 bg-white px-3 py-1.5 text-sm text-gray-900 ring-1 ring-green-600">
                      {formValues.pcbSpec.edgeConnectorGoldNickelThickness || formValues.pcbSpec.edgeConnectorCustomGoldThickness || 'Au:10U"/Ni:120U"'}
                      <span className="absolute bottom-0 right-0 flex h-3 w-3 items-center justify-center overflow-hidden rounded-tl-sm bg-green-600 text-[8px] leading-none text-white">
                        ✓
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenGoldThicknessModal}
                      disabled={isBusy || isConfigLoading}
                      className="min-h-[34px] border border-gray-300 bg-gradient-to-b from-white to-gray-100 px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:border-green-500 hover:text-green-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Please choose other options of Gold /Nickel thickness here
                    </button>
                  </div>
                </div>
              ) : null}

              {formValues.pcbSpec.edgeConnector === "yes" &&
              formValues.pcbSpec.edgeConnectorSurfaceFinish === "enepig" &&
              !isFpcRigidFlex ? (
                <div className="flex flex-col gap-4 border-b border-gray-50 pb-4 md:flex-row md:items-start">
                  <QuoteFieldLabel label="Thickness of ENEPIG:" help="Choose the nickel, palladium and gold thickness for the edge connector contacts." alignTop />
                  <div className="flex-grow">
                    <SelectableGroup
                      name="edgeConnectorEnepigThickness"
                      value={formValues.pcbSpec.edgeConnectorEnepigThickness}
                      options={['Ni:200U" Pd:2U" Au:2U"']}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                </div>
              ) : null}

              {showGoldThicknessModal ? (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="gold-thickness-title"
                >
                  <div className="w-full max-w-[505px] border border-gray-300 bg-white shadow-xl">
                    <div className="flex items-center justify-between border-b border-gray-300 bg-gray-50 px-4 py-3">
                      <h4 id="gold-thickness-title" className="text-sm font-bold text-gray-700">
                        Please choose other options of Gold /Nickel thickness here
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowGoldThicknessModal(false)}
                        aria-label="Close Gold and Nickel thickness selection"
                        className="flex h-7 w-7 items-center justify-center border-0 bg-transparent p-0 text-xl font-bold text-gray-500 hover:text-gray-800"
                      >
                        ×
                      </button>
                    </div>
                    <div className="space-y-2 p-5">
                      {EDGE_CONNECTOR_GOLD_THICKNESS_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleGoldThicknessPresetChange(option.value)}
                          className={`relative block min-w-[150px] border px-3 py-1.5 text-left text-sm ${
                            goldThicknessDraft.selection === option.value
                              ? "border-green-600 text-gray-900 ring-1 ring-green-600"
                              : "border-gray-300 text-gray-700 hover:border-green-400"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                      <div
                        className={`flex flex-wrap items-center gap-1 border px-3 py-2 text-sm ${
                          goldThicknessDraft.selection === "custom"
                            ? "border-green-600 ring-1 ring-green-600"
                            : "border-gray-300"
                        }`}
                      >
                        <span>Please Enter | Au:</span>
                        <input
                          type="text"
                          name="au"
                          value={goldThicknessDraft.au}
                          onChange={handleGoldThicknessInputChange}
                          aria-label="Custom Au thickness"
                          className="w-16 border border-gray-400 px-1 py-0.5 outline-none focus:border-green-600"
                        />
                        <span>U&quot;/Ni:</span>
                        <input
                          type="text"
                          name="ni"
                          value={goldThicknessDraft.ni}
                          onChange={handleGoldThicknessInputChange}
                          aria-label="Custom Ni thickness"
                          className="w-16 border border-gray-400 px-1 py-0.5 outline-none focus:border-green-600"
                        />
                        <span>U&quot;</span>
                      </div>
                      <div className="flex justify-center pt-4">
                        <button
                          type="button"
                          onClick={handleGoldThicknessSubmit}
                          className="border border-green-700 bg-green-600 px-8 py-2 text-sm font-semibold text-white hover:bg-green-700"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="Surface finish:" help={STANDARD_PCB_HELP_TEXT.surfaceFinish} image={STANDARD_PCB_HELP_IMAGES.surfaceFinish} alignTop />
                <div className="min-w-0 flex-grow space-y-3">
                  <SelectableGroup
                    name="surfaceFinish"
                    value={formValues.pcbSpec.surfaceFinish}
                    options={SURFACE_FINISH_OPTIONS}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    groupClassName={isAdvancedPcb ? "quote-pcbway-options" : ""}
                    buttonClassName={isAdvancedPcb ? "quote-pcbway-option" : ""}
                  />
                  {["hasl_with_lead", "hasl_lead_free"].includes(formValues.pcbSpec.surfaceFinish) ? (
                    <div className="pt-1">
                      <SelectableGroup
                        name="acceptHaslToEnigSubstitution"
                        value={formValues.pcbSpec.acceptHaslToEnigSubstitution}
                        options={[
                          { value: true, label: "Accept HASL to ENIG substitution" },
                          { value: false, label: "Do not accept substitution" },
                        ]}
                        onChange={handlePcbSpecChange}
                        disabled={isBusy || isConfigLoading}
                      />
                      <p className="mt-2 text-xs leading-5 text-amber-600">
                        *Accept means you allow us to change HASL to ENIG at our discretion without extra charge.
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              {renderSurfaceFinishDetail(
                formValues.pcbSpec.surfaceFinish,
                formValues.pcbSpec.surfaceFinishDetail,
                handleSurfaceFinishDetailChange,
                isBusy || isConfigLoading
              )}

              <div className="flex flex-col md:flex-row md:items-start gap-4 border-b border-gray-50 pb-4">
                <QuoteFieldLabel label="Finished Copper" help={STANDARD_PCB_HELP_TEXT.finishedCopper} image={STANDARD_PCB_HELP_IMAGES.finishedCopper} alignTop />
                <div className="flex-grow">
                  <SelectableGroup
                    name="finishedCopper"
                    value={formValues.pcbSpec.finishedCopper}
                    options={formatCopperOptions(standardPcbConfig.copperOptions).map(opt => ({
                      ...opt,
                      disabled: getOptionRestriction("finishedCopper", formValues.pcbSpec)(opt)
                    }))}
                    onChange={handlePcbSpecChange}
                    disabled={isBusy || isConfigLoading}
                    trailingContent={(
                      <img
                        src="/img/copper.png"
                        alt="Finished copper thickness illustration"
                        className="h-auto w-auto flex-none self-center object-contain"
                      />
                    )}
                  />
                </div>
              </div>

              {!isAdvancedPcb && !isFpcRigidFlex && Number(formValues.pcbSpec.layers) >= 4 ? (
                <div className="flex flex-col gap-4 border-b border-gray-50 pb-4 md:flex-row md:items-start">
                  <QuoteFieldLabel label="Inner Copper:" help="Choose the copper weight used on the inner layers of a multilayer PCB." alignTop />
                  <div className="flex-grow">
                    <SelectableGroup
                      name="innerCopper"
                      value={formValues.pcbSpec.innerCopper}
                      options={INNER_COPPER_OPTIONS}
                      onChange={handlePcbSpecChange}
                      disabled={isBusy || isConfigLoading}
                    />
                  </div>
                </div>
              ) : null}

            </div>
            ) : null}
          </section>
          ) : (
            null
          )}

          {isAssemblyQuote ? (
            renderStandaloneAssemblySpec()
          ) : isSmdStencilQuote ? (
            renderStandaloneSmdStencilSpec()
          ) : isCnc3dQuote ? (
            renderCnc3dSpec()
          ) : !isPcbQuoteForm ? (
          <section className="quote-section-card border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              {quoteCategories.find((item) => item.value === formValues.quoteType)?.label} Specification
            </h3>
            <div className="grid grid-cols-1 gap-6">
              {(formValues.spec?.size || null) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">Length (mm)</label>
                    <input
                      type="number"
                      name="length"
                      min="0"
                      value={formValues.spec.size?.length || ""}
                      onChange={handleGenericSizeChange}
                      className="w-full max-w-[200px] rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">Width (mm)</label>
                    <input
                      type="number"
                      name="width"
                      min="0"
                      value={formValues.spec.size?.width || ""}
                      onChange={handleGenericSizeChange}
                      className="w-full max-w-[200px] rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  {formValues.spec.size?.height !== undefined ? (
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-gray-700">Height (mm)</label>
                      <input
                        type="number"
                        name="height"
                        min="0"
                        value={formValues.spec.size?.height || ""}
                        onChange={handleGenericSizeChange}
                        className="w-full max-w-[200px] rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {(GENERIC_SPEC_FIELDS[formValues.quoteType] || []).map((field) => (
                <div key={field.name} className="space-y-2">
                  {field.type !== "checkbox" ? (
                    <label className="block text-sm font-bold text-gray-700">
                      {field.label}
                    </label>
                  ) : null}
                  {renderGenericField(field)}
                </div>
              ))}
            </div>
          </section>
          ) : null}

          {!isAssemblyQuote && !isSmdStencilQuote && !isCnc3dQuote ? (
          <section className="quote-section-card quote-attachment-card flex flex-col border border-gray-200 bg-white p-4">
            {!isPcbQuoteForm ? (
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-green-600 rounded-full"></span>
                Attachments and RFQ Notes
              </h3>
            ) : null}
            
            {isPcbQuoteForm ? (
              <div className="order-2">
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions((isVisible) => !isVisible)}
                  aria-expanded={showAdvancedOptions}
                  className="flex w-full items-center justify-between gap-4 bg-gray-100 px-4 py-3 text-left hover:bg-gray-200"
                >
                  <span className="flex min-w-0 items-center gap-2 text-sm font-bold text-gray-800 md:text-base">
                    <span className="w-4 flex-none text-center text-xl leading-none" aria-hidden="true">
                      {showAdvancedOptions ? "−" : "+"}
                    </span>
                    <span>
                      Customized Services and Advanced Options
                      <span className="ml-1 font-normal text-amber-600">
                        (Castellated holes,Edge plating,Impedance control...)
                      </span>
                    </span>
                  </span>
                  <span className="flex flex-none items-center gap-1 text-sm font-bold text-gray-800">
                    {showAdvancedOptions ? "Hide" : "More"}
                    <FiChevronDown
                      className={`h-4 w-4 transition-transform ${showAdvancedOptions ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </span>
                </button>

                {showAdvancedOptions ? (
                  <div className="border border-t-0 border-gray-200 px-2 py-4 md:px-4">
                    <p className="mb-4 text-sm text-gray-400">
                      We may add extra cost for these special options which will be confirmed after review.
                    </p>

                    <div className="quote-advanced-select-grid grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
                      {ADVANCED_SELECT_FIELDS.map((field) => (
                        <label key={field.name} className="quote-advanced-select-field flex items-center gap-3 text-sm text-gray-600">
                          <span className="quote-advanced-select-label flex w-40 flex-none items-center gap-1">
                            {field.label}:
                            <HelpTooltip
                              content={`${field.label} is a special manufacturing option and may require manual review.`}
                              label={field.label}
                            />
                          </span>
                          <select
                            name={field.name}
                            value={formValues.pcbSpec.advancedOptions[field.name]}
                            onChange={handleAdvancedOptionChange}
                            disabled={isBusy}
                            className="quote-advanced-select-control min-w-0 flex-1 border border-gray-300 bg-white px-3 py-2 text-gray-700 outline-none focus:border-green-500"
                          >
                            {field.options.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                      ))}
                      {formValues.pcbSpec.advancedOptions.serialNumbers !== "none" ? (
                        <label className="quote-advanced-select-field flex items-center gap-3 text-sm text-gray-600">
                          <span className="quote-advanced-select-label flex w-40 flex-none items-center gap-1">
                            Serial Number Printing Location:
                            <HelpTooltip
                              content="Choose the factory default location or specify the serial-number format and position in the PCB files."
                              label="Serial Number Printing Location"
                            />
                          </span>
                          <select
                            name="serialNumberPrintingLocation"
                            value={formValues.pcbSpec.advancedOptions.serialNumberPrintingLocation}
                            onChange={handleAdvancedOptionChange}
                            disabled={isBusy}
                            className="quote-advanced-select-control min-w-0 flex-1 border border-gray-300 bg-white px-3 py-2 text-gray-700 outline-none focus:border-green-500"
                          >
                            <option value="factory_default">Follow the factory's default format and location</option>
                            <option value="specified">Specified format and position</option>
                          </select>
                        </label>
                      ) : null}
                    </div>

                    <div className="quote-advanced-checkbox-grid mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                      {(standardPcbConfig.advancedOptions || []).map((option) => {
                        const isSelected = Boolean(formValues.pcbSpec.advancedOptions[option.key]);
                        return (
                          <label key={option.key} className="quote-advanced-checkbox-card flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                            <input
                              type="checkbox"
                              name={option.key}
                              checked={isSelected}
                              onChange={handleAdvancedOptionChange}
                              className="quote-advanced-checkbox-input h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span>{option.label}</span>
                            <HelpTooltip
                              content={
                                STANDARD_PCB_HELP_TEXT[option.key] ||
                                `${option.label} is a special manufacturing process and may add extra cost after review.`
                              }
                              label={option.label}
                              image={STANDARD_PCB_HELP_IMAGES[option.key]}
                            />
                          </label>
                        );
                      })}
                    </div>

                    {formValues.pcbSpec.advancedOptions.impedanceControl ? (
                      <div className="mt-4 grid grid-cols-1 gap-3 border border-green-200 bg-green-50 p-3 md:grid-cols-2">
                        <div className="flex flex-col gap-2">
                          <span className="text-xs font-bold text-gray-600">Quantity of Impedance Design</span>
                          <SelectableGroup
                            name="impedanceDesignCount"
                            value={formValues.pcbSpec.advancedOptions.impedanceDesignCount}
                            options={standardPcbConfig.impedanceDesignOptions || [1, 2, 3, 4]}
                            onChange={(e) => handleAdvancedOptionChange({
                              target: { name: "impedanceDesignCount", value: e.target.value, type: "text" }
                            })}
                            disabled={isBusy}
                          />
                        </div>
                        <label className="flex flex-col gap-2">
                          <span className="text-xs font-bold text-gray-600">Impedance Layers (e.g. L1, L2-L3)</span>
                          <input
                            type="text"
                            name="impedanceLayers"
                            placeholder="Specify layers for impedance control"
                            value={formValues.pcbSpec.advancedOptions.impedanceLayers}
                            onChange={handleAdvancedOptionChange}
                            disabled={isBusy}
                            className="w-full border border-gray-300 bg-white px-3 py-2 text-xs outline-none focus:border-green-500"
                          />
                        </label>
                      </div>
                    ) : null}

                  </div>
                ) : null}

                <div className="border border-t-0 border-gray-200 px-2 py-5 md:px-4">
                  <div className="quote-inspection-report-section">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start">
                      <span className="quote-inspection-report-label flex w-48 flex-none items-center gap-1 text-sm text-gray-600 md:pt-2">
                        <span className="whitespace-pre-line">Final Inspection{"\n"}Report(free):</span>
                        <HelpTooltip
                          content="Choose the final inspection report documents required for this PCB order."
                          label="Final Inspection Report"
                        />
                      </span>
                      <div className="quote-inspection-report-grid grid min-w-0 flex-grow grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {INSPECTION_REPORT_OPTIONS.map((option) => {
                          const checked = (formValues.pcbSpec.advancedOptions.inspectionReports || []).includes(option.value);

                          return (
                            <label
                              key={option.value}
                              className={`quote-inspection-report-option flex items-center gap-2 text-sm ${
                                option.disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer text-gray-800"
                              }`}
                            >
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={checked}
                                disabled={option.disabled || isBusy}
                                onChange={handleInspectionReportChange}
                                className="quote-advanced-checkbox-input h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="flex flex-col gap-3 md:flex-row md:items-start">
                      <span className="w-48 flex-none text-sm text-gray-600 md:pt-2">Other special request:</span>
                      <textarea
                        name="specialRequest"
                        rows="4"
                        value={formValues.pcbSpec.specialRequest}
                        onChange={handlePcbSpecChange}
                        className="w-full border border-gray-300 px-4 py-3 text-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-green-500"
                        placeholder="Fill in any PCB details to make it as clear as possible for us to understand your requirements."
                      />
                    </label>
                  </div>
                  <div className="mt-4">
                    <label className="flex flex-col gap-3 md:flex-row md:items-center">
                      <span className="w-48 flex-none text-sm text-gray-600">PO No.:</span>
                      <input
                        type="text"
                        name="poNumber"
                        maxLength="200"
                        value={formValues.pcbSpec.poNumber || ""}
                        onChange={handlePcbSpecChange}
                        className="w-full max-w-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                        placeholder="Optional PCB purchase order number"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="contents">
              {!isPcbQuoteForm ? (
                <div id="quote-gerber-upload" className="flex flex-col gap-4 md:flex-row md:items-start">
                <label className="w-48 text-sm font-semibold text-gray-600 md:pt-2">
                  {attachmentRequirement.label}
                </label>
                <div className="flex-grow">
                  <div className="relative group">
                    <input
                      type="file"
                      multiple
                      accept={attachmentRequirement.accept}
                      onChange={(e) => handleAttachmentChange(e, attachmentRequirement.defaultCategory)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className={`quote-upload-zone border-2 border-dashed ${isParsing ? "border-green-500 bg-green-50" : "border-gray-300 group-hover:border-green-500 bg-gray-50"} p-6 text-center transition-all`}>
                      {isParsing ? (
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                          <p className="text-sm font-bold text-green-700">Analyzing Gerber files...</p>
                          <p className="text-xs text-green-600 mt-1">Extracting layers, size, and track spacing</p>
                        </div>
                      ) : (
                        <>
                          <svg className="w-10 h-10 text-gray-400 group-hover:text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-sm font-bold text-gray-700">Click or drag files here to upload</p>
                          <p className="text-xs text-gray-400 mt-1">{attachmentRequirement.helper}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <QuoteAttachmentList
                    attachments={attachments}
                    disabled={isBusy || isConfigLoading}
                    onCategoryChange={handleAttachmentCategoryChange}
                    onRemove={handleRemoveAttachment}
                  />
                </div>
                </div>
              ) : null}

              {!isPcbQuoteForm && !isSmdStencilQuote ? (
              <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-start">
                <label className="w-48 text-sm font-semibold text-gray-600 md:pt-2">
                  Special Request
                </label>
                <div className="flex-grow">
                  <textarea
                    name="specialRequest"
                    rows="4"
                    value={formValues.spec.specialRequest || ""}
                    onChange={handleGenericSpecChange}
                    className="w-full border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                    placeholder="Describe custom stackup, special marking, or manual review notes."
                  />
                </div>
              </div>
              ) : null}

              {isPcbQuoteForm ? (
                <div id="quote-gerber-upload" className="order-4 mt-6 space-y-4">
                  <div className="flex flex-col gap-4 border border-gray-200 bg-white p-5 shadow-sm md:flex-row md:items-start">
                    <label className="w-48 text-sm font-semibold text-gray-600 md:pt-2">
                      {attachmentRequirement.label}
                    </label>
                    <div className="min-w-0 flex-grow">
                      <div className="relative group">
                        <input
                          type="file"
                          multiple
                          accept={attachmentRequirement.accept}
                          onChange={(e) => handleAttachmentChange(e, attachmentRequirement.defaultCategory)}
                          disabled={isBusy || isConfigLoading}
                          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                        />
                        <div className={`quote-upload-zone border-2 border-dashed ${isParsing ? "border-green-500 bg-green-50" : "border-gray-300 group-hover:border-green-500 bg-gray-50"} p-6 text-center transition-all`}>
                          <FiUpload className="mx-auto mb-3 h-10 w-10 text-gray-400 group-hover:text-green-500" aria-hidden="true" />
                          <p className="text-sm font-bold text-gray-700">Click or drag files here to upload</p>
                          <p className="mt-1 text-xs text-gray-400">{attachmentRequirement.helper}</p>
                        </div>
                      </div>
                      <QuoteAttachmentList
                        attachments={attachments}
                        disabled={isBusy || isConfigLoading}
                        onCategoryChange={handleAttachmentCategoryChange}
                        onRemove={handleRemoveAttachment}
                      />
                    </div>
                  </div>

                  <div className="border border-gray-300 bg-white shadow-sm">
                    <label className="flex cursor-pointer items-center gap-4 px-5 py-5">
                      <input
                        type="checkbox"
                        name="orderWithStencil"
                        checked={formValues.pcbSpec.orderWithStencil}
                        onChange={handlePcbToggleChange}
                        className="h-4 w-4 border-gray-400 text-green-600 focus:ring-green-500"
                      />
                      <FiGrid className="h-6 w-6 flex-none text-gray-700" aria-hidden="true" />
                      <span className="min-w-[180px] text-base font-bold text-gray-800">
                        SMD-Stencil
                      </span>
                      <span className="hidden text-sm text-gray-500 md:block">Order together with PCB</span>
                      <FiChevronDown
                        className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${
                          formValues.pcbSpec.orderWithStencil ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </label>

                    {formValues.pcbSpec.orderWithStencil ? (
                      <div className="space-y-4 border-t border-gray-200 px-5 pb-6 pt-3">
                        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr_auto]">
                          <span className="text-sm text-gray-600 md:pt-2">Stencil type:</span>
                          <div>
                            <div className="flex flex-wrap gap-3">
                              {[
                                { value: "framework", label: "Framework" },
                                { value: "non_framework", label: "Non-framework" },
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleStencilTypeChange(option.value)}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.stencilType === option.value
                                      ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                      : "border-gray-300 text-gray-500"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                            <p className="mt-1 text-xs text-amber-600">
                              Allow combining several PCBs on a single SMD stencil.
                            </p>
                          </div>
                          <img
                            src="/img/stencil-sm.png"
                            alt="Non-framework stencil custom size illustration"
                            className="hidden h-auto w-[200px] object-contain pr-4 lg:block"
                          />
                        </div>

                        {formValues.pcbSpec.stencilSpec.stencilType === "framework" ? (
                          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                            <span className="text-sm text-gray-600">Multi-level/Step stencil:</span>
                            <div className="flex gap-3">
                              {[true, false].map((value) => (
                                <button
                                  key={String(value)}
                                  type="button"
                                  onClick={() => handleStencilSpecChange({
                                    target: { name: "multiLevel", value, type: "checkbox", checked: value },
                                  })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.multiLevel === value
                                      ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                      : "border-gray-300 text-gray-500"
                                  }`}
                                >
                                  {value ? "Yes" : "No"}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">Size (mm):</span>
                          <select
                            name="size"
                            value={formValues.pcbSpec.stencilSpec.size}
                            onChange={handleStencilSpecChange}
                            className="w-full max-w-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-green-500"
                          >
                            {STENCIL_SIZE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>

                        {["custom", "custom_large"].includes(formValues.pcbSpec.stencilSpec.size) ? (
                          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                            <span className="text-sm text-gray-600">Custom Size (mm):</span>
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                name="customWidth"
                                value={formValues.pcbSpec.stencilSpec.customWidth}
                                onChange={handleStencilSpecChange}
                                className="w-28 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                                aria-label="Custom stencil width in millimeters"
                              />
                              <span className="text-gray-500">×</span>
                              <input
                                type="number"
                                min="1"
                                name="customHeight"
                                value={formValues.pcbSpec.stencilSpec.customHeight}
                                onChange={handleStencilSpecChange}
                                className="w-28 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                                aria-label="Custom stencil height in millimeters"
                              />
                              <span className="text-xs text-amber-600">
                                {formValues.pcbSpec.stencilSpec.size === "custom_large"
                                  ? "*Maximum valid area: 550×550mm"
                                  : "*Maximum valid area: 190×290mm"}
                              </span>
                            </div>
                          </div>
                        ) : null}

                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <AssemblyHelpLabel
                            label="Stencil side:"
                            help="Choose the PCB side that requires solder paste printing. Select a combined or separate stencil when both sides are needed."
                          />
                          <div className="flex flex-wrap gap-3">
                            {STENCIL_SIDE_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleStencilSpecChange({
                                  target: { name: "side", value: option.value, type: "text" },
                                })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.side === option.value
                                    ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">Quantity:</span>
                          <span className="flex max-w-xs">
                            <input
                              type="number"
                              min="1"
                              name="quantity"
                              value={formValues.pcbSpec.stencilSpec.quantity}
                              onChange={handleStencilSpecChange}
                              className="min-w-0 flex-1 border border-gray-300 bg-amber-50 px-3 py-2 text-sm outline-none focus:border-green-500"
                            />
                            <span className="border border-l-0 border-gray-300 bg-gray-100 px-4 py-2 text-sm text-gray-600">Pieces</span>
                          </span>
                        </label>

                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <AssemblyHelpLabel
                            label="Thickness:"
                            help="Select the stainless-steel stencil thickness. The appropriate value depends on component pitch, aperture size and required solder paste volume."
                          />
                          <div className="flex flex-wrap gap-3">
                            {STENCIL_THICKNESS_OPTIONS.map((value) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => handleStencilSpecChange({
                                  target: { name: "thickness", value, type: "text" },
                                })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.thickness === value
                                    ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {value}mm
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <AssemblyHelpLabel
                            label="Existing fiducials:"
                            help="Choose whether fiducial marks should be absent, half lasered or lasered through for stencil alignment."
                          />
                          <div className="flex flex-wrap gap-3">
                            {[
                              { value: "none", label: "None" },
                              { value: "half_lasered", label: "half lasered" },
                              { value: "lasered_through", label: "lasered through" },
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleStencilSpecChange({
                                  target: { name: "fiducials", value: option.value, type: "text" },
                                })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.fiducials === option.value
                                    ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <AssemblyHelpLabel
                            label="Electropolishing:"
                            help="Electropolishing smooths stencil aperture walls to improve solder paste release, especially for fine-pitch components."
                          />
                          <div className="flex gap-3">
                            {[true, false].map((value) => (
                              <button
                                key={String(value)}
                                type="button"
                                onClick={() => handleStencilSpecChange({
                                  target: { name: "electropolishing", value, type: "checkbox", checked: value },
                                })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.stencilSpec.electropolishing === value
                                    ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {value ? "Yes" : "No"}
                              </button>
                            ))}
                          </div>
                        </div>

                        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
                          <span className="md:pt-2">
                            <AssemblyHelpLabel
                              label="Other Special request:"
                              help="Describe special stencil requirements such as stepped areas, custom apertures, markings or other manufacturing notes."
                            />
                          </span>
                          <textarea
                            name="specialRequest"
                            rows="4"
                            value={formValues.pcbSpec.stencilSpec.specialRequest}
                            onChange={handleStencilSpecChange}
                            className="w-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-green-500"
                            placeholder="Fill in any Stencil details to make it as clear as possible for us to understand your requirements. e.g. Multi-level /Step stencil, please specify the region and thickness for step-up/step-down in the gerber."
                          />
                        </label>
                        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">PO No.:</span>
                          <input
                            type="text"
                            name="poNumber"
                            maxLength="200"
                            value={formValues.pcbSpec.stencilSpec.poNumber || ""}
                            onChange={handleStencilSpecChange}
                            className="w-full max-w-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500"
                            placeholder="Optional stencil purchase order number"
                          />
                        </label>
                      </div>
                    ) : null}
                  </div>

                  <div className="border border-gray-300 bg-white shadow-sm">
                    <label className="flex cursor-pointer items-center gap-4 px-5 py-5">
                        <input
                          type="checkbox"
                          name="orderWithAssembly"
                          checked={formValues.pcbSpec.orderWithAssembly}
                          onChange={handlePcbToggleChange}
                          className="h-4 w-4 border-gray-400 text-green-600 focus:ring-green-500"
                        />
                        <FiTool className="h-6 w-6 flex-none text-gray-700" aria-hidden="true" />
                        <span className="min-w-[180px] text-base font-bold text-gray-800">Assembly Service</span>
                        <span className="hidden text-sm text-gray-500 md:block">The PCBs above need assembly</span>
                        <FiChevronDown
                          className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${
                            formValues.pcbSpec.orderWithAssembly ? "rotate-180" : ""
                          }`}
                          aria-hidden="true"
                        />
                    </label>

                    {formValues.pcbSpec.orderWithAssembly ? (
                      <div className="space-y-5 border-t border-gray-200 px-5 pb-6 pt-4">
                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">3 flexible options:</span>
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                            {[
                              { value: "turnkey", title: "Turnkey", description: "Creatyea supply parts" },
                              { value: "consigned", title: "Kitted or Consigned", description: "Customer supply parts" },
                              { value: "combo", title: "Combo", description: "You supply some parts, we do the rest" },
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleAssemblySpecChange({
                                  target: { name: "sourcingType", value: option.value, type: "text" },
                                })}
                                className={`flex min-h-[78px] items-center gap-3 border px-4 py-3 text-left ${
                                  formValues.pcbSpec.assemblySpec.sourcingType === option.value
                                    ? "border-[#b9a175] shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300"
                                }`}
                              >
                                <span className={`h-6 w-6 flex-none rounded-full border-2 ${
                                  formValues.pcbSpec.assemblySpec.sourcingType === option.value
                                    ? "border-[#b9a175] shadow-[inset_0_0_0_4px_white] bg-[#b9a175]"
                                    : "border-gray-300"
                                }`}></span>
                                <span>
                                  <strong className="block text-sm text-gray-700">{option.title}</strong>
                                  <span className="block text-xs text-gray-500">{option.description}</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600 md:pt-2">Board type:</span>
                          <div>
                            <div className="flex flex-wrap gap-3">
                              {[
                                { value: "single_pieces", label: "Single pieces" },
                                { value: "panelized", label: "Panelized PCBs" },
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleAssemblySpecChange({
                                    target: { name: "boardType", value: option.value, type: "text" },
                                  })}
                                  className={`border px-3 py-2 text-sm ${
                                    formValues.pcbSpec.assemblySpec.boardType === option.value
                                      ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                      : "border-gray-300 text-gray-500"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                            <p className="mt-3 max-w-4xl text-xs text-amber-600">
                              We suggest choose to do panel if single PCB qty is more than 20pcs or any side of single board is smaller than 50mm.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">Assembly side(s):</span>
                          <div className="flex flex-wrap gap-3">
                            {[
                              { value: "top", label: "Top side" },
                              { value: "bottom", label: "Bottom side" },
                              { value: "both", label: "Both sides" },
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleAssemblySpecChange({
                                  target: { name: "assemblySide", value: option.value, type: "text" },
                                })}
                                className={`border px-3 py-2 text-sm ${
                                  formValues.pcbSpec.assemblySpec.assemblySide === option.value
                                    ? "border-[#b9a175] text-gray-800 shadow-[inset_0_0_0_1px_#b9a175]"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600 md:pt-2"><span className="text-red-600">*</span> Quantity:</span>
                          <span>
                            <span className="flex max-w-xs">
                              <input
                                type="number"
                                min="1"
                                name="quantity"
                                value={formValues.pcbSpec.assemblySpec.quantity}
                                onChange={handleAssemblySpecChange}
                                className="min-w-0 flex-1 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#b9a175]"
                              />
                              <span className="border border-l-0 border-gray-300 bg-gray-100 px-4 py-2 text-sm text-gray-600">pcs</span>
                            </span>
                            <span className="mt-2 block text-xs text-amber-600">Please fill in total quantity of single PCBs.</span>
                          </span>
                        </label>

                        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600"><span className="text-red-600">*</span> Pay attention:</span>
                          <div className="space-y-5">
                            {[
                              { name: "sensitiveComponents", label: "Contains sensitive components/parts" },
                              { name: "acceptAlternatives", label: "Do you accept alternatives/substitutes made in China?" },
                            ].map((field) => (
                              <div key={field.name}>
                                <p className="mb-2 text-sm text-gray-800">{field.label}</p>
                                <div className="flex gap-7">
                                  {[false, true].map((value) => (
                                    <button
                                      key={String(value)}
                                      type="button"
                                      onClick={() => handleAssemblySpecChange({
                                        target: { name: field.name, checked: value, type: "checkbox" },
                                      })}
                                      className="flex items-center gap-2 text-sm text-gray-700"
                                    >
                                      <span className={`h-6 w-6 rounded-full border-2 ${
                                        formValues.pcbSpec.assemblySpec[field.name] === value
                                          ? "border-[#b9a175] shadow-[inset_0_0_0_4px_white] bg-[#b9a175]"
                                          : "border-gray-300"
                                      }`}></span>
                                      {value ? "Yes" : "No"}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <label className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600">PO No.:</span>
                          <input
                            type="text"
                            name="poNumber"
                            maxLength="200"
                            value={formValues.pcbSpec.assemblySpec.poNumber || ""}
                            onChange={handleAssemblySpecChange}
                            className="w-full max-w-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#b9a175]"
                            placeholder="Optional assembly purchase order number"
                          />
                        </label>

                        {[
                          {
                            title: "Other Parameters",
                            hint: "(Fill in to get the exact price, or leave it blank to wait for final quote.)",
                            open: showAssemblyOtherParameters,
                            toggle: () => setShowAssemblyOtherParameters((visible) => !visible),
                          },
                          {
                            title: "Customized Services and Advanced Options",
                            hint: "",
                            open: showAssemblyAdvancedOptions,
                            toggle: () => setShowAssemblyAdvancedOptions((visible) => !visible),
                          },
                        ].map((section) => (
                          <div key={section.title}>
                            <button
                              type="button"
                              onClick={section.toggle}
                              className="flex w-full items-center justify-between bg-gray-100 px-4 py-3 text-left"
                            >
                              <span className="font-bold text-gray-800">
                                <span className="mr-2 text-xl">{section.open ? "−" : "+"}</span>
                                {section.title}
                                {section.hint ? <span className="ml-2 text-xs font-normal text-amber-600">{section.hint}</span> : null}
                              </span>
                              <span className="flex items-center gap-1 text-sm font-bold text-gray-800">
                                {section.open ? "Hide" : "More"}
                                <FiChevronDown className={`h-4 w-4 ${section.open ? "rotate-180" : ""}`} aria-hidden="true" />
                              </span>
                            </button>
                            {section.open ? (
                              section.title === "Other Parameters" ? (
                                <div className="border border-t-0 border-gray-200 p-4">
                                  <p className="mb-4 text-xs text-amber-600">
                                    They can be left blank if you're not sure about the number of Unique Parts, SMT Parts, BGA/QFP Parts or THT parts.
                                  </p>
                                  <div className="space-y-3">
                                    {[
                                      {
                                        name: "uniquePartsCount",
                                        label: "Number of\nUnique Parts:",
                                        placeholder: "",
                                        visual: "unique",
                                        help: "Enter the number of different component part numbers in the BOM. Identical components with the same part number count as one unique part.",
                                      },
                                      {
                                        name: "smdPartsCount",
                                        label: "Number of\nSMD Parts:",
                                        placeholder: "Total number",
                                        visual: "smd",
                                        help: "Enter the total quantity of surface-mount components required for one assembled PCB.",
                                      },
                                      {
                                        name: "bgaQfpPartsCount",
                                        label: "Number of\nBGA/QFP Parts:",
                                        placeholder: "",
                                        visual: "bga",
                                        help: "Enter the total quantity of BGA, QFP, QFN and other fine-pitch package components for one assembled PCB.",
                                      },
                                      {
                                        name: "throughHolePartsCount",
                                        label: "Number of\nThrough-Hole\nParts:",
                                        placeholder: "Total number",
                                        visual: "throughHole",
                                        help: "Enter the total quantity of through-hole components requiring lead insertion and soldering for one assembled PCB.",
                                      },
                                    ].map((field) => (
                                      <label
                                        key={field.name}
                                        className="grid grid-cols-1 items-center gap-3 md:grid-cols-[190px_225px_1fr]"
                                      >
                                        <AssemblyHelpLabel label={field.label} help={field.help} />
                                        <input
                                          type="number"
                                          min="0"
                                          name={field.name}
                                          value={formValues.pcbSpec.assemblySpec[field.name]}
                                          onChange={handleAssemblySpecChange}
                                          placeholder={field.placeholder}
                                          className="border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#b9a175]"
                                        />
                                        <span className="flex min-h-12 items-center gap-2">
                                          {field.visual === "unique" ? (
                                            <img
                                              src="/img/electronic_01.png"
                                              alt="Example showing six unique electronic parts"
                                              className="h-auto w-[245px] object-contain"
                                            />
                                          ) : null}
                                          {field.visual === "smd" ? (
                                            <img
                                              src="/img/04_furn_smd2.png"
                                              alt="SMD resistor part example"
                                              className="h-[70px] w-[70px] object-contain"
                                            />
                                          ) : null}
                                          {field.visual === "bga" ? (
                                            <>
                                              <img
                                                src="/img/04_furn_smd1.png"
                                                alt="QFP part example"
                                                className="h-[70px] w-[70px] object-contain"
                                              />
                                              <img
                                                src="/img/04_furn_bga.png"
                                                alt="BGA part example"
                                                className="h-[70px] w-[70px] object-contain"
                                              />
                                            </>
                                          ) : null}
                                          {field.visual === "throughHole" ? (
                                            <>
                                              <img
                                                src="/img/04_furn_dip1.png"
                                                alt="Through-hole capacitor part example"
                                                className="h-[70px] w-[70px] object-contain"
                                              />
                                              <img
                                                src="/img/04_furn_dip3.png"
                                                alt="Through-hole transistor part example"
                                                className="h-[70px] w-[70px] object-contain"
                                              />
                                            </>
                                          ) : null}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="border border-t-0 border-gray-200 p-4">
                                  <p className="mb-4 text-xs text-amber-600">
                                    Below options, their cost is not included in the online quotation.
                                  </p>
                                  <div className="grid grid-cols-1 gap-x-12 gap-y-4 lg:grid-cols-2">
                                    {[
                                      { name: "depanelBoards", label: "Depanel the\nboards to delivery:", help: "Select Yes when assembled boards should be separated from the production panel before delivery." },
                                      { name: "functionTest", label: "Function test:", help: "Select Yes when finished assemblies require functional testing according to your test procedure." },
                                      { name: "conformalCoating", label: "Conformal coating:", help: "Select Yes when the assembled PCB requires a protective conformal coating." },
                                      { name: "firmwareLoading", label: "Firmware loading:", help: "Select Yes when firmware or software must be programmed into components before delivery." },
                                      { name: "pressFitAssembly", label: "Press-fit assembly:", help: "Select Yes when press-fit connectors or components require mechanical insertion." },
                                      { name: "boxBuildAssembly", label: "Box build assembly:", help: "Select Yes for complete enclosure, wiring and final product assembly." },
                                      { name: "cableHarnessAssembly", label: "Cable wire harness\nassembly:", help: "Select Yes when cable or wire harness manufacturing and connection are required." },
                                    ].map((field) => (
                                      <div
                                        key={field.name}
                                        className="grid grid-cols-[190px_1fr] items-center gap-3"
                                      >
                                        <AssemblyHelpLabel label={field.label} help={field.help} />
                                        <div className="flex gap-3">
                                          {[false, true].map((value) => (
                                            <AssemblyChoiceButton
                                              key={String(value)}
                                              onClick={() => handleAssemblySpecChange({
                                                target: { name: field.name, checked: value, type: "checkbox" },
                                              })}
                                              selected={Boolean(formValues.pcbSpec.assemblySpec[field.name]) === value}
                                            >
                                              {value ? "Yes" : "No"}
                                            </AssemblyChoiceButton>
                                          ))}
                                        </div>
                                      </div>
                                    ))}

                                    <div className="grid grid-cols-[190px_1fr] items-center gap-3 lg:col-start-1">
                                      <AssemblyHelpLabel
                                        label="Package box:"
                                        help="Choose Neutral box for standard packaging, or Custom box when special branded or sized packaging is required."
                                      />
                                      <div className="flex flex-nowrap items-center gap-3 whitespace-nowrap">
                                        {[
                                          { value: "neutral", label: "Neutral box" },
                                          { value: "custom", label: "Custom box" },
                                        ].map((option) => (
                                          <AssemblyChoiceButton
                                            key={option.value}
                                            onClick={() => handleAssemblySpecChange({
                                              target: { name: "packageBox", value: option.value, type: "text" },
                                            })}
                                            selected={
                                              (formValues.pcbSpec.assemblySpec.packageBox || "neutral") === option.value
                                            }
                                          >
                                            {option.label}
                                          </AssemblyChoiceButton>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-[190px_1fr] items-center gap-3 lg:col-start-1">
                                      <AssemblyHelpLabel
                                        label={"Flying Probe\nTesting:"}
                                        help="Select Yes when the assembled boards require flying probe electrical testing."
                                      />
                                      <div className="flex gap-3">
                                        {[false, true].map((value) => (
                                          <AssemblyChoiceButton
                                            key={String(value)}
                                            onClick={() => handleAssemblySpecChange({
                                              target: { name: "flyingProbeTesting", checked: value, type: "checkbox" },
                                            })}
                                            selected={
                                              Boolean(formValues.pcbSpec.assemblySpec.flyingProbeTesting) === value
                                            }
                                          >
                                            {value ? "Yes" : "No"}
                                          </AssemblyChoiceButton>
                                        ))}
                                      </div>
                                    </div>

                                    <label className="grid grid-cols-[190px_1fr] items-center gap-3 lg:col-start-1">
                                      <AssemblyHelpLabel
                                        label={"Number of X-ray\ntest:"}
                                        help="Enter the number of boards or locations that require X-ray inspection."
                                      />
                                      <input
                                        type="number"
                                        min="0"
                                        name="xrayTestCount"
                                        value={formValues.pcbSpec.assemblySpec.xrayTestCount}
                                        onChange={handleAssemblySpecChange}
                                        className="max-w-[250px] border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#b9a175]"
                                      />
                                    </label>
                                  </div>
                                </div>
                              )
                            ) : null}
                          </div>
                        ))}

                        <label className="grid grid-cols-1 items-start gap-3 md:grid-cols-[190px_1fr]">
                          <span className="text-sm text-gray-600 md:pt-2">Detailed information<br />of assembly:</span>
                          <textarea
                            name="detailedInformation"
                            rows="4"
                            value={formValues.pcbSpec.assemblySpec.detailedInformation}
                            onChange={handleAssemblySpecChange}
                            className="w-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#b9a175]"
                            placeholder="Please fill in detailed technical information about the PCB assembly, other shell assembly, cable soldering, fuse wires, rivets, etc."
                          />
                        </label>

                        <div className="bg-amber-50 px-4 py-3 text-xs leading-5 text-gray-500">
                          Price does not include PCB fabrication or the cost of components, exact quotation will be updated after all the files you uploaded pass review or contact hello@creatyea.com for help. Need a quick and accurate quote? Need an efficient production?
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
          ) : null}

          <section className="quote-action-bar flex flex-col gap-3 border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleCalculate}
              disabled={isBusy || isConfigLoading}
              className="order-2 bg-orange-500 px-8 py-3 font-bold text-white transition-all hover:bg-orange-600 disabled:bg-gray-400 sm:w-2/3"
            >
              <FiCpu className="mr-3 inline h-5 w-5" aria-hidden="true" />
              {isCalculating ? "Calculating..." : "Calculate"}
            </button>
            <button
              type="button"
              onClick={handleSaveQuote}
              disabled={isBusy || isConfigLoading}
              className="order-1 border border-gray-300 bg-white px-6 py-3 font-semibold text-gray-700 transition-all hover:border-green-600 hover:text-green-700 disabled:border-gray-300 disabled:text-gray-400"
            >
              {isSavingQuote ? "Saving..." : savedQuote?._id ? "Update Quote" : "Save Quote"}
            </button>
          </section>
        </div>
      </div>

      <aside className="quote-sidebar relative">
        <div className="sticky top-6 space-y-4">
          {isCnc3dQuote && !quoteResult ? (
            renderCnc3dRfqCard()
          ) : (
          <div className="quote-pricing-card border border-gray-200 bg-white">
            <h3 className="quote-pricing-title border-b px-4 py-3 text-lg font-bold text-gray-900">
              Pricing Summary
            </h3>
            {quoteResult ? (
              <div className="space-y-3 p-4 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Quote Type</span>
                  <strong className="text-right text-blue-600">
                    {quoteResult?.quoteSnapshot?.quoteTypeLabel ||
                      QUOTE_CATEGORIES.find((item) => item.value === formValues.quoteType)?.label}
                  </strong>
                </div>

                <div className="pt-2 space-y-2 border-t border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cost Breakdown</p>
                  {quoteResult?.pricing?.details?.length > 0 ? (
                    quoteResult.pricing.details.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-medium text-gray-800">${Number(item.amount).toFixed(2)}</span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Base PCB Cost</span>
                        <span className="font-medium text-gray-800">${Number(quoteResult?.pricing?.basePcbCost || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Advanced Options</span>
                        <span className="font-medium text-gray-800">${Number(quoteResult?.pricing?.advancedOptionsCost || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Engineering Fee</span>
                        <span className="font-medium text-gray-800">${Number(quoteResult?.pricing?.engineeringFee || 0).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>

                {deliveryOptions.length > 0 ? (
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      Build Time
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {deliveryOptions.map((option) => {
                        const isSelected = option.id === selectedDeliveryOption?.id;

                        return (
                          <button
                            type="button"
                            key={option.id}
                            onClick={() => handleDeliveryOptionChange(option)}
                            disabled={isBusy}
                            className={`grid grid-cols-[1fr_auto] items-center gap-3 border px-3 py-2 text-left text-xs transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                              isSelected
                                ? "border-orange-500 bg-orange-50 text-gray-900"
                                : "border-gray-200 bg-white text-gray-600 hover:border-orange-300"
                            }`}
                          >
                            <span>
                              <span className="block font-semibold">{option.label}</span>
                              <span className="block text-[11px] text-gray-500">
                                {option.leadTimeDays} working days
                              </span>
                            </span>
                            <strong className="text-sm text-orange-600">
                              ${Number(option.manufacturingCost || 0).toFixed(2)}
                            </strong>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-between border-t pt-2">
                  <span>Shipping</span>
                  <strong className="text-green-600">${Number(quoteResult?.pricing?.shippingCost || 0).toFixed(2)}</strong>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Method</span>
                  <span className="text-right">{quoteResult?.pricing?.shippingMethod || "Pending"}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Lead Time</span>
                  <span className="text-right">
                    {quoteResult?.pricing?.leadTimeDays
                      ? `${quoteResult.pricing.leadTimeDays} working days`
                      : "Calculated after review"}
                  </span>
                </div>
                <div className="flex justify-between border-t-2 border-green-100 pt-3 text-xl text-gray-900">
                  <span className="font-bold">Total</span>
                  <strong className="text-orange-600">${Number(quoteResult?.pricing?.totalAmount || 0).toFixed(2)}</strong>
                </div>

                {savedQuote?.quoteNo ? (
                  <div className="mt-4 rounded-md bg-blue-50 border border-blue-200 p-2 text-center">
                    <span className="text-xs text-blue-600 block">Quote No</span>
                    <strong className="text-sm text-blue-800">{savedQuote.quoteNo}</strong>
                  </div>
                ) : null}

                {isShippingPending && (
                  <div className="rounded-md border border-orange-200 bg-orange-50 p-2 text-[10px] text-orange-900">
                    ⚠️ Shipping needs manual confirmation.
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-sm leading-relaxed text-gray-500">
                Fill in the PCB parameters on the left and click <strong className="text-gray-700">Calculate</strong> to see your instant quote.
              </div>
            )}
          </div>
          )}

          {/* Quick Actions in Sidebar when calculated */}
          {quoteResult && (
            <div className="quote-sidebar-actions grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isBusy || isShippingPending || isManualReviewRequired}
                className="w-full bg-green-700 py-3 font-bold text-white shadow-sm transition-all hover:bg-green-800 disabled:bg-gray-400"
              >
                {isAddingQuoteToCart ? "Adding..." : "Add To Cart"}
              </button>
              <button
                type="button"
                onClick={handleSubmitQuote}
                disabled={isBusy}
                className="w-full border border-orange-500 bg-white py-2 font-semibold text-orange-600 transition-all hover:bg-orange-50 disabled:border-gray-300 disabled:text-gray-400"
              >
                Submit for Review
              </button>
            </div>
          )}

          <ContactDetails />
        </div>
      </aside>
    </div>
  );
}

export default FormGetQuote;
